import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import { docText, currentLang } from './docText';
import { s4aDone, s4bDone, onboardingDoneCount, ONBOARDING_STEP_COUNT } from './section4Steps';
import EmployeeView from './EmployeeView';
import Login from './Login';
import './App.css';
import { DarkModeProvider, useDarkMode, DarkModeToggle } from './DarkModeContext';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useMatch } from 'react-router-dom';
import NewStarterForm from './NewStarterForm';
import LeaverForm from './LeaverForm';
import ComplianceModule from './ComplianceModule';
import AdminPanel from './AdminPanel';
import PolicyRepository from './PolicyRepository';
// POLICYHR2: the shared policy write allowlist. See policyRoles.js for why it is separate.
import { POLICY_WRITE_ROLES } from './policyRoles';
import RetentionReview from './RetentionReview';
import ComplianceReport from './ComplianceReport';
import ComplianceEmployeeCard from './ComplianceEmployeeCard';
import ComplianceAttestations from './ComplianceAttestations';
import ComplianceAttestationDetail from './ComplianceAttestationDetail';
// COMPSECTION: one shared helper turns a stored compliance task title into display text.
import { complianceTaskTitle } from './complianceTaskTitle';
import { supportEmail, warnIfSupportEmailMissing } from './supportContact';
// OPSLAYOUT: the COO Operational Tasks presentation, shared rather than re-styled.
// OPSTASKSALL: the COO's Operational Tasks card and its styles, for the compliance, payroll and finance
// Tasks pages.
import OperationalTasksCard from './OperationalTasksCard';
import Dashboard from './Dashboard';
import { mkS } from './dashboardStyles';
// ITSHELL: documentTypeLabel is the SHARED label helper (cv -> "CV", not "Cv"), taken from the
// same module EmployeeView and EmployeeProfile already take it from - no second label table.
import MigrationConfirmation, { documentTypeLabel } from './MigrationConfirmation';
import WorkflowList from './WorkflowList';
import ValidationView from './ValidationView';
// APPREQROLE item 8 / ITSHELL: the SAME component the COO shell renders, reused (never forked) so
// the assign/move_to_testing rights the technology role holds server side are actually reachable.
// It is now the default SECTION of the technology shell (TechnologyView, /it), rather than the
// full-page takeover it briefly was inside StaffView. The COO's own route is untouched.
import ApplicationRequestDashboard from './ApplicationRequestDashboard';
// NSFINVIEW: the shared Add Employee form, reused as-is by FinanceView's "NEW STARTERS" group
// exactly as EmployeeView.js already reuses it for delegate submitters. No fork, no duplicated
// submission logic - the same component, the same POST /employees/ endpoint and its own gate.
import AddEmployee, { ENTITY_TO_LOCATIONS } from './AddEmployee';
import EmployeeProfile from './EmployeeProfile';
// PAYOVERSIGHT: the shared failed-load banner (LOADERR), so this view distinguishes a genuine
// fetch failure from a genuinely quiet 60-day period.
import { AccessWait, LoadError, useIsNarrow, gridCols, Sidebar } from './ui';
import { FormTopBar } from './FormTopBar';
import { IconSettings, IconDashboard, IconUser, IconShield, IconChart, IconFiles, IconCheck } from './icons';
import { ROLE_DEFAULTS } from './roleDefaults';
// SHELLURL: one declaration per shell, both directions derived from it (see shellRoutes.js).
import { PAYROLL_SHELL_ROUTES, FINANCE_SHELL_ROUTES, TECHNOLOGY_SHELL_ROUTES, isUnderBasePath } from './shellRoutes';
import { setPostLoginNext, consumePostLoginNext, peekPostLoginNext, setOpenFpAssessment } from './postLoginNext';
import { isSafeReturn, loginUrlFor, resolveOpenLink } from './emailLinks';
import { GOLD, NAVY_FORM, NAVY_SHELL, TEXT_DARK_MODE, TEXT_MUTED } from './colors';
// REDESIGN Stage 2: the same tokens Dashboard.js adopted in Stage 1, so all 5 shells resolve
// their chrome from one system instead of five near-identical copies.
import { NAVY_700, N_0, N_50, N_200, N_300, N_500, N_600, N_900, WARN_50, WARN_600 } from './colors';
import { shellNav, shellSurface, TYPE, S, RADIUS, ELEV, FONT_STACK } from './theme';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';
import { isWorkflowStepTask, workflowStepOpenPath, completeWorkflowStepTask } from './workflowStepTask';
import { fpStepRefusal } from './FPAssessmentShared';
import { shownTaskTitle } from './taskTitle';

const API = '/api';

// ── Full-page connectivity/maintenance overlay support (Parts 2 & 4) ──
// The axios interceptor runs outside React, so it broadcasts state to a mounted
// <ConnectionOverlay/> through a tiny listener set. Must match main.py's _MAINTENANCE_DETAIL.
const MAINTENANCE_DETAIL = 'Platform is temporarily unavailable for maintenance. Please try again later.';
// SUPPORTCFG: the support address, from the ONE shared module. Read from configuration at build
// time, never committed; when it is not set no contact line is shown at all. See supportContact.js
// for why this cannot come from an API: this overlay exists because the backend is unreachable.
const SUPPORT_EMAIL = supportEmail();
warnIfSupportEmailMissing();
const connectionListeners = new Set();
function emitConnectionState(state) { connectionListeners.forEach(fn => { try { fn(state); } catch { /* noop */ } }); }

const FRENCH_ENTITIES = new Set(['Enko Capital West Africa (CIV)', 'Enko Capital Central Africa (CMR)']);

function detectAndSetLanguage(email) {
  // Resolve the logged-in user's own record via the self endpoint (GET /employees/
  // is now restricted to coo/admin/hr).
  axios.get(`${API}/employees/me`)
    .then(res => {
      const emp = res.data;
      i18n.changeLanguage(FRENCH_ENTITIES.has(emp?.primary_entity) ? 'fr' : 'en');
    })
    .catch(() => i18n.changeLanguage('en'));
}

function loadAuth() {
  try {
    const raw = localStorage.getItem('enko_auth');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch { return false; }
}

// Intercept 401 responses globally - clear session and reload to login
//
// TOKENBOUND: a 401 now has THREE distinct causes, and the person must be told which. Before this
// change every 401 produced the same silent reload to the login screen, so somebody whose session
// had just been invalidated by a password change saw their work vanish with no explanation and no
// idea whether the portal was broken. The server sends a plain-language `detail` for the two new
// cases (sessions cut off, ceiling reached); it is stashed in sessionStorage and rendered on the
// login screen by the banner below. sessionStorage rather than localStorage deliberately: the
// message is about THIS interruption and should not resurface days later in a new tab.
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !err.config?.url?.includes('/auth/')) {
      const detail = err.response?.data?.detail;
      try {
        // Only the server's own session-ending explanations are shown. A generic
        // "Invalid or expired token" is left to the ordinary silent reload, which is what an
        // everyday expiry has always done and is not worth alarming anyone about.
        if (typeof detail === 'string' && (detail.includes('signed out everywhere') || detail.includes('hour limit'))) {
          sessionStorage.setItem('enko_signout_reason', detail);
        }
      } catch { /* sessionStorage unavailable (private mode) - fall through to the silent reload */ }
      localStorage.removeItem('enko_auth');
      delete axios.defaults.headers.common['Authorization'];
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// PADPREVIEW: requests that own their failure, and must never raise the full-page overlay.
//
// THE DEFECT THIS REMOVES. The overlay means "the portal is unreachable". It is correct for a
// dropped network or a backend that is down, because nothing on screen can be trusted. It is WRONG
// for a single document that could not be fetched: the portal is working, one file is not. A PAD
// attachment whose storage call fails returns a deliberate, readable 502 (the document store did
// not respond), and that 502 used to replace the entire portal with "Connection issue" - losing the
// officer's place, her filters and the declaration she had open, over one unreachable file.
//
// Matched on the REQUEST URL rather than on the status, because the status is the honest answer and
// should not be weakened: the endpoint keeps returning 502, the panel keeps showing its own message
// inside itself, and a genuine outage still raises the overlay from every other request.
//
// The caller still receives the rejection unchanged, so each of these endpoints is responsible for
// rendering its own failure locally. That is already how they are written.
const SELF_HANDLED_FAILURE_PATHS = [
  '/compliance/pad/',            // PAD trade confirmation and pre-approval attachments
  '/attachment',                 // any attachment fetch that names itself as one
  '/documents/',                 // document view/download
  '/policies/',                  // policy repository downloads
  '/grievance/',                 // grievance attachments
];

function ownsItsFailure(config) {
  const url = config?.url;
  if (typeof url !== 'string') return false;
  return SELF_HANDLED_FAILURE_PATHS.some(p => url.includes(p));
}

// Detect maintenance mode and network/server outages to drive the full-page overlay.
// Any successful response clears it (auto-recovery). Deliberately ignores 400/401/403/404.
axios.interceptors.response.use(
  res => { emitConnectionState({ type: 'ok' }); return res; },
  err => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    // Maintenance is a whole-platform state and is reported wherever it is seen, including from a
    // document request: every screen is about to be unavailable, not just this file.
    if (status === 503 && detail === MAINTENANCE_DETAIL) {
      emitConnectionState({ type: 'maintenance', config: err.config });
    } else if (ownsItsFailure(err.config)) {
      // PADPREVIEW: this request renders its own message. No overlay, and deliberately no 'ok'
      // either: one file failing says nothing about whether the portal is reachable, so the
      // overlay's current state is left exactly as it was.
      return Promise.reject(err);
    } else if (!err.response || err.code === 'ERR_NETWORK' || [502, 503, 504].includes(status)) {
      emitConnectionState({ type: 'connection', config: err.config });
    }
    return Promise.reject(err);
  }
);

function SessionExpiryWarning({ token, onLogout, onRefresh }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;
  // Root-cause fix: the countdown interval below kept ticking for the entire duration of
  // handleStayLoggedIn's `await onRefresh()` - if that network round-trip took long enough for
  // the interval to reach 0 first, onLogout() fired before the (successful) refresh could ever
  // call setShowWarning(false), so "Stay logged in" appeared to do nothing. This ref is set
  // synchronously the instant the button is clicked, BEFORE the await, so the interval's own
  // tick (checked every 1s) stops counting down/never fires onLogout while a refresh is in
  // flight - reset to false only if the refresh actually fails, so the countdown resumes exactly
  // where the CRITICAL RULE requires (existing "Log out"/expiry behavior otherwise unchanged).
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    if (!token) { setShowWarning(false); return; }
    let exp;
    try { exp = JSON.parse(atob(token.split('.')[1])).exp; } catch { return; }

    const expMs = exp * 1000;
    const warnMs = expMs - 2 * 60 * 1000;

    setShowWarning(false);
    setSecondsLeft(0);

    const triggerWarning = () => {
      setSecondsLeft(Math.max(0, Math.floor((expMs - Date.now()) / 1000)));
      setShowWarning(true);
    };

    if (warnMs <= Date.now()) { triggerWarning(); return; }
    const t = setTimeout(triggerWarning, warnMs - Date.now());
    return () => clearTimeout(t);
  }, [token]);

  useEffect(() => {
    if (!showWarning) return;
    const interval = setInterval(() => {
      if (refreshInFlightRef.current) return; // a refresh is in progress - don't count down/expire
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(interval);
          onLogoutRef.current();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showWarning]);

  const handleStayLoggedIn = async () => {
    refreshInFlightRef.current = true; // stop the countdown from expiring the session mid-refresh
    setRefreshing(true);
    try {
      await onRefresh();
      setShowWarning(false);
    } catch {
      /* refresh failed - warning stays up, countdown resumes from where it left off */
      refreshInFlightRef.current = false;
    }
    finally { setRefreshing(false); }
  };

  if (!showWarning) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const countdown = `${mins}:${String(secs).padStart(2, '0')}`;

  return (
    <div style={sessionStyles.backdrop}>
      <div style={sessionStyles.modal}>
        <h2 style={sessionStyles.title}>Session expiring soon</h2>
        <p style={sessionStyles.body}>
          Your session will expire in{' '}
          <span style={{ ...sessionStyles.countdown, color: secondsLeft < 30 ? '#e74c3c' : GOLD }}>
            {countdown}
          </span>.
          <br />Would you like to stay logged in?
        </p>
        <div style={sessionStyles.btnRow}>
          <button style={sessionStyles.stayBtn} onClick={handleStayLoggedIn} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Stay logged in'}
          </button>
          <button style={sessionStyles.logoutBtn} onClick={() => { setShowWarning(false); onLogoutRef.current(); }}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

const sessionStyles = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 10000,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '32px 28px', maxWidth: '400px', width: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  title: { fontSize: '20px', fontWeight: '600', color: NAVY_FORM, margin: '0 0 12px 0' },
  body: { fontSize: '15px', color: '#444', lineHeight: '1.6', margin: '0 0 24px 0' },
  countdown: { fontWeight: '700', fontSize: '20px' },
  btnRow: { display: 'flex', gap: '12px', justifyContent: 'center' },
  stayBtn: {
    backgroundColor: GOLD, color: NAVY_FORM,
    border: 'none', borderRadius: '6px',
    padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  logoutBtn: {
    backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '6px',
    padding: '10px 24px', fontSize: '14px', cursor: 'pointer',
  },
};

function CookieBanner({ onAccept }) {
  return (
    <div style={cookieBannerStyles.overlay}>
      <p style={cookieBannerStyles.text}>
        This platform uses strictly necessary cookies for authentication and session management only.
        No third-party analytics or tracking cookies are used.
      </p>
      <button style={cookieBannerStyles.btn} onClick={onAccept}>Accept</button>
    </div>
  );
}

const cookieBannerStyles = {
  overlay: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
    backgroundColor: NAVY_FORM,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: '12px',
    padding: '14px 24px',
    boxShadow: '0 -2px 12px rgba(0,0,0,0.3)',
  },
  text: {
    margin: 0, color: '#e0e0e0', fontSize: '13px',
    flex: 1, minWidth: '200px', lineHeight: '1.5',
  },
  btn: {
    backgroundColor: GOLD, color: NAVY_FORM,
    border: 'none', borderRadius: '6px',
    padding: '8px 20px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
};

// ── Full-page overlay: connection issue (Part 2) + maintenance (Part 4) ──
function ConnectionOverlay() {
  const [mode, setMode] = useState(null);          // null | 'connection' | 'maintenance'
  const [retrying, setRetrying] = useState(false);
  const lastConfig = useRef(null);

  useEffect(() => {
    const listener = (s) => {
      if (s.type === 'ok') {
        setMode(null);
      } else if (s.type === 'maintenance') {
        if (s.config) lastConfig.current = s.config;
        setMode('maintenance');
      } else if (s.type === 'connection') {
        if (s.config) lastConfig.current = s.config;
        setMode(prev => (prev === 'maintenance' ? prev : 'connection'));
      }
    };
    connectionListeners.add(listener);
    return () => { connectionListeners.delete(listener); };
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      if (lastConfig.current) await axios.request({ ...lastConfig.current });
      setMode(null);
    } catch {
      /* still failing - overlay stays up */
    } finally {
      setRetrying(false);
    }
  };

  if (!mode) return null;
  const isMaintenance = mode === 'maintenance';

  return (
    <div style={overlayStyles.page}>
      <div style={overlayStyles.card}>
        <div style={overlayStyles.badge}><span style={overlayStyles.badgeMark}>{isMaintenance ? <IconSettings size={22} /> : '!'}</span></div>
        <h1 style={overlayStyles.title}>{isMaintenance ? 'Scheduled maintenance' : 'Connection issue'}</h1>
        <p style={overlayStyles.msg}>
          {isMaintenance
            ? 'Enko Portal is currently undergoing scheduled maintenance. We will be back shortly.'
            : "We're experiencing a temporary issue. Please try again in a few minutes."}
        </p>
        {!isMaintenance && (
          <button style={overlayStyles.btn} type="button" onClick={handleRetry} disabled={retrying}>
            {retrying ? 'Retrying…' : 'Try again'}
          </button>
        )}
        {/* SUPPORTCFG: shown only when an address is configured. No address, no line - never a
            hardcoded fallback. */}
        {SUPPORT_EMAIL && (
          <p style={overlayStyles.contact}>
            If the issue persists, please contact{' '}
            <a style={overlayStyles.link} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        )}
      </div>
    </div>
  );
}

// ── 404 page (Part 3) ──
function NotFound() {
  const nav = useNavigate();
  return (
    <div style={overlayStyles.page}>
      <div style={overlayStyles.card}>
        <div style={overlayStyles.code}>404</div>
        <h1 style={overlayStyles.title}>Page not found</h1>
        <p style={overlayStyles.msg}>The page you are looking for doesn't exist or has been moved.</p>
        <button style={overlayStyles.btn} type="button" onClick={() => nav('/')}>Go to dashboard</button>
      </div>
    </div>
  );
}

const overlayStyles = {
  page: {
    position: 'fixed', inset: 0, zIndex: 10001,
    background: '#edecea',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  card: {
    background: '#fff', border: '0.5px solid rgba(26,39,68,0.1)', borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(26,39,68,0.12)', maxWidth: '460px', width: '100%',
    padding: '40px 32px', textAlign: 'center',
  },
  badge: { width: '56px', height: '56px', margin: '0 auto 20px', borderRadius: '50%', background: NAVY_SHELL, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  badgeMark: { color: GOLD, fontSize: '26px', fontWeight: 800, lineHeight: 1 },
  code: { fontSize: '48px', fontWeight: 800, color: GOLD, letterSpacing: '0.1em', marginBottom: '8px' },
  title: { fontSize: '20px', fontWeight: 700, margin: '0 0 12px 0', color: NAVY_SHELL },
  msg: { fontSize: '15px', lineHeight: 1.6, color: '#4a5568', margin: '0 0 28px 0' },
  btn: { background: GOLD, color: NAVY_SHELL, fontSize: '15px', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '12px 32px', cursor: 'pointer' },
  contact: { marginTop: '28px', fontSize: '13px', color: TEXT_MUTED, lineHeight: 1.7 },
  link: { color: GOLD, textDecoration: 'none', fontWeight: 600 },
};

// ITSHELL item 3: where a REFUSED account is sent, when the path it asked for names a section its
// own shell also has.
//
// THE PROBLEM THIS SOLVES IS EMAILS THAT HAVE ALREADY BEEN SENT. Task notification emails carry a
// literal https://enkoportal.com/employee/tasks button (email_i18n.py, and one Task.link_url in
// employees.py). Those messages are already in people's inboxes and cannot be edited. A technology
// account clicking one is refused by the /employee/* guard below and, without this, lands on its
// role default (/it) - the Application Requested section, NOT their tasks. The email would then
// be a link to the wrong page forever.
//
// So the redirect is RESOLVED AT CLICK TIME, in the browser, from the path the link asks for. It
// therefore fixes every link already in the wild as well as every future one, which is exactly
// what changing the email template alone could not do.
//
// Deliberately NOT done by widening the /employee/* guard to admit technology: that route mounts
// the whole employee shell (leave, grievance, compliance declarations, app-request submission),
// and admitting a role to all of it in order to rescue one link would be a real scope increase.
// This maps one path to the equivalent section of the role's OWN shell and nothing else.
//
// Structure is a per-role table of {requested tail -> that role's own path}, so adding a role or a
// section later is a data edit, not a new branch. An unlisted tail falls through to the plain
// role-default redirect, unchanged.
//
// ITSHELLTRIM (2026-09-17): 'tasks' was removed from TECHNOLOGY_SHELL_ROUTES, so
// TECHNOLOGY_SHELL_ROUTES.toPath('tasks') now correctly returns null (createShellRoutes' own
// "unknown id" contract) rather than a real path. This line is left AS IS deliberately, not
// deleted: redirectForRefusedPath below does `table[clean] || null`, so a null entry here is
// indistinguishable from an absent one, and its caller already falls back to
// `mapped || ROLE_DEFAULTS[userRole]` - the ordinary role-default bounce to /it (Application
// Requested), exactly what happens for every other unmapped path. A stale /employee/tasks link
// from an already-sent email therefore still lands somewhere real, just no longer on a
// tasks-specific page that no longer exists.
const REFUSED_PATH_REDIRECTS = {
  technology: {
    '/employee/tasks': TECHNOLOGY_SHELL_ROUTES.toPath('tasks'),
  },
};

export function redirectForRefusedPath(userRole, pathname) {
  const table = REFUSED_PATH_REDIRECTS[userRole];
  if (!table || !pathname) return null;
  // Tolerate a trailing slash, matching createShellRoutes' own toSection.
  const clean = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return table[clean] || null;
}

// CLVIEW: is this auth payload a Country Lead? The ONE frontend definition, mirroring the
// server's is_country_lead() exactly: role 'payroll' AND can_submit_new_starters.
//
// READS THE LOGIN PAYLOAD, which is why the flag was added to every token-issuing response: this
// runs inside route protection, before any component can fetch /auth/me, so a value that arrives
// later would either flash a redirect or refuse a legitimate Country Lead.
//
// FAILS CLOSED: a missing or falsy flag means "not a Country Lead", so the account keeps exactly
// the routing it has today. This decides WHICH SHELL to show and never what may be done inside it.
function isCountryLeadAuth(auth) {
  return (auth?.role || '').toLowerCase() === 'payroll'
    && auth?.can_submit_new_starters === true;
}

function ProtectedRoute({ auth, allowedRole, children }) {
  const location = useLocation();
  // EMAILBTN: a logged-out user is sent to login WITH the page they asked for, and handleLogin
  // brings them back to it. The router state alone was lost: login always went to the role home.
  if (!auth || !isTokenValid(auth.token)) return <Navigate to={loginUrlFor(location)} state={{ from: location }} replace />;
  const userRole = (auth.role || 'employee').toLowerCase();
  // SECONDARYROLE: a real, permanent second role (auth.secondary_role, set on login/refresh from
  // the same DB-backed value /auth/me exposes) admits its holder to that role's route tree exactly
  // as if it were the primary role - a simple, auditable OR, not a rule engine, and it changes
  // nothing for any account whose secondary_role is unset (the overwhelming majority: this stays
  // undefined/null, so secondaryRole below is '' and never matches a real allowedRole value).
  const secondaryRole = (auth.secondary_role || '').toLowerCase();
  const allowed = Array.isArray(allowedRole) ? allowedRole : [allowedRole];
  if (!allowed.includes(userRole) && !(secondaryRole && allowed.includes(secondaryRole))) {
    // A refused path that names a section this role's own shell has is redirected THERE, so an
    // already-sent email link reaches the right screen. Everything else keeps the pre-existing
    // role-default bounce, byte for byte.
    const mapped = redirectForRefusedPath(userRole, location?.pathname);
    return <Navigate to={mapped || ROLE_DEFAULTS[userRole] || '/login'} replace />;
  }
  return children;
}

// EMAILBTN: the target of every email button. Resolves /open/<kind>[/<id>] to the right page for
// the signed-in role (emailLinks.js); a logged-out user logs in and comes back to this same link.
function OpenLinkRoute({ auth }) {
  const location = useLocation();
  // FIRSTLOGIN: /open/forgot is the credentials reminder's button: signed out, it opens "Forgot
  // password"; signed in, it simply goes home.
  if (/^\/open\/forgot\/?$/.test(location.pathname) && (!auth || !isTokenValid(auth.token))) {
    return <Navigate to="/login?forgot=1" replace />;
  }
  if (!auth || !isTokenValid(auth.token)) return <Navigate to={loginUrlFor(location)} replace />;
  const rest = location.pathname.replace(/^\/open\/?/, '');
  const { to, next, fpAssessment } = resolveOpenLink(rest, location.search, {
    role: auth.role, isCountryLead: isCountryLeadAuth(auth),
  });
  if (next) setPostLoginNext(next);
  // FPMGRMAIL: the assessment the manager's email names, opened by the Validation page.
  if (fpAssessment) setOpenFpAssessment(fpAssessment);
  return <Navigate to={to} replace />;
}

function RootRedirect({ auth }) {
  if (!auth || !isTokenValid(auth.token)) return <Navigate to="/login" replace />;
  // CLVIEW: a Country Lead's home is the COO shell, not /payroll. Without this, landing on "/"
  // would bounce them to the payroll view and the new route would only be reachable by typing it.
  if (isCountryLeadAuth(auth)) return <Navigate to="/dashboard" replace />;
  return <Navigate to={ROLE_DEFAULTS[(auth.role || 'employee').toLowerCase()] || '/login'} replace />;
}

// Session guard for /login: an already-authenticated user (e.g. pressing Back with a still-valid
// token) is redirected to their role default instead of seeing the Login form again, mirroring
// RootRedirect's logic exactly.
function LoginRoute({ auth, onLogin, cookieAccepted, onAcceptCookie }) {
  const location = useLocation();
  if (auth && isTokenValid(auth.token)) {
    // EMAILBTN: already signed in and sent here with a return path: go to it.
    const ret = new URLSearchParams(location.search).get('return');
    if (isSafeReturn(ret)) return <Navigate to={ret} replace />;
    return <Navigate to={ROLE_DEFAULTS[(auth.role || 'employee').toLowerCase()] || '/login'} replace />;
  }
  // FIRSTLOGIN: the credentials reminder links here with ?forgot=1, straight to "Forgot password".
  const forgot = new URLSearchParams(location.search).get('forgot') === '1';
  return (
    <>
      <Login onLogin={onLogin} initialStep={forgot ? 'forgot_password' : undefined} />
      {!cookieAccepted && <CookieBanner onAccept={onAcceptCookie} />}
    </>
  );
}

function ResetPasswordRoute({ onLogin, cookieAccepted, onAcceptCookie }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  if (!token) return <Navigate to="/login" replace />;
  return <>{<Login onLogin={onLogin} initialStep="reset_password" initialToken={token} />}{!cookieAccepted && <CookieBanner onAccept={onAcceptCookie} />}</>;
}

function AppMain() {
  // PWDEXPIRY: the first use of i18n in this shell component. The existing banners here are
  // English-only, but the password rotation warning reaches French-speaking entities too, so it
  // reads its two strings from the same locale files every employee-facing component already
  // uses rather than hardcoding English.
  const { t } = useTranslation();
  const [auth, setAuth] = useState(() => {
    const a = loadAuth();
    if (!a || !isTokenValid(a.token)) {
      // One-time migration shim: enko_auth used to live in sessionStorage before the
      // localStorage switch. A user actively logged in at the exact moment this shipped
      // would otherwise be stranded (still-valid token, wrong storage) - only runs when
      // localStorage genuinely has no entry; an expired/invalid localStorage entry
      // behaves exactly as before (cleared below, no fallback).
      if (!a && localStorage.getItem('enko_auth') === null) {
        const raw = sessionStorage.getItem('enko_auth');
        if (raw) {
          try {
            const migrated = JSON.parse(raw);
            if (migrated && isTokenValid(migrated.token)) {
              localStorage.setItem('enko_auth', raw);
              sessionStorage.removeItem('enko_auth');
              axios.defaults.headers.common['Authorization'] = `Bearer ${migrated.token}`;
              return migrated;
            }
          } catch { /* corrupted entry - cleared below like the expired case */ }
          sessionStorage.removeItem('enko_auth');
        }
      }
      localStorage.removeItem('enko_auth');
      return null;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${a.token}`;
    return a;
  });

  const [cookieAccepted, setCookieAccepted] = useState(
    () => localStorage.getItem('enko_cookie_consent') === 'accepted'
  );

  // ── Delegate access: acting as another employee (e.g. the COO's delegate) ──
  const [delegateToken, setDelegateToken] = useState(() => {
    const t = sessionStorage.getItem('delegate_token');
    return t && isTokenValid(t) ? t : null;
  });
  const [delegateName, setDelegateName] = useState(() => sessionStorage.getItem('delegate_employee_name') || '');
  const [delegates, setDelegates] = useState([]); // employees the logged-in user may act as
  const [delegateError, setDelegateError] = useState(null);

  // null = not yet checked, { needed: false } = no migration, { needed: true, employeeId, employeeEntity } = must confirm
  const [migrationState, setMigrationState] = useState(null);

  const handleCookieAccept = () => {
    localStorage.setItem('enko_cookie_consent', 'accepted');
    setCookieAccepted(true);
  };

  const currentUser = auth?.email || null;

  // TOPBARNAME: the signed-in person's display name for the top bar, which previously showed
  // their email address. Fetched once from GET /auth/me (the call every shell already relies on
  // for can_submit_new_starters), and deliberately kept SEPARATE from `currentUser`: that value
  // is the account IDENTITY and is still the email everywhere it matters (auth, API calls,
  // props that key on the account). This is only what the interface renders.
  //
  // Null until the fetch resolves, and null for an account with no linked employee record.
  // displayIdentity() below turns either case into something readable, so no shell ever renders
  // an empty span or the word undefined.
  const [displayName, setDisplayName] = useState(null);
  // PWDEXPIRY: how many days this account's password has left, for the warning banner.
  // Null means no banner: either no clock is running on this account, or the fetch failed.
  // Read from the SAME /auth/me call displayName already makes rather than a second request.
  const [pwdDaysLeft, setPwdDaysLeft] = useState(null);
  useEffect(() => {
    if (!auth?.token) { setDisplayName(null); setPwdDaysLeft(null); return; }
    let cancelled = false;
    axios.get(`${API}/auth/me`)
      .then(res => {
        if (cancelled) return;
        setDisplayName(res.data?.display_name || null);
        // Only when the backend says to warn. The banner never decides for itself what counts
        // as close to expiry, so it cannot disagree with the gate that does the blocking.
        setPwdDaysLeft(res.data?.password_expiry_warning
          ? res.data?.password_days_remaining ?? null
          : null);
      })
      // Fail soft: fall back to email, and show no banner rather than a wrong one.
      .catch(() => { if (!cancelled) { setDisplayName(null); setPwdDaysLeft(null); } });
    return () => { cancelled = true; };
  }, [auth?.token]);

  // TOPBARNAME: one rule for what the top bar shows, shared by every role shell so they cannot
  // drift. Name when we have one; otherwise the email, which is always present and is a genuine
  // identifier rather than a placeholder - never an empty space and never "undefined".
  const topBarIdentity = displayName || currentUser || '';

  // Set language on initial load (page refresh with existing session)
  useEffect(() => {
    if (!auth) return;
    const role = (auth.role || 'employee').toLowerCase();
    if (role !== 'employee' && role !== 'ceo') { i18n.changeLanguage('en'); return; }
    detectAndSetLanguage(auth.email);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rnav = useNavigate();

  // Check whether the logged-in user's own employee record requires migration confirmation.
  // Runs whenever auth changes (login, logout, token refresh).
  const checkMigration = (currentAuth) => {
    const a = currentAuth !== undefined ? currentAuth : auth;
    if (!a || !isTokenValid(a.token)) {
      setMigrationState(null);
      return;
    }
    // Resolve the logged-in user's own record via the self endpoint (GET /employees/{id}
    // is now restricted to coo/admin/hr). A 404 (no linked employee) means no migration.
    axios.get(`${API}/employees/me`)
      .then(res => {
        const match = res.data;
        if (match?.is_migrated && !match?.migration_confirmed) {
          setMigrationState({ needed: true, employeeId: match.id, employeeEntity: match.primary_entity });
        } else {
          setMigrationState({ needed: false });
        }
      })
      .catch(() => setMigrationState({ needed: false }));
  };

  useEffect(() => { checkMigration(); }, [auth]); // eslint-disable-line

  // Keep the axios Authorization header in sync with whichever token is active: the
  // delegate token while acting as someone else, otherwise the real login token.
  useEffect(() => {
    const tok = delegateToken || auth?.token;
    if (tok) axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    else delete axios.defaults.headers.common['Authorization'];
  }, [delegateToken, auth]);

  // If the delegate token quietly expires (8h lifetime), fall back to the real session
  // instead of forcing a full logout.
  useEffect(() => {
    if (delegateToken && !isTokenValid(delegateToken)) {
      sessionStorage.removeItem('delegate_token');
      sessionStorage.removeItem('delegate_employee_name');
      setDelegateToken(null);
      setDelegateName('');
    }
  }, [delegateToken]);

  // Discover employees the logged-in user may act as (their delegate_email matches).
  // Only checked against the real session, never while already acting as someone else.
  useEffect(() => {
    if (!auth || !isTokenValid(auth.token) || delegateToken) { setDelegates([]); return; }
    axios.get(`${API}/auth/my-delegations`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(res => setDelegates(res.data || []))
      .catch(() => setDelegates([]));
  }, [auth, delegateToken]); // eslint-disable-line

  const handleSwitchToDelegate = async (employeeId) => {
    setDelegateError(null);
    try {
      const res = await axios.post(`${API}/auth/delegate-access/${employeeId}`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      sessionStorage.setItem('delegate_token', res.data.delegate_token);
      sessionStorage.setItem('delegate_employee_name', res.data.employee_name);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.delegate_token}`;
      setDelegateToken(res.data.delegate_token);
      setDelegateName(res.data.employee_name);
      rnav('/employee');
    } catch (err) {
      setDelegateError(err.response?.data?.detail || 'Could not switch to that account.');
    }
  };

  const handleReturnToOwnAccount = () => {
    sessionStorage.removeItem('delegate_token');
    sessionStorage.removeItem('delegate_employee_name');
    setDelegateToken(null);
    setDelegateName('');
    const realToken = auth?.token;
    if (realToken) axios.defaults.headers.common['Authorization'] = `Bearer ${realToken}`;
    rnav(ROLE_DEFAULTS[(auth?.role || 'employee').toLowerCase()] || '/employee');
  };

  const handleLogin = (email, token, role, isTestAccount, secondaryRole) => {
    // A fresh login always starts from the real account - never inherit a leftover
    // delegate session from a previous user of this browser tab.
    sessionStorage.removeItem('delegate_token');
    sessionStorage.removeItem('delegate_employee_name');
    setDelegateToken(null);
    setDelegateName('');
    // Captures a `?next=` param from the /login URL (e.g. an emailed deep link,
    // .../login?next=validation) before the role-default navigation below overwrites the
    // address bar - read directly from window.location rather than a hook, since this runs at
    // the moment of a real credential login, not on every render. See postLoginNext.js.
    setPostLoginNext(new URLSearchParams(window.location.search).get('next'));
    // SECONDARYROLE: stored alongside role, straight from the login response's own DB-backed
    // value - undefined/null for every account except the one this real second role was set on.
    const a = { email, token, role, is_test_account: !!isTestAccount, secondary_role: secondaryRole || null };
    localStorage.setItem('enko_auth', JSON.stringify(a));
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAuth(a);
    setMigrationState(null); // reset so useEffect re-checks with new auth
    const normalizedRole = (role || 'employee').toLowerCase();
    if (normalizedRole !== 'employee' && normalizedRole !== 'ceo') {
      i18n.changeLanguage('en');
    } else {
      detectAndSetLanguage(email);
    }
    // EMAILBTN: back to the page the user was going to (an email button, a bookmarked page), when
    // it is an internal path; otherwise the role home exactly as before.
    const _ret = new URLSearchParams(window.location.search).get('return');
    rnav(isSafeReturn(_ret) ? _ret : (ROLE_DEFAULTS[normalizedRole] || '/employee'), { replace: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('enko_auth');
    sessionStorage.removeItem('delegate_token');
    sessionStorage.removeItem('delegate_employee_name');
    delete axios.defaults.headers.common['Authorization'];
    i18n.changeLanguage('en');
    setAuth(null);
    setDelegateToken(null);
    setDelegateName('');
    setMigrationState(null);
    rnav('/login');
  };

  const handleRefresh = async () => {
    const res = await axios.post(`${API}/auth/refresh`);
    const newToken = res.data.access_token;
    const newAuth = { ...auth, token: newToken };
    localStorage.setItem('enko_auth', JSON.stringify(newAuth));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setAuth(newAuth);
  };

  const isAuthenticated = auth && isTokenValid(auth.token);
  const showMigration   = isAuthenticated && migrationState?.needed === true;
  const checkingMigration = isAuthenticated && migrationState === null;

  // While delegateToken is active, every route/role gate sees an "employee" session tied to
  // the delegated employee's record - never the delegate's own (possibly more privileged)
  // role. This is a frontend routing convenience only; the real enforcement is server-side
  // (get_current_user re-verifies delegate_email on every request).
  const delegateTokenValid = !!delegateToken && isTokenValid(delegateToken);
  const effectiveAuth = delegateTokenValid ? { ...auth, token: delegateToken, role: 'employee' } : auth;

  const routes = (
    <Routes>
      <Route path="/login" element={<LoginRoute auth={effectiveAuth} onLogin={handleLogin} cookieAccepted={cookieAccepted} onAcceptCookie={handleCookieAccept} />} />
      <Route path="/reset-password" element={<ResetPasswordRoute onLogin={handleLogin} cookieAccepted={cookieAccepted} onAcceptCookie={handleCookieAccept} />} />

      {/* CLVIEW: a COUNTRY LEAD gets the COO shell, scoped to its own entity.
          WHY THIS ROUTE RATHER THAN A SECOND VIEW (confirmed decision): the requirement is "the
          COO's portal view, limited to the employees of their own entity". One view with one set
          of panels cannot drift from the COO's; a parallel CountryLeadView would.
          THE ROLE PROP IS NOT "coo". It is 'country_lead', so every panel can tell the two apart
          and nothing has to infer the difference. The COO's own value is unchanged.
          ADMISSION IS NOT A PERMISSION. Reaching this route grants nothing: every endpoint behind
          it re-checks the caller server side, and a Country Lead is already refused on the HR
          report, policies, retention and all compliance routes. */}
      <Route path="/dashboard/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole={isCountryLeadAuth(auth) ? ["coo", "payroll"] : "coo"}>
          <Dashboard currentUser={currentUser} topBarIdentity={topBarIdentity}
            role={isCountryLeadAuth(auth) ? 'country_lead' : 'coo'} onLogout={handleLogout}
            hasTechnologyAccess={(auth?.secondary_role || '').toLowerCase() === 'technology'} />
        </ProtectedRoute>
      } />

      <Route path="/compliance/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="compliance">
          <ComplianceView email={currentUser} topBarIdentity={topBarIdentity} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* ITSHELL: /it/* so the technology shell's sections are real URLs. The BASE PATH IS
          UNCHANGED - /it is what ROLE_DEFAULTS already maps this role to, so bare /it still
          resolves and every existing redirect into it still works; the wildcard only ADDS sub
          paths beneath it. The component is NEW (TechnologyView), not StaffView: StaffView is a
          topbar-only, state-navigated, GET /employees/-backed worklist that 403s this role, which
          is the defect being fixed. StaffView itself is untouched and still serves /hr. */}
      <Route path="/it/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="technology">
          <TechnologyView email={currentUser} topBarIdentity={topBarIdentity} onLogout={handleLogout}
            hasCooAccess={(auth?.role || '').toLowerCase() === 'coo'} />
        </ProtectedRoute>
      } />

      {/* HRSHELL: /hr/* so the HR shell's sections are real URLs - bookmarkable, reloadable and
          back-button-able, exactly as SHELLURL and FINROUTE did for /payroll and /finance.
          /hr itself is UNCHANGED as a path and still resolves; the wildcard only ADDS sub paths
          beneath the same base, so every existing redirect into /hr still works.

          THE COMPONENT IS Dashboard, NOT StaffView, and not a new component either. Every screen
          HR needs (HR Overview, HR Report, New starters, Interns, Leavers, Grievances, Tasks,
          Validation, Policies, My Profile) already exists inside Dashboard, parameterised by
          `role`. StaffView could not serve this role: it is a topbar-only layout by its own
          design, navigates by local state with no URL at all, and its landing screen is the new
          starter PAYROLL worklist. It is now unreferenced by any route and is kept only for the
          regression suite that renders it directly.

          role="hr" is what drives the sidebar, the /hr URL base and the hidden actions. It grants
          nothing: every endpoint behind this shell re-checks the caller server side. */}
      <Route path="/hr/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="hr">
          <Dashboard currentUser={currentUser} topBarIdentity={topBarIdentity}
            role="hr" onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* FINROUTE: /finance/* so the finance shell's sections are real URLs, exactly as SHELLURL
          did for /payroll below. /finance itself is NOT renamed and still resolves - the wildcard
          only ADDS sub paths beneath the same base, and 'workflows' keeps path '' so the bare URL
          lands on the same default section it lands on today. */}
      <Route path="/finance/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="finance">
          <FinanceView email={currentUser} topBarIdentity={topBarIdentity} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* PAYSPLIT: the per-entity payroll contact's shell - the same FinanceView component
          parameterized by role. The backend pre-filters everything this view fetches (payroll
          sees only the Payroll workflow's instances and its own entity's worklist/prepopulate),
          so the shell needs no data logic of its own. */}
      {/* SHELLURL: /payroll/* so the Country Lead shell's sections are real URLs (bookmarkable,
          shareable, reloadable). /payroll itself is UNCHANGED and still lands on the same default
          section; the wildcard only ADDS sub paths beneath it. The finance route above keeps the
          exact path it had, so the shared component serves one URL-driven shell and one
          state-driven shell - see FinanceView's urlRoutes gate. */}
      <Route path="/payroll/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="payroll">
          <FinanceView email={currentUser} topBarIdentity={topBarIdentity} onLogout={handleLogout} role="payroll" />
        </ProtectedRoute>
      } />

      <Route path="/employee/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole={["employee", "ceo"]}>
          <EmployeeByEmail
            key={delegateTokenValid ? 'delegate' : 'self'}
            email={currentUser}
            topBarIdentity={topBarIdentity}
            onLogout={handleLogout}
            role="employee"
          />
        </ProtectedRoute>
      } />

      {/* ADMINPAGES: /admin/* so the 3 admin pages (User accounts, Audit log, Scheduler) are
          real URLs. AdminPanel reads location.pathname to pick the active page, matching
          Dashboard.js's URL-driven pattern, so refresh and back/forward work. */}
      <Route path="/admin/*" element={
        <ProtectedRoute auth={effectiveAuth} allowedRole="admin">
          <AdminPanel currentUser={currentUser} topBarIdentity={topBarIdentity} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* EMAILBTN: every email button lands here first. */}
      <Route path="/open/*" element={<OpenLinkRoute auth={effectiveAuth} />} />
      <Route path="/" element={<RootRedirect auth={effectiveAuth} />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <>
      {delegateTokenValid && !showMigration && (
        <button
          type="button"
          onClick={handleReturnToOwnAccount}
          title="Click to return to your account"
          style={{
            position: 'sticky', top: 0, zIndex: 2000, width: '100%',
            backgroundColor: GOLD, color: NAVY_SHELL, fontSize: '13px', fontWeight: 600,
            textAlign: 'center', padding: '8px 12px', cursor: 'pointer',
            border: 'none', display: 'block', font: 'inherit',
          }}
        >
          You are acting as {delegateName || 'a delegated employee'}. Click here to return to your account.
        </button>
      )}

      {/* PWDEXPIRY: the password rotation warning. NON-BLOCKING by design - it is a sticky
          strip like the delegate banners above, and nothing about navigation changes while it
          shows. The BLOCK happens server-side at expiry, not here.

          Shown only when the backend sets password_expiry_warning, so the threshold lives in
          one place (PASSWORD_WARN_DAYS) rather than being duplicated in the browser. Hidden
          during the migration screen for the same reason the delegate banners are: that screen
          is a full-page takeover and a strip above it would be stranded.

          The action is Sign out, not a link to a change-password screen, because the portal has
          no in-session change-password screen - that flow lives on the login page (Login.js).
          Sending somebody to a route that does not exist would be worse than telling them
          plainly what to do. */}
      {!delegateTokenValid && isAuthenticated && !showMigration && pwdDaysLeft !== null && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 2000, width: '100%',
          backgroundColor: GOLD, color: NAVY_SHELL, fontSize: '13px', fontWeight: 600,
          textAlign: 'center', padding: '8px 12px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <span>
            {pwdDaysLeft === 0
              ? t('passwordExpiry.bannerToday')
              : t('passwordExpiry.banner', { count: pwdDaysLeft })}
          </span>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              background: 'transparent', border: `1px solid ${NAVY_SHELL}`, color: NAVY_SHELL,
              borderRadius: '6px', padding: '4px 12px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('passwordExpiry.bannerAction')}
          </button>
        </div>
      )}

      {!delegateTokenValid && isAuthenticated && !showMigration && delegates.length > 0 && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 2000, width: '100%',
          backgroundColor: NAVY_SHELL, color: '#fff', fontSize: '13px',
          textAlign: 'center', padding: '8px 12px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          {delegates.map(d => (
            <button
              key={d.employee_id}
              onClick={() => handleSwitchToDelegate(d.employee_id)}
              style={{
                background: 'transparent', border: `1px solid ${GOLD}`, color: GOLD,
                borderRadius: '6px', padding: '4px 12px', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Switch to {d.employee_name}'s account
            </button>
          ))}
          {delegateError && <span style={{ color: '#e74c3c' }}>{delegateError}</span>}
        </div>
      )}

      {showMigration ? (
        <MigrationConfirmation
          employeeId={migrationState.employeeId}
          employeeEntity={migrationState.employeeEntity}
          onLogout={handleLogout}
          onComplete={() => checkMigration()}
          role={auth?.role}
        />
      ) : checkingMigration ? (
        <div style={loadingStyle}>Loading...</div>
      ) : (
        routes
      )}

      {isAuthenticated && !showMigration && (
        <SessionExpiryWarning token={auth.token} onLogout={handleLogout} onRefresh={handleRefresh} />
      )}
      {!cookieAccepted && <CookieBanner onAccept={handleCookieAccept} />}
      <ConnectionOverlay />
    </>
  );
}

// ─── Staff portal for HR / Compliance ───
// ITSHELL: the technology role no longer routes here (it has its own shell, TechnologyView at
// /it/*). The only remaining route into this component is /hr, and its behaviour for that role is
// deliberately unchanged by this task. EXPORTED for the ITSHELL regression suite, which renders it
// directly to prove the hr experience is byte-identical - export only, no behaviour change.
export function StaffView({ email, topBarIdentity, role, section, sectionLabel, onLogout }) {
  const { isDarkMode } = useDarkMode();
  // DECLRENAME: this component had no translator of its own, so the renamed top-bar entry below
  // (the second label for the Declarations screen) had no `t` in scope and failed the build. Named
  // `svT` rather than `t` deliberately, matching the `cvT` convention ComplianceView already uses in
  // this file: several callbacks here name their own row argument `t`, and a translator called `t`
  // would be shadowed inside them.
  const { t: svT } = useTranslation();
  const rnav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [newStarterForms, setNewStarterForms] = useState({});
  const [leaverForms, setLeaverForms] = useState({});
  const [loading, setLoading] = useState(true);
  // STAFFLOADFAILSAFE: distinct from `loading`. A failed GET /employees/ left `employees` at its
  // empty-array default, and once loading=false the New Starters section rendered "All done" -
  // this is the exact defect ITSHELL fixed for the technology role's own shell; StaffView is now
  // hr-only, and hr inherited the same defect since fixing it there did not touch this component.
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedLeaverEmployee, setSelectedLeaverEmployee] = useState(null);
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showComplianceAdmin, setShowComplianceAdmin] = useState(false);
  const [showPolicyRepo, setShowPolicyRepo] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // ITSHELL: the APPREQROLE/APPREQASSIGN technology additions (showAppRequests, appRequestCount,
  // the "App Requests" topbar button and its count fetch) have been REMOVED from here. They were
  // reachable only under `role === 'technology'`, and the technology role no longer routes to this
  // component at all - it has its own shell now (TechnologyView, /it/*), where Application
  // Requests is a real sidebar section with its own URL and the same badge. The only remaining
  // caller of StaffView is `hr` at /hr, for which none of those lines could ever execute. The
  // FEATURE is not removed, it moved; the COO's own nav item in Dashboard.js is untouched.
  const [isManager, setIsManager] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  const openMyProfile = () => {
    setShowMyProfile(true);
  };

  // "Validation" topbar button gating - is_manager is a derived capability (GET /employees/me),
  // not a role, so it needs its own small fetch. StaffView has no sidebar (topbar-only layout),
  // so "Validation" is added as a topbar button here rather than a sidebar nav item, reusing this
  // file's existing conditional-topbar-button pattern (see the role === 'compliance' button below).
  // is_approver (same endpoint) covers the COO fallback-approver case: an employee with no
  // manager_id has the COO as their resolved approver, so the COO has a genuine pending approval
  // here even though is_manager alone is False for them.
  useEffect(() => {
    axios.get(`${API}/employees/me`)
      .then(res => {
        const nowIsManager = !!res.data.is_manager;
        const nowIsApprover = !!res.data.is_approver;
        setIsManager(nowIsManager);
        setIsApprover(nowIsApprover);
        // UX audit item 10 (§2.3): pending-approval count badge - /employees/me returns only
        // booleans, never a count, so the count comes from the existing pending-approvals
        // endpoint, fetched only for users who actually qualify.
        // LEAVECANCEL: the badge counts BOTH kinds of decision the Validation page now shows -
        // pending holiday approvals AND pending leave cancellation requests. Counting only the
        // former would leave a manager whose sole outstanding item is a cancellation looking at an
        // unbadged Validation item, which reads as "nothing to do". allSettled so one failing
        // endpoint degrades that half to zero rather than losing the whole count.
        if (nowIsManager || nowIsApprover) {
          Promise.allSettled([
            axios.get(`${API}/leave/pending-approvals`),
            axios.get(`${API}/leave/pending-cancellations`),
          ]).then(rs => setPendingApprovalCount(
            rs.reduce((n, r) => n + (r.status === 'fulfilled' ? (r.value.data || []).length : 0), 0),
          ));
        }
        // Honors an emailed deep link's ?next=validation (see postLoginNext.js) once manager
        // status is actually known - a non-manager/non-approver's pending "next" is simply dropped.
        if ((nowIsManager || nowIsApprover) && consumePostLoginNext() === 'validation') setShowValidation(true);
        // ATTESTDEEPLINK: an attestation email's ?next=attestations targets the personal My Profile
        // view (EmployeeView via EmployeeByEmail here), which loads and can submit the viewer's own
        // attestations - so open My Profile and let EmployeeView consume the signal and land on its
        // Attestations tab. PEEK (not consume) so EmployeeView still sees it.
        else if (['attestations', 'fp'].includes(peekPostLoginNext())) setShowMyProfile(true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    axios.get(`${API}/employees/`)
      .then(async res => {
        const allEmps = res.data;
        setEmployees(allEmps);
        const nsMap = {};
        const lvMap = {};
        await Promise.all(allEmps.map(emp =>
          Promise.all([
            axios.get(`${API}/new-starter/${emp.id}`).then(r => { nsMap[emp.id] = r.data; }).catch(() => {}),
            axios.get(`${API}/leaver/${emp.id}`).then(r => { lvMap[emp.id] = r.data; }).catch(() => {})
          ])
        ));
        setNewStarterForms(nsMap);
        setLeaverForms(lvMap);
        setLoading(false);
      })
      .catch(() => { setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (showMyProfile) return <EmployeeByEmail email={email} onLogout={onLogout} onBack={() => setShowMyProfile(false)} role={role} />;
  if (showCompliance) return <ComplianceModule currentUser={email} isAdmin={false} userRole={role} onBack={() => setShowCompliance(false)} />;
  if (showComplianceAdmin) return <ComplianceModule currentUser={email} isAdmin={true} isStaffView={true} userRole={role} onBack={() => setShowComplianceAdmin(false)} />;
  // POLICYHR2: the SHARED allowlist, not a second hardcoded rule. This mount said
  // `role === 'compliance'`, so it disagreed with Dashboard's mount about coo and admin
  // (both of which the server has always admitted) and would now also have disagreed about
  // hr. Two screens deriving one server rule two different ways is the defect this replaces,
  // not a style preference.
  if (showPolicyRepo) return <PolicyRepository currentUser={email} userRole={role} canManage={POLICY_WRITE_ROLES.includes(role)} onBack={() => setShowPolicyRepo(false)} />;
  if (showValidation) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
        <div style={{ padding: '10px 20px', background: isDarkMode ? '#0a1020' : '#f8f6f3', borderBottom: '0.5px solid rgba(26,39,68,0.1)' }}>
          <button
            onClick={() => setShowValidation(false)}
            style={{
              fontSize: '12px', color: NAVY_SHELL, border: '0.5px solid rgba(26,39,68,0.25)',
              borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
              background: 'none', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            ← Back
          </button>
        </div>
        <div style={{ padding: '24px' }}>
          <ValidationView />
        </div>
      </div>
    );
  }

  if (selectedEmployee) {
    return (
      <>
        <FormTopBar subtitle={sectionLabel} onLogout={onLogout} role={role} onLogoClick={() => setSelectedEmployee(null)} />
        <NewStarterForm
          employeeId={selectedEmployee.id}
          employeeName={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
          employeeEntity={selectedEmployee.primary_entity}
          section={section}
          completedBy={email}
          currentUserRole={role}
          onBack={() => setSelectedEmployee(null)}
          onComplete={() => { setSelectedEmployee(null); load(); }}
        />
      </>
    );
  }

  if (selectedLeaverEmployee) {
    return (
      <>
        <FormTopBar subtitle={sectionLabel} onLogout={onLogout} role={role} onLogoClick={() => setSelectedLeaverEmployee(null)} />
        <LeaverForm
          employeeId={selectedLeaverEmployee.id}
          employeeName={`${selectedLeaverEmployee.first_name} ${selectedLeaverEmployee.last_name}`}
          requiresVisa={selectedLeaverEmployee.requires_visa}
          section={section}
          completedBy={email}
          /* SASCOPE: compliance no longer fills the leaver IT section (the server refuses it). */
          allowedSections={role === 'compliance' ? [1, 3] : undefined}
          onBack={() => setSelectedLeaverEmployee(null)}
          onComplete={() => { setSelectedLeaverEmployee(null); load(); }}
        />
      </>
    );
  }

  const sectionKey = `section${section}_completed`;

  // New starter pending - active employees who haven't completed this section
  const activeEmployees = employees.filter(e => e.is_active);
  const nsPending = activeEmployees.filter(e => !newStarterForms[e.id]?.[sectionKey]);
  const nsDone = activeEmployees.filter(e => newStarterForms[e.id]?.[sectionKey]);

  // Leaver pending - employees with leaver form started (section1 done) but this section not done
  const leaverPending = employees.filter(e =>
    leaverForms[e.id]?.section1_completed && !leaverForms[e.id]?.[sectionKey]
  );
  const leaverDone = employees.filter(e =>
    leaverForms[e.id]?.section1_completed && leaverForms[e.id]?.[sectionKey]
  );

  const totalPending = nsPending.length + leaverPending.length; // eslint-disable-line no-unused-vars

  return (
    <div style={shellSurface(isDarkMode).page}>

      {/* ── Sticky navy topbar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: NAVY_SHELL, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => rnav(ROLE_DEFAULTS[(role || 'employee').toLowerCase()] || '/employee')} title="Go to dashboard">
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' }}>Σ</span>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>Enko Portal</span>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}> · {sectionLabel}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* TOPBARNAME: the person's name, falling back to their email when the account has
              no linked employee record. */}
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginRight: '4px' }}>{topBarIdentity || email}</span>
          <DarkModeToggle />
          <NavBtn onClick={openMyProfile}>My profile</NavBtn>
          {/* DECLRENAME: the SECOND label for the same screen, in the legacy top-bar shell. Renamed
              with the sidebar so the two cannot read differently for the same destination. Already
              gated role === 'compliance', so no other role sees this control at all. */}
          {role === 'compliance' && <NavBtn onClick={() => setShowComplianceAdmin(true)}>{svT('compliance.declarationsTitle')}</NavBtn>}
          <NavBtn onClick={() => setShowPolicyRepo(true)}>Policy Repository</NavBtn>
          {(isManager || isApprover) && (
            <NavBtn onClick={() => setShowValidation(true)}>
              Validation
              {pendingApprovalCount > 0 && (
                <span style={{ marginLeft: '6px', backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 600 }}>
                  {pendingApprovalCount}
                </span>
              )}
            </NavBtn>
          )}
          <NavBtn onClick={onLogout}>Sign out</NavBtn>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {loading ? <p style={staffStyles.empty}>Loading...</p> : loadFailed ? (
        // STAFFLOADFAILSAFE: replaces BOTH sections entirely on failure, so a failed load can
        // never render as "All done" - the exact defect this repeats the fix for, per ITSHELL.
        <LoadError message="Could not load the worklist." onRetry={load} />
      ) : (
        <>
          {/* New Starter pending */}
          <div style={staffStyles.section}>
            <h2 style={staffStyles.sectionTitle}>
              New Starters: {sectionLabel}
              {nsPending.length > 0 && <span style={staffStyles.badge}>{nsPending.length}</span>}
            </h2>
            {nsPending.length === 0 ? (
              <p style={staffStyles.empty}>All done</p>
            ) : (
              nsPending.map(emp => (
                <div key={emp.id} style={staffStyles.card}>
                  <div style={staffStyles.cardLeft}>
                    <div style={staffStyles.avatar}>{emp.first_name.charAt(0)}</div>
                    <div>
                      <p style={staffStyles.name}>{emp.first_name} {emp.last_name}</p>
                      {/* HRPROFILE: the team beside the department, when set. */}
                      <p style={staffStyles.meta}>{emp.job_title || '-'} · {emp.department || '-'}{emp.function_team ? ` · ${emp.function_team}` : ''}</p>
                    </div>
                  </div>
                  <button style={staffStyles.fillBtn} onClick={() => setSelectedEmployee(emp)}>
                    Fill {sectionLabel}
                  </button>
                </div>
              ))
            )}
            {nsDone.length > 0 && nsDone.map(emp => (
              <div key={emp.id} style={{...staffStyles.card, opacity: 0.6}}>
                <div style={staffStyles.cardLeft}>
                  <div style={{...staffStyles.avatar, backgroundColor: '#27ae60'}}>{emp.first_name.charAt(0)}</div>
                  <div>
                    <p style={staffStyles.name}>{emp.first_name} {emp.last_name}</p>
                    <p style={staffStyles.meta}>{emp.job_title || '-'}</p>
                  </div>
                </div>
                <span style={staffStyles.doneBadge}>Done</span>
              </div>
            ))}
          </div>

          {/* Leaver pending */}
          {(leaverPending.length > 0 || leaverDone.length > 0) && (
            <div style={{...staffStyles.section, borderLeft: '4px solid #e74c3c'}}>
              <h2 style={staffStyles.sectionTitle}>
                Leavers: {sectionLabel}
                {leaverPending.length > 0 && <span style={{...staffStyles.badge, backgroundColor: '#e74c3c'}}>{leaverPending.length}</span>}
              </h2>
              {leaverPending.length === 0 ? (
                <p style={staffStyles.empty}>All done</p>
              ) : (
                leaverPending.map(emp => (
                  <div key={emp.id} style={staffStyles.card}>
                    <div style={staffStyles.cardLeft}>
                      <div style={{...staffStyles.avatar, backgroundColor: '#e74c3c'}}>{emp.first_name.charAt(0)}</div>
                      <div>
                        <p style={staffStyles.name}>{emp.first_name} {emp.last_name}</p>
                        <p style={staffStyles.meta}>{emp.job_title || '-'} · Leaving {leaverForms[emp.id]?.last_working_date ? toDisplayDate(leaverForms[emp.id].last_working_date).toLocaleDateString() : '-'}</p>
                      </div>
                    </div>
                    <button style={{...staffStyles.fillBtn, backgroundColor: '#e74c3c'}}
                      onClick={() => setSelectedLeaverEmployee(emp)}>
                      Fill Leaver {sectionLabel}
                    </button>
                  </div>
                ))
              )}
              {leaverDone.map(emp => (
                <div key={emp.id} style={{...staffStyles.card, opacity: 0.6}}>
                  <div style={staffStyles.cardLeft}>
                    <div style={{...staffStyles.avatar, backgroundColor: '#27ae60'}}>{emp.first_name.charAt(0)}</div>
                    <div>
                      <p style={staffStyles.name}>{emp.first_name} {emp.last_name}</p>
                    </div>
                  </div>
                  <span style={staffStyles.doneBadge}>Done</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

// ─── ITSHELL: the Technology shell (/it/*) ────────────────────────────────────
//
// WHY A NEW COMPONENT, not StaffView. StaffView is a topbar-only layout by design (its own
// comments and ui.js's Sidebar comment both say so), its navigation is local useState with no
// URL at all, and its landing screen is a new-starter/leaver worklist built on GET /employees/ -
// an endpoint that 403s a technology account. Nothing about it fits this role. The finance and
// compliance shells were each built as NEW components for exactly this reason; this follows that
// precedent rather than bending a fifth role through StaffView.
//
// AUTHORIZATION IS UNCHANGED BY THIS COMPONENT. Every section here is something a technology
// account could already reach or already owns: Application Requests (assign / move_to_testing,
// granted server side by APPREQROLE/APPREQASSIGN) and their OWN profile (self access, already
// permitted). There is no employee list, no new starter section and no leaver pipeline in this
// shell, and no backend gate is widened anywhere to support it.
//
// ITSHELLTRIM (2026-09-17): the shell shows only these two sections now, per Marie-Lynne's
// decision. Tasks, My Documents and Policy Repository are NAVIGATION removed, not access removed:
// a technology account still owns its own tasks and documents server side (nothing here ever
// granted that - self access on GET /dashboard/employee/{id} and GET /documents/employee/{id} was
// already permitted before this shell existed) and Policy Repository's read gate is untouched for
// every role that still reaches it. Own documents remain reachable from this shell via My Profile
// -> EmployeeView, exactly as every other shell already shows a person's own documents.
export function TechnologyView({ email, topBarIdentity, onLogout, hasCooAccess }) {
  const { isDarkMode } = useDarkMode();
  const isNarrow = useIsNarrow();
  const shellNavigate = useNavigate();
  const shellLocation = useLocation();

  // Same one-line gate every other shell uses: the URL drives the section only when the component
  // is actually mounted under its own base, so rendering it elsewhere (as tests do) keeps state.
  const [stateSection, setStateSection] = useState(TECHNOLOGY_SHELL_ROUTES.defaultSection);
  const urlRoutes = isUnderBasePath(shellLocation?.pathname, TECHNOLOGY_SHELL_ROUTES.base)
    ? TECHNOLOGY_SHELL_ROUTES
    : null;
  // An unknown sub path resolves to null and falls back to the default section, the same
  // forgiving behaviour every other shell has, rather than rendering a blank page.
  const activeSection = urlRoutes
    ? (urlRoutes.toSection(shellLocation.pathname) || urlRoutes.defaultSection)
    : stateSection;
  const setActiveSection = (section) => {
    if (!urlRoutes) { setStateSection(section); return; }
    const url = urlRoutes.toPath(section);
    if (url) shellNavigate(url);
  };

  // The account's own employee record, needed by every self-scoped section (tasks, profile,
  // documents). ONE fetch, shared - not one per section.
  const [myEmployeeId, setMyEmployeeId] = useState(null);
  const [meError, setMeError] = useState(false);
  const [meLoading, setMeLoading] = useState(true);

  const fetchMe = () => {
    setMeLoading(true);
    axios.get(`${API}/employees/me`)
      .then(res => {
        if (res.data?.id) { setMyEmployeeId(res.data.id); setMeError(false); }
        else { setMyEmployeeId(null); setMeError(true); }
      })
      .catch(() => { setMyEmployeeId(null); setMeError(true); })
      .finally(() => setMeLoading(false));
  };
  useEffect(() => { fetchMe(); }, []); // eslint-disable-line

  // APPREQASSIGN item 7: the same pending-count badge mechanism, on the sidebar item this time.
  // The endpoint is role aware and returns this role's own meaning of "pending" (assigned to me
  // or my team AND still buildable) - see TECHNOLOGY_PENDING_STATUSES.
  // SECONDARYROLE: this call is made FROM the technology shell's own code, so it explicitly asks
  // for the technology number via ?shell=technology - an ordinary request parameter, not a session
  // signal. This matters for a dual-capability account (role=coo, secondary_role=technology): its
  // primary `role` alone would otherwise resolve to the COO number even while looking at this
  // shell. A plain technology-role account is unaffected, since ?shell=technology returns exactly
  // what its bare role branch already returned before this parameter existed.
  const [appRequestCount, setAppRequestCount] = useState(0);
  useEffect(() => {
    axios.get(`${API}/application-requests/pending-count?shell=technology`)
      .then(r => setAppRequestCount(r.data?.count || 0))
      .catch(() => setAppRequestCount(0));
  }, [activeSection]);

  // ITSHELLTRIM: Tasks and My Documents sections removed per Marie-Lynne's decision - their
  // state, fetch effects and (for documents) the file-open handler are removed with them, since
  // nothing here rendered outside those two sections. This is presentation only: the account's
  // own documents remain reachable exactly as before via My Profile -> EmployeeView (see below,
  // unchanged), and task assignment/notification is untouched - neither lived in this file.

  const navSections = [
    { label: 'APPLICATION REQUESTS', items: [
      { id: 'requests', label: 'Application Requested', icon: <IconFiles />, badge: appRequestCount || null },
    ]},
    { label: 'MY PROFILE', items: [
      { id: 'myprofile', label: 'My Profile', icon: <IconUser /> },
    ]},
  ];

  const navStyle = { ...shellNav(isDarkMode) };
  const navSectionLabelStyle = {
    fontSize: '10px', color: isDarkMode ? TEXT_MUTED : '#667085', textTransform: 'uppercase',
    letterSpacing: '0.08em', margin: '14px 16px 4px', fontWeight: 600,
  };

  // My Profile is the full EmployeeView on the account's OWN record - the same component every
  // other shell embeds as its My Profile. It is rendered outside this shell's base-path check by
  // construction (its own urlRoutes gate sees /it, not /employee, so it keeps its state fallback
  // and can never write an /employee URL from here).
  if (activeSection === 'myprofile') {
    const backBar = (
      <div style={{ padding: '10px 20px', background: isDarkMode ? '#0a1020' : '#f8f6f3', borderBottom: '0.5px solid rgba(26,39,68,0.1)' }}>
        <button
          onClick={() => setActiveSection('requests')}
          style={{
            fontSize: '12px', color: NAVY_SHELL, border: '0.5px solid rgba(26,39,68,0.25)',
            borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
            background: 'none', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        >
          ← Back to Application Requested
        </button>
      </div>
    );
    if (meLoading) {
      return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
          {backBar}
          <p style={{ padding: '24px', color: '#aaa', fontStyle: 'italic', fontSize: '13px' }}>Loading...</p>
        </div>
      );
    }
    if (meError || !myEmployeeId) {
      return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
          {backBar}
          <div style={{ padding: '24px' }}>
            <LoadError dark={isDarkMode} message="Couldn't load your profile." onRetry={fetchMe} />
          </div>
        </div>
      );
    }
    return (
      <>
        {backBar}
        <EmployeeView
          employeeId={myEmployeeId}
          currentUser={email}
          userRole="technology"
          onBack={() => setActiveSection('requests')}
          onLogoClick={(resetToOverview) => resetToOverview()}
        />
      </>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#f8f9fa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: NAVY_SHELL, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' }}>Σ</span>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>Enko Portal</span>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}> · Technology</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginRight: '4px' }}>{topBarIdentity || email}</span>
          <DarkModeToggle />
          {/* SECONDARYROLE: ordinary navigation back to /dashboard, only for the account whose
              real, permanent PRIMARY role is coo (a plain technology account has nowhere to
              "return" to and never sees this). */}
          {hasCooAccess && (
            <NavBtn onClick={() => shellNavigate('/dashboard')}>COO shell</NavBtn>
          )}
          <NavBtn onClick={onLogout}>Sign out</NavBtn>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Sidebar
          width={isNarrow ? '180px' : '200px'}
          containerStyle={{ backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', borderRight: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`, minHeight: 'calc(100vh - 53px)', paddingTop: '8px' }}
          sections={navSections}
          activeSection={activeSection}
          onSelect={(item) => setActiveSection(item.id)}
          itemStyle={navStyle.item}
          itemActiveStyle={navStyle.itemActive}
          sectionLabelStyle={navSectionLabelStyle}
          renderBadge={(item) => item.badge ? (
            <span style={{ backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 600, flexShrink: 0 }}>{item.badge}</span>
          ) : null}
        />

        <div style={{ flex: 1, padding: '20px 24px', minWidth: 0 }}>
          {/* ── APPLICATION REQUESTS (the default landing section) ──
              Item 8 decision: a SECTION inside this shell with its own URL (/it), not the
              full-page takeover it was in StaffView. The whole point of this shell is real URLs
              and a real sidebar; a takeover that hides both, exitable only through a "Back to
              dashboard" link, is the exact thing being replaced. showAssignedToMe keeps the
              "assigned to me / assigned to the team" section APPREQASSIGN already built, now
              living inside this section rather than behind a button. No onBack is passed, so the
              component renders without its back link - the sidebar is the way out. */}
          {/* SHOWREFUSEDBTN: isCoo intentionally omitted (defaults to false) - this component is
              always the technology shell's landing section, so Approve/Reject/Mark Live/KO,
              which are coo-only server-side, must not render here. */}
          {activeSection === 'requests' && (
            <ApplicationRequestDashboard currentUser={email} showAssignedToMe />
          )}

          {/* ITSHELLTRIM: Tasks, My Documents and Policy Repository sections removed from this
              shell per Marie-Lynne's decision - the sidebar now offers exactly two sections
              (Application Requested, My Profile), and 'tasks'/'documents'/'policies' are no
              longer valid section ids at all (removed from TECHNOLOGY_SHELL_ROUTES above), so
              activeSection can never equal any of them here. Own documents remain reachable via
              My Profile -> EmployeeView below, unchanged. */}
        </div>
      </div>
    </div>
  );
}

// ── NavBtn for Compliance topbar ──────────────────────────────────────────────
function NavBtn({ children, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? 'rgba(201,168,76,0.15)' : 'transparent',
        color: hovered ? GOLD : '#fff',
        border: `0.5px solid ${hovered ? GOLD : 'rgba(255,255,255,0.25)'}`,
        borderRadius: '6px', padding: '6px 12px', fontSize: '13px',
        cursor: 'pointer', transition: 'all 0.15s', fontWeight: hovered ? 500 : 400,
      }}
    >
      {children}
    </button>
  );
}

// ─── Financial Controller landing shell ───
// V1: dedicated shell (not StaffView, which is hard-wired to New Starter/Leaver onboarding
// sections) - mirrors the precedent set by ComplianceView (its own view, not StaffView reuse).
// The FCA Quarterly Reporting workflow list/diagram (WorkflowList, reusing the same component as
// ComplianceView/Dashboard.js) is now wired in - GET /workflows/ server-side scopes this viewer
// (role=finance) to Finance-category instances only, per the confirmed visibility rules.
// Interactive step-completion actions (marking "Complete FCA docs" done, uploading the
// submission screenshot, etc.) remain a separate follow-up task - see app/workflows.py's module
// docstring - this is the read-only visual layer only.
//
// SIDEBAR: added to close the gap confirmed by manual testing - every other role's landing view
// (Dashboard.js/COO, ComplianceView) has a left sidebar + "My Profile" access; this V1 shell had
// neither. ComplianceView was chosen as the structural reference over StaffView: StaffView has
// no left sidebar at all (just 2-3 topbar buttons for New Starter/Leaver section-filling, a
// different shape of task this role doesn't have), while ComplianceView is - like this view - its
// own dedicated single-role shell with a genuine left sidebar. Mirrors ComplianceView's own
// myProfileEmployeeId/myProfileError/openMyProfile pattern (GET /employees/me, not the team-wide
// employees list, so this doesn't depend on list-level permissions) and its exact "My Profile is
// a full-page replacement - own topbar+sidebar via EmployeeView, this view's own topbar/sidebar
// are not rendered" structure, including the onLogoClick={(resetToOverview) => resetToOverview()}
// pattern already established for COO/Compliance's own My Profile.
// PAYSPLIT: role prop added - 'finance' (Financial Controller, the unchanged default) or
// 'payroll' (per-entity payroll contact). The shell is identical; the role flows into
// WorkflowList/EmployeeView and picks the header subtitle and workflows nav label. Everything
// the view fetches is already role-filtered server-side.
// PENSIONFC: the task types the UK payroll contact marks complete from its own Tasks list.
const ENROLMENT_TASK_TYPES = ['new_starter_pension_enrol', 'new_starter_health_insurance_enrol'];

export function FinanceView({ email, topBarIdentity, onLogout, role = 'finance' }) {
  const { isDarkMode } = useDarkMode();
  // RESPONSIVE Phase 1: below ~900px the shell sidebar narrows (200px -> 180px) instead of its
  // flexShrink:0 crushing the main content. Invisible at normal desktop widths.
  const isNarrow = useIsNarrow();
  // SHELLURL / FINROUTE: BOTH shells this component serves now drive their section from the URL -
  // the Country Lead shell (/payroll) since SHELLURL, the finance shell (/finance) since FINROUTE.
  // urlRoutes remains the single gate, so a role with no declaration (or the component rendered
  // outside its base path) still falls back to exactly the previous state-driven behaviour.
  //
  // ONE LINE, not a second mechanism: each shell picks its own declaration and every line below is
  // unchanged. Both directions come from ONE declaration per shell in shellRoutes.js (see
  // PAYROLL_SHELL_ROUTES and FINANCE_SHELL_ROUTES), rather than the two mirrored literal maps
  // Dashboard.js and ComplianceView each maintain.
  const shellRoutes = role === 'payroll' ? PAYROLL_SHELL_ROUTES
    : role === 'finance' ? FINANCE_SHELL_ROUTES
    : null;
  const shellNavigate = useNavigate();
  const shellLocation = useLocation();
  // The state fallback still exists: it is the source for any role with no shellRoutes declaration,
  // and for either shell when rendered outside its own base path.
  const [stateSection, setStateSection] = useState('workflows');
  // URL mode applies only when the shell is actually mounted UNDER its own base path. Mounted
  // anywhere else the URL cannot describe a section, so state stays authoritative and the shell
  // behaves exactly as it did before. That keeps it correct when it is rendered outside its route
  // (as several existing test suites do) instead of silently refusing to change section.
  const urlRoutes = shellRoutes && isUnderBasePath(shellLocation.pathname, shellRoutes.base)
    ? shellRoutes
    : null;
  // CLROUTE: the per-employee profile now has its own path, /payroll/new-starters/:employeeId,
  // following the COO shell's /dashboard/employees/:employeeId shape. The id is derived FROM
  // THE URL, so the selection is route-derived and self-clears on navigation - which is what makes
  // the browser back button work, and what makes a pasted or reloaded URL load the employee.
  // Works for both shells: FINANCE_SHELL_ROUTES also has a submissions path, so a finance user who
  // opens an employee lands on /finance/new-starters/:employeeId and this resolves the id the same
  // way it does under /payroll.
  // Derived from the pathname rather than from useMatch: the shell is rendered in several existing
  // suites whose react-router-dom mock supplies only useNavigate/useLocation, and calling a hook
  // that the mock does not define would throw on import for all of them. Reading the pathname uses
  // only useLocation, which every one of those mocks already provides, and gives exactly the same
  // answer - matchPath would add a dependency for a single-segment suffix match.
  const clProfileEmployeeId = (() => {
    if (!urlRoutes) return null;
    const listPath = urlRoutes.toPath('submissions');
    const p = shellLocation.pathname || '';
    if (!p.startsWith(`${listPath}/`)) return null;
    const rest = p.slice(listPath.length + 1);
    // Exactly ONE further segment is an employee id; anything deeper is not this route.
    return rest && !rest.includes('/') ? decodeURIComponent(rest) : null;
  })();
  // An unknown sub path resolves to null, which falls back to the default section rather than
  // rendering a blank shell - the same forgiving behaviour Dashboard.js and ComplianceView already
  // have via their `|| 'overview'`.
  //
  // A PROFILE path takes precedence and resolves to the SUBMISSIONS section: toSection does an
  // exact map lookup, so /payroll/new-starters/<id> is not in the map and would otherwise fall
  // back to the default section, sending a pasted profile URL to the workflow list. This mirrors
  // Dashboard.js's own `empProfileMatch ? 'hr-overview' : ...` precedence exactly.
  const activeSection = urlRoutes
    ? (clProfileEmployeeId
        ? 'submissions'
        : (urlRoutes.toSection(shellLocation.pathname) || urlRoutes.defaultSection))
    : stateSection;
  // Navigating writes the URL, so the address bar updates and back/forward work (items 6 and 8).
  // An unknown id is ignored rather than navigated to, matching Dashboard.js's `if (url)` guard.
  const setActiveSection = (section) => {
    if (!urlRoutes) { setStateSection(section); return; }
    const url = urlRoutes.toPath(section);
    if (url) shellNavigate(url);
  };
  const [myProfileEmployeeId, setMyProfileEmployeeId] = useState(null);
  const [myProfileError, setMyProfileError] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  // FINPAYROLL: Section 3 (HR & Payroll) worklist - the Financial Controller owns this section
  // of the Leaver Form (confirmed COO decision), so this view lists every leaver with S1
  // submitted and S3 still open, and opens the real LeaverForm pinned to Section 3.
  const [payrollLeavers, setPayrollLeavers] = useState([]);
  const [payrollLeaver, setPayrollLeaver] = useState(null);
  // LOADSTATES: Leaver Payroll had neither of these. Same pair PAYOVERSIGHT already keeps.
  const [leaversLoading, setLeaversLoading] = useState(false);
  const [leaversError, setLeaversError] = useState(false);
  // NSPAYNAV: New Starter Section 3 worklist state, mirroring payrollLeavers/payrollLeaver above.
  const [payrollNewStarters, setPayrollNewStarters] = useState([]);
  const [payrollNewStarter, setPayrollNewStarter] = useState(null);
  // PAYOVERSIGHT: rolling 60-day view. Its own fetch and error state, so a failure degrades this
  // one section rather than the whole shell.
  const [oversight, setOversight] = useState(null);
  const [oversightLoading, setOversightLoading] = useState(false);
  const [oversightError, setOversightError] = useState(false);
  // PAYMINI: clicking an oversight row opens a NARROW read-only mini profile in a modal, rather
  // than navigating to the full employee profile (which finance/payroll cannot open at all - see
  // the row renderer). Own loading/error state so a failed fetch shows inside the modal instead
  // of leaving it blank or breaking the list behind it.
  const [miniProfile, setMiniProfile] = useState(null);
  const [miniLoading, setMiniLoading] = useState(false);
  const [miniError, setMiniError] = useState(false);

  // PAYBANK: bank values are fetched ONLY when deliberately revealed, in a second request. They
  // are never part of the initial payload, so simply opening a row does not pull account numbers
  // over the wire, and they are dropped again on close rather than lingering in state.
  const [bankRevealed, setBankRevealed] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(false);

  const openMiniProfile = (employeeId) => {
    if (!employeeId) return;
    setMiniProfile({ id: employeeId });   // opens the modal immediately, in its loading state
    setMiniLoading(true);
    setMiniError(false);
    setBankRevealed(false);               // every open starts hidden, never pre-revealed
    setBankError(false);
    axios.get(`${API}/workflows/payroll-oversight/employee/${employeeId}/mini-profile`)
      .then(res => { setMiniProfile(res.data); setMiniError(false); })
      .catch(() => { setMiniError(true); })
      .finally(() => setMiniLoading(false));
  };

  const revealBankDetails = () => {
    if (!miniProfile?.id) return;
    setBankLoading(true);
    setBankError(false);
    axios.get(`${API}/workflows/payroll-oversight/employee/${miniProfile.id}/mini-profile`,
              { params: { include_bank: true } })
      .then(res => { setMiniProfile(res.data); setBankRevealed(true); })
      .catch(() => { setBankError(true); })
      .finally(() => setBankLoading(false));
  };

  const closeMiniProfile = () => {
    setMiniProfile(null); setMiniError(false);
    setBankRevealed(false); setBankError(false);   // values do not survive the modal closing
  };

  const fetchOversight = () => {
    setOversightLoading(true);
    axios.get(`${API}/workflows/payroll-oversight`)
      .then(res => { setOversight(res.data); setOversightError(false); })
      .catch(() => { setOversight(null); setOversightError(true); })
      .finally(() => setOversightLoading(false));
  };
  useEffect(() => { if (activeSection === 'oversight') fetchOversight(); }, [activeSection]); // eslint-disable-line
  // NSFINVIEW: can_submit_new_starters is a PER-ACCOUNT permission, not a role - the 4 non-UK
  // country payroll contacts hold it, Gaynor (finance) does not. It was only ever surfaced in
  // EmployeeView's sidebar, so a payroll-role account (whose own view is this one) could not
  // reach it. Same source of truth EmployeeView reads: GET /auth/me. The backend gate on
  // POST /employees/ is untouched and remains the real enforcement.
  const [canSubmitNewStarters, setCanSubmitNewStarters] = useState(false);
  // NSWORKLIST_RELEASE: the "New Starters" worklist UX (entity-wide list + View -> full
  // EmployeeProfile) is held behind a release flag, default OFF, pending Craig's approval. Read from
  // GET /auth/me (new_starters_worklist_released). When FALSE (default) the ONBOARDING item reverts
  // to the original per-creator "My Submissions" table; when TRUE it shows the full worklist built
  // today. Fails closed to False so an unset/failed flag keeps the safe original behaviour.
  const [nsWorklistReleased, setNsWorklistReleased] = useState(false);
  // NSWORKLIST: submissionsLoading guards the "New Starters" worklist load (flag ON).
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  // LOADSTATES: ONE error flag for BOTH submissions loaders, matching submissionsLoading, which
  // both already share. The two are mutually exclusive on nsWorklistReleased (only one ever runs
  // for a given account), so a single flag cannot conflate two live screens, and the retry below
  // re-picks the loader by the same flag rather than hardcoding one.
  const [submissionsError, setSubmissionsError] = useState(false);
  // NSWORKLIST_RELEASE: restored for the flag-OFF path - the original "My Submissions" per-creator
  // tracking table (employees this account created + each one's new-starter form for the progress
  // count). Only populated/rendered when nsWorklistReleased is False.
  const [submissions, setSubmissions] = useState([]);
  const [nsFormsMap, setNsFormsMap] = useState({});
  // NSADDBTN: the "+ Add Employee" top-right button opens AddEmployee as a MODAL (matching the COO
  // Dashboard's own showAddEmployee pattern), replacing the removed ONBOARDING create-employee item.
  const [showAddEmployee, setShowAddEmployee] = useState(false);

  // LOADSTATES: the same three-state shape PAYOVERSIGHT's fetchOversight already uses - set
  // loading on, clear the error on success, set it on failure, clear loading in finally. This
  // screen previously had NO loading state at all, so it showed "No leaver payroll sections
  // pending" while the fetch was still in flight, and no error state, so a failure showed that
  // same line. A 403 here is NOT expected and is deliberately NOT silenced the way SOFT403
  // silenced its cases: GET /leaver/pending/payroll is a plain route-level
  // require_roles(finance, payroll, coo, admin, hr), which every account that can reach this nav
  // item already satisfies, so a 403 would be a real fault worth showing, not a normal
  // restriction. SOFT403's cases were per-employee access rules evaluated per row, which is a
  // different thing entirely.
  const fetchPayrollLeavers = () => {
    setLeaversLoading(true);
    axios.get(`${API}/leaver/pending/payroll`)
      .then(res => { setPayrollLeavers(res.data || []); setLeaversError(false); })
      .catch(() => { setPayrollLeavers([]); setLeaversError(true); })
      .finally(() => setLeaversLoading(false));
  };
  useEffect(() => { if (activeSection === 'leavers') fetchPayrollLeavers(); }, [activeSection]); // eslint-disable-line

  // NSWORKLIST: the upgraded ENTITY-WIDE "New Starters" worklist. It REPLACES both the old NSPAYNAV
  // Section 3 worklist (id 'ns-payroll', removed) and the old NSFINVIEW per-creator "My Submissions"
  // table. It reuses the SAME GET /new-starter/pending/payroll endpoint the Section 3 worklist used,
  // now with ?all=true so it returns EVERY non-archived new starter in this account's entity (not
  // just Section-3-pending ones) - already entity-scoped server side via _resolve_payroll_contact,
  // so a payroll/finance account only ever sees its own entity. One list, one fetch, S1..S4 status
  // columns + a "View" per row that opens NewStarterForm pinned to the payroll-relevant sections.
  // LOADSTATES: a failure here used to set an empty array and nothing else, so it rendered "No new
  // starters in your entity." - indistinguishable from a genuinely empty entity. A 403 is NOT
  // expected (the endpoint is a plain route-level require_roles(finance, payroll, coo, admin, hr))
  // so it is surfaced like any other failure rather than silenced.
  // S3FILTER (confirmed decision by Marie-Lynne): the Section 3 pending filter is SERVER SIDE,
  // reusing the endpoint's OWN existing default rather than adding a browser-side predicate.
  //
  // WHY SERVER SIDE HERE, when NSLIST deliberately put search and sort in the browser: this filter
  // already exists on the server. all=false IS pending-only, byte-for-byte the endpoint's original
  // behaviour, and the frontend has simply been overriding it with all=true since NSWORKLIST. So
  // "reuse the existing behaviour rather than adding new logic" means passing the flag the
  // endpoint already understands. Search and sort had no server-side equivalent, which is why
  // those stayed local.
  //
  // IT CANNOT WIDEN THE ENTITY SCOPE. The endpoint applies its _resolve_payroll_contact filter
  // IDENTICALLY in both modes - `all` only widens the SECTION filter, never the entity one (see
  // its own docstring). Pending-only is a strict SUBSET of all, so this control can only ever show
  // FEWER rows than the list already showed.
  const [nsS3PendingOnly, setNsS3PendingOnly] = useState(false);
  const fetchPayrollNewStarters = (pendingOnly = nsS3PendingOnly) => {
    setSubmissionsLoading(true);
    axios.get(`${API}/new-starter/pending/payroll`, { params: { all: !pendingOnly } })
      .then(res => { setPayrollNewStarters(res.data || []); setSubmissionsError(false); })
      .catch(() => { setPayrollNewStarters([]); setSubmissionsError(true); })
      .finally(() => setSubmissionsLoading(false));
  };
  // Toggling refetches with the other mode. The value is passed explicitly rather than read from
  // state inside the fetch, because setState is asynchronous and the fetch would otherwise send
  // the PREVIOUS value on the very click that changed it.
  const toggleNsS3PendingOnly = () => {
    const next = !nsS3PendingOnly;
    setNsS3PendingOnly(next);
    fetchPayrollNewStarters(next);
  };

  // ── CLROUTE: the employee profile as a real URL ──────────────────────────────
  // The profile is open when the URL says so (route-derived, so browser back/forward work), OR,
  // when the shell is mounted outside its base path and therefore has no URL to read, when the
  // old click state says so. The second arm keeps the shell correct wherever it is rendered
  // without a router path, exactly as SHELLURL's own urlRoutes gate does.
  const clProfileOpen = urlRoutes ? Boolean(clProfileEmployeeId) : Boolean(payrollNewStarter);

  // Opening writes the URL. The click state is ALSO set, so the breadcrumb can name the employee
  // immediately rather than waiting for the worklist to resolve - but the PROFILE itself reads the
  // URL id, so a reload or a pasted link behaves identically to a click.
  // S3ONLYFIN (confirmed decision by Marie-Lynne): the FINANCE role's New Starters worklist is
  // stripped to the one thing that role is there to do - Section 3. It shows the employee name and
  // the S3 status only, its action opens the SECTION-3-ONLY form rather than the full employee
  // profile, and a row whose Section 3 is already done offers no action at all.
  //
  // Deliberately keyed on role === 'finance', NOT on the secondary role or on any one account: a
  // GENUINE payroll Country Lead runs their whole entity and keeps the full worklist (S1..S4,
  // Country, Date created, View into the full profile) exactly as before. ONE named constant, read
  // by the header, the cells and the action cell, so those three can never disagree about which
  // view is being rendered.
  const s3OnlyWorklist = role === 'finance';

  const clProfileOpenEmployee = (ns) => {
    setPayrollNewStarter(ns);
    // FINROUTE: navigate under the shell that is actually mounted, not always /payroll - the
    // finance role reaches this same worklist and must land on /finance/new-starters/:id, not
    // borrow the Country Lead shell's base path.
    if (urlRoutes) shellNavigate(`${urlRoutes.toPath('submissions')}/${ns.employee_id}`);
  };

  // CLCRUMB: true while EmployeeProfile has the New Starter Form open, so the shell's own
  // breadcrumb can stand down and let the form's fuller one show alone. Reset whenever the open
  // employee changes, so returning to the list or opening a different person never leaves the
  // outer crumb suppressed - the same guard Dashboard.js keys on selectedEmployee.
  const [clProfileFormOpen, setClProfileFormOpen] = useState(false);
  useEffect(() => { setClProfileFormOpen(false); },
    [clProfileEmployeeId, payrollNewStarter?.employee_id]);

  // Going back clears the state AND returns the URL to the list, so the address bar and the view
  // never disagree. Refreshes the list, preserving CLBACK's behaviour exactly.
  const clProfileBack = () => {
    setPayrollNewStarter(null);
    fetchPayrollNewStarters();
    if (urlRoutes) shellNavigate(urlRoutes.toPath('submissions'));
  };

  // The breadcrumb's employee name. On a click we already hold the row. On a DIRECT VISIT we do
  // not, so it is looked up in the worklist once that loads - and falls back to a neutral label
  // until then, rather than rendering a blank crumb or a raw id.
  // ── NSLIST: sorting, search and the conditional country column ──────────────
  // ALL THREE OPERATE ON payrollNewStarters, the array the SERVER already returned. That array is
  // entity scoped server side (pending/payroll filters on _resolve_payroll_contact), so sorting,
  // searching and grouping can only ever REORDER or HIDE rows the account could already see. No
  // wider set is fetched and no new endpoint is called, so this cannot widen visibility.
  // S3TASK: the Country Lead's own task list. Fetched only when that section is open, so no other
  // shell pays for it, and it fails soft to an empty list rather than blocking the section.
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myTasksError, setMyTasksError] = useState(false);
  // TASKTABS (confirmed decision by Marie-Lynne): overdue and upcoming are two TABS rather than two
  // stacked sections, so the user sees only the pile they picked.
  //
  // DEFAULTS TO OVERDUE because that is the work that is already late - opening on Upcoming would
  // bury it behind a click. Both counts are on the tab labels, so choosing one never hides the
  // existence of the other, which is the whole reason the counts are always rendered (including
  // zero) rather than only when non-empty.
  //
  // Held in component state, NOT in the URL: it is a view preference within an already-routed
  // section, and putting it in the URL would mean a new route for the payroll shell. It therefore
  // SURVIVES a refetch and a LoadError retry (neither remounts this component) but resets on a
  // full page reload, which is the same lifetime every other tab pair in this codebase has.
  const [myTasksTab, setMyTasksTab] = useState('overdue');
  // LOADSTATES: the fetch was INLINE in the effect below, so there was no function a Retry could
  // call - extracted to a named fetcher for exactly that reason, and the effect now just calls it.
  // This is the same shape fetchOversight already has (named fetcher + effect that calls it on
  // section open), so the retry re-runs the REAL request rather than a neighbouring one. A 403 is
  // not expected: GET /dashboard/my-tasks is route-level require_roles(coo, admin, hr, payroll).
  const fetchMyTasks = () => {
    setMyTasksLoading(true);
    axios.get(`${API}/dashboard/my-tasks`)
      .then(res => { setMyTasks(res.data?.tasks || []); setMyTasksError(false); })
      .catch(() => { setMyTasks([]); setMyTasksError(true); })
      .finally(() => setMyTasksLoading(false));
  };
  useEffect(() => {
    if (activeSection !== 'tasks') return;
    fetchMyTasks();
  }, [activeSection]); // eslint-disable-line

  // PENSIONFC point 4: the UK payroll contact marks its pension and health insurance enrolment
  // tasks complete here, through the same endpoint the COO's Operational Tasks use. Only these two
  // types get the button; every other task in this list keeps "Open" alone.
  const { t: fvT } = useTranslation();
  const [completingMyTaskId, setCompletingMyTaskId] = useState(null);
  const [myTaskCompleteError, setMyTaskCompleteError] = useState(false);
  // WFTASKS: a workflow step task completes its step; a step that needs a file or a form opens.
  const completeMyWorkflowStepTask = async (task) => {
    setCompletingMyTaskId(task.task_id);
    setMyTaskCompleteError(false);
    try {
      const outcome = await completeWorkflowStepTask(API, task, (path) => shellNavigate(path));
      if (outcome === 'completed') fetchMyTasks();
    } catch (err) {
      setMyTaskCompleteError(true);
    }
    setCompletingMyTaskId(null);
  };
  const completeMyTask = async (taskId) => {
    setCompletingMyTaskId(taskId);
    setMyTaskCompleteError(false);
    try {
      await axios.patch(`${API}/dashboard/tasks/${taskId}/complete`);
      fetchMyTasks();
    } catch (err) {
      setMyTaskCompleteError(fpStepRefusal(err.response?.data?.detail, fvT) || true);
    }
    setCompletingMyTaskId(null);
  };

  const [nsSearch, setNsSearch] = useState('');
  const [nsSort, setNsSort] = useState({ key: 'name', dir: 'asc' });

  // The account's own entity, used ONLY to decide whether the country column is worth showing.
  const [ownEntity, setOwnEntity] = useState(null);
  useEffect(() => {
    if (role !== 'payroll') return;
    axios.get(`${API}/employees/me`)
      .then(res => setOwnEntity(res.data?.primary_entity || null))
      .catch(() => setOwnEntity(null));   // fails soft: no entity means no column
  }, [role]); // eslint-disable-line

  // Show the country column ONLY when this account's entity genuinely covers more than one
  // location. Derived from ENTITY_TO_LOCATIONS, the mapping AddEmployee already builds from
  // LOCATION_COMPANIES + COMPANY_TO_ENTITY - NOT hardcoded to ECWA, so an entity that gains a
  // second location later gets the column automatically with no code change. Today only the CIV
  // entity qualifies (Cote d'Ivoire + its Senegal liaison office).
  // The optional-chain on the map itself is deliberate, not defensive noise: several existing test
  // suites mock './AddEmployee' as a default-only component, which replaces the whole module and
  // removes this named export. Reading it unguarded threw for all of them. Treating a missing map
  // as "no multi-location entity" is also the right production default - the column is an extra,
  // and failing to show it is strictly safer than crashing the shell.
  const showCountryColumn = (ENTITY_TO_LOCATIONS?.[ownEntity] || []).length > 1;

  // The column displays employee.location, which is the SAME field _resolve_payroll_location_key
  // reads FIRST when resolving who owns an employee's payroll. So the column can never disagree
  // with the routing: if it says Senegal, the payroll resolution saw Senegal too.
  const nsCountry = (ns) => (ns.location || '').trim() || 'Not set';

  const nsFiltered = (() => {
    // SEARCH: first name, last name and the two joined, case insensitive, matching ANY substring
    // rather than a whole word - typing "sou" finds "De SOUZA". Deliberately NOT matching email,
    // which was not asked for.
    const q = nsSearch.trim().toLowerCase();
    const rows = !q ? payrollNewStarters : payrollNewStarters.filter(ns => {
      const first = (ns.first_name || '').toLowerCase();
      const last = (ns.last_name || '').toLowerCase();
      return first.includes(q) || last.includes(q) || `${first} ${last}`.includes(q);
    });
    // SORT: browser side, deliberately. This list is ONE entity's non-archived new starters - 82
    // rows for the largest entity today, and the endpoint already returns the whole set in one
    // response with no pagination. Sorting that in the browser is instant and costs no round trip;
    // moving it server side would mean a new query parameter and a refetch per click for a list
    // that is already fully in hand. If this ever paginates, sorting must move server side, since
    // sorting one page of a paginated set is wrong.
    const dir = nsSort.dir === 'asc' ? 1 : -1;
    const val = (ns) => {
      switch (nsSort.key) {
        case 'country': return nsCountry(ns).toLowerCase();
        case 's1': return ns.section1_completed ? 1 : 0;
        case 's2': return ns.section2_completed ? 1 : 0;
        case 's3': return ns.section3_completed ? 1 : 0;
        case 's4a': return s4aDone(ns) ? 1 : 0;
        case 's4b': return s4bDone(ns) ? 1 : 0;
        case 'created': return ns.created_at || '';
        default: return `${ns.last_name || ''} ${ns.first_name || ''}`.trim().toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      // Stable tiebreak by name, so equal statuses do not shuffle between renders.
      const an = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
      const bn = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
      return an < bn ? -1 : an > bn ? 1 : 0;
    });
  })();

  const nsToggleSort = (key) => setNsSort(s =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  // S3ONLYFIN: the row currently open, resolved ONCE. This is the exact expression clProfileName
  // already used inline; lifting it out lets the Section-3-only mount name the same employee the
  // breadcrumb does, instead of a second copy of the same lookup.
  const clProfileRow = (clProfileEmployeeId
    ? payrollNewStarters.find(n => n.employee_id === clProfileEmployeeId)
    : null) || payrollNewStarter;

  const clProfileName = (() => {
    const name = `${clProfileRow?.first_name || ''} ${clProfileRow?.last_name || ''}`.trim();
    return name || 'Employee';
  })();
  // NSWORKLIST_RELEASE (flag OFF): the ORIGINAL two-step "My Submissions" load - the employees this
  // account created, then each one's new-starter form for the progress column. A 404 there is
  // "no form yet", not a failure. Restored verbatim from the pre-NSWORKLIST behaviour.
  // LOADSTATES: only the OUTER employees fetch decides success or failure. The per-employee
  // new-starter fetches inside keep their existing silent catch, because a 404 there genuinely
  // means "no form started yet" and is the normal case for a freshly created employee - letting
  // one of those flip the screen into a failure banner would be the same conflation this task
  // exists to remove, in the opposite direction. A 403 on the outer call is not expected either:
  // this whole section is gated on canSubmitNewStarters and sends created_by = its own email,
  // which is exactly MYSUBS403's carve-out, so the request is admitted by construction.
  const loadSubmissions = () => {
    setSubmissionsLoading(true);
    axios.get(`${API}/employees/`, { params: { created_by: email, status: 'all' } })
      .then(async res => {
        const emps = res.data || [];
        setSubmissions(emps);
        const map = {};
        await Promise.all(emps.map(emp =>
          axios.get(`${API}/new-starter/${emp.id}`)
            .then(r => { map[emp.id] = r.data; })
            .catch(() => {})
        ));
        setNsFormsMap(map);
        setSubmissionsError(false);
        setSubmissionsLoading(false);
      })
      .catch(() => { setSubmissions([]); setSubmissionsError(true); setSubmissionsLoading(false); });
  };
  // NSWORKLIST_RELEASE: pick the loader by flag state - the entity-wide worklist fetch when the
  // flag is ON, the original per-creator "My Submissions" load when OFF. Re-runs when the flag
  // resolves (it starts False and is set from /auth/me) so the correct data loads either way.
  useEffect(() => {
    if (activeSection !== 'submissions') return;
    if (nsWorklistReleased) fetchPayrollNewStarters();
    else loadSubmissions();
  }, [activeSection, nsWorklistReleased]); // eslint-disable-line

  // NSFINVIEW: its own small fetch, deliberately separate from the /employees/me call below -
  // the flag lives on the ACCOUNT (auth), not on the employee record. Failing closed (the
  // default false) is correct: a failed read hides an optional group rather than showing a
  // control the backend would then refuse.
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => {
        setCanSubmitNewStarters(!!res.data.can_submit_new_starters);
        // NSWORKLIST_RELEASE: same call carries the worklist UX release flag; fails closed to False.
        setNsWorklistReleased(!!res.data.new_starters_worklist_released);
      })
      .catch(() => { setCanSubmitNewStarters(false); setNsWorklistReleased(false); });
  }, []); // eslint-disable-line

  // "Validation" nav item gating - is_manager is a derived capability (GET /employees/me), not a
  // role, so it needs its own small fetch, independent of the on-demand openMyProfile() call.
  // is_approver (same endpoint) covers the COO fallback-approver case: an employee with no
  // manager_id has the COO as their resolved approver, so the COO has a genuine pending approval
  // here even though is_manager alone is False for them.
  useEffect(() => {
    axios.get(`${API}/employees/me`)
      .then(res => {
        const nowIsManager = !!res.data.is_manager;
        const nowIsApprover = !!res.data.is_approver;
        setIsManager(nowIsManager);
        setIsApprover(nowIsApprover);
        // UX audit item 10 (§2.3): pending-approval count badge. /employees/me returns only
        // booleans (is_manager/is_approver), never a count, so the count comes from the existing
        // pending-approvals endpoint - fetched only for users who actually qualify, so a
        // non-approver triggers no extra request at all.
        // LEAVECANCEL: the badge counts BOTH kinds of decision the Validation page now shows -
        // pending holiday approvals AND pending leave cancellation requests. Counting only the
        // former would leave a manager whose sole outstanding item is a cancellation looking at an
        // unbadged Validation item, which reads as "nothing to do". allSettled so one failing
        // endpoint degrades that half to zero rather than losing the whole count.
        if (nowIsManager || nowIsApprover) {
          Promise.allSettled([
            axios.get(`${API}/leave/pending-approvals`),
            axios.get(`${API}/leave/pending-cancellations`),
          ]).then(rs => setPendingApprovalCount(
            rs.reduce((n, r) => n + (r.status === 'fulfilled' ? (r.value.data || []).length : 0), 0),
          ));
        }
        // Honors an emailed deep link's ?next=validation (see postLoginNext.js) once manager
        // status is actually known - a non-manager/non-approver's pending "next" is simply dropped.
        // FPFIX: PEEK first. The old order consumed the signal for a manager or approver before the
        // attestations / F&P branch could read it, so those links were dropped for them; and the My
        // Profile section was opened without its employee id, so it stayed on "Loading...".
        const _next = peekPostLoginNext();
        // ATTESTDEEPLINK: an attestation email's ?next=attestations targets the personal My Profile
        // view (EmployeeView), which loads and can submit the viewer's own attestations - so open
        // My Profile and let EmployeeView consume the signal and land on its Attestations tab. PEEK
        // (not consume) so EmployeeView still sees it.
        if (['attestations', 'fp'].includes(_next)) {
          if (res.data && res.data.id) setMyProfileEmployeeId(res.data.id);
          setActiveSection('myprofile');
        }
        else if ((nowIsManager || nowIsApprover) && _next === 'validation') { consumePostLoginNext(); setActiveSection('validation'); }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const openMyProfile = () => {
    setActiveSection('myprofile');
    if (myProfileEmployeeId) return;
    setMyProfileError(null);
    axios.get(`${API}/employees/me`)
      .then(res => { if (res.data) setMyProfileEmployeeId(res.data.id); else setMyProfileError('Unable to load your profile. Please try again.'); })
      .catch(() => setMyProfileError('Unable to load your profile. Please try again.'));
  };

  if (activeSection === 'myprofile') {
    const backBar = (
      <div style={{ padding: '10px 20px', background: isDarkMode ? '#0a1020' : '#f8f6f3', borderBottom: '0.5px solid rgba(26,39,68,0.1)' }}>
        <button
          onClick={() => setActiveSection('workflows')}
          style={{
            fontSize: '12px', color: NAVY_SHELL, border: '0.5px solid rgba(26,39,68,0.25)',
            borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
            background: 'none', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        >
          ← Back to workflows
        </button>
      </div>
    );

    if (!myProfileEmployeeId) {
      return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
          {backBar}
          {myProfileError ? (
            <div style={{ padding: '24px', color: '#aaa', fontSize: '14px' }}>{myProfileError}</div>
          ) : (
            <p style={{ padding: '24px', color: '#aaa', fontStyle: 'italic', fontSize: '13px' }}>Loading...</p>
          )}
        </div>
      );
    }

    return (
      <>
        {backBar}
        <EmployeeView
          employeeId={myProfileEmployeeId}
          currentUser={email}
          userRole={role}
          onBack={() => setActiveSection('workflows')}
          onLogoClick={(resetToOverview) => resetToOverview()}
        />
      </>
    );
  }

  // The Validation item is a derived capability (is_manager/is_approver from GET /employees/me),
  // not a role. VALALWAYSVIS: in the payroll/finance shell it is now ALWAYS visible for a
  // STRUCTURAL MANAGER (isManager) - even with zero pending approvals - so a manager always has a
  // stable place to check, with the page showing its own empty state. It also stays visible while
  // any approval is actually pending (isApprover). It is NOT forced on a payroll/finance account
  // that is neither: unlike the COO (the permanent fallback approver, always-on in Dashboard.js),
  // a payroll/finance non-manager is not a fallback approver, so an always-empty Validation would
  // be misleading noise. The badge shows a number ONLY when > 0. The isManager/isApprover approval
  // PERMISSION is unchanged; this only affects nav visibility.
  const showValidationNav = isManager || isApprover;
  const validationItem = showValidationNav
    ? { id: 'validation', label: 'Validation', icon: <IconCheck />, badge: pendingApprovalCount || null }
    : null;
  // FINANCE role: FINNAVGROUP - the confirmed grouped sidebar (WORKFLOWS / WORKFLOW OVERSIGHT /
  // EMPLOYEE LIFECYCLE / VALIDATION / MY PROFILE), reusing the SAME grouped {label, items}
  // render as the payroll shell (PAYNAVGROUP) and the COO Dashboard. Option A only: this regroups
  // the SIDEBAR items; it does NOT restructure WorkflowList's internal rows (the 10 finance-category
  // workflow types still all live behind the single "Financial Workflows" item - that is Option B,
  // a separate follow-up). Same ids/actions/badges as the previous flat finance nav, so this is a
  // grouping+label change only, no route or click-action change. VALIDATION stays whole-section
  // spread-gated (header never shows when the condition is false), matching PAYNAVGROUP-VALSECTION's
  // precedent: VALIDATION on validationItem (isManager || isApprover).
  // EMPLIFECYCLE: ONBOARDING and OFFBOARDING are now the single EMPLOYEE LIFECYCLE group below, and
  // the can_submit_new_starters gate moved from the group onto the New Starters ITEM so that Leavers
  // (never gated on that flag) keeps rendering for an account without it. The payroll shell's own
  // sections are a SEPARATE array and were deliberately left untouched.
  const financeNavSections = [
    // WFNAVLABEL (confirmed COO decision): item label sentence-cased to "Financial workflows"
    // (was "Financial Workflows") - navigation label only, route/component/id unchanged.
    { label: 'WORKFLOWS', items: [
      { id: 'workflows', label: 'Financial workflows', icon: <IconFiles /> },
      // OPSTASKSALL: finance gets the Tasks entry payroll has (after its workflow item), opening the
      // shared Operational Tasks card on its own tasks.
      { id: 'tasks', label: fvT('opsTasks.navTasks'), icon: <IconCheck /> },
    ]},
    { label: 'WORKFLOW OVERSIGHT', items: [
      // PAYOVERSIGHT: a rolling 60-day view, deliberately separate from the monthly Payroll
      // workflow. Bank-detail changes are fraud-sensitive, so a calendar-month cadence is the
      // wrong rhythm for noticing one.
      { id: 'oversight', label: 'Payroll Oversight', icon: <IconChart /> },
    ]},
    // EMPLIFECYCLE (confirmed decision by Marie-Lynne): the finance shell's ONBOARDING and
    // OFFBOARDING groups are merged into ONE "Employee Lifecycle" group, New Starters first and
    // Leavers second. LABELS AND GROUPING ONLY: both items keep their exact ids ('submissions',
    // 'leavers'), so their routes (/finance/new-starters, /finance/leavers via
    // FINANCE_SHELL_ROUTES), their permissions and the screens they open are all unchanged.
    //
    // THE GATE MOVED DOWN, DELIBERATELY: the whole group used to be spread-gated on
    // can_submit_new_starters, because it held only New Starters. Leavers was NEVER gated on that
    // flag and must not become so - so the gate now sits on the New Starters ITEM, and the group
    // itself always renders. An account without the delegate flag therefore still sees Employee
    // Lifecycle containing Leavers alone, exactly the Leaver access it had before.
    { label: 'EMPLOYEE LIFECYCLE', items: [
      // NSWORKLIST_RELEASE: label follows the flag - "My Submissions" (original) when OFF,
      // "New Starters" (entity-wide worklist) when ON. Same id either way; the render branches below.
      ...(canSubmitNewStarters
        ? [{ id: 'submissions', label: nsWorklistReleased ? 'New Starters' : 'My Submissions', icon: <IconFiles /> }]
        : []),
      // FINPAYROLL: Section 3 of the Leaver Form belongs to the Financial Controller. Moved here
      // from the removed OFFBOARDING group and RELABELLED "Leavers", matching the wording the COO
      // Dashboard already uses for the same destination. Id, route and screen unchanged.
      { id: 'leavers', label: 'Leavers', icon: <IconUser /> },
    ]},
    ...(validationItem ? [{ label: 'VALIDATION', items: [validationItem] }] : []),
    { label: 'MY PROFILE', items: [
      { id: 'myprofile', label: 'My Profile', icon: <IconUser />, action: openMyProfile },
    ]},
    // ONBOARDING: the finance shell's own NEW STARTERS group, whole-section gated on the per-account
    // permission (never the role). "New Starter" label + add-employee/submissions ids, identical to
    // the payroll ONBOARDING section, so the two shells present the delegate group consistently.
    // NSADDBTN: ONBOARDING holds ONLY "New Starters" (the worklist); the create-employee item moved
    // to the top-right "+ Add Employee" button, matching the payroll shell and the COO Dashboard.
  ];

  // PAYROLL role: the confirmed grouped sidebar (ONBOARDING / PAYROLL / OFFBOARDING / VALIDATION /
  // MY PROFILE), reusing the COO Dashboard's grouped {label, items} render pattern. ONBOARDING and
  // VALIDATION are each present only when their gate holds (the whole SECTION is spread-gated, not
  // just its item - an empty section would paint a stray header, mirroring Dashboard.js's own
  // spread-conditional FINANCE section): ONBOARDING on can_submit_new_starters, VALIDATION on
  // validationItem (isManager || isApprover). VALIDATION sits between OFFBOARDING and MY PROFILE per
  // the confirmed COO decision (PAYNAVGROUP-VALSECTION), so it never renders an empty header.
  //
  // NSADDBTN (confirmed final COO decision): ONBOARDING now holds ONLY "New Starters" (the
  // entity-wide worklist, id 'submissions'). The earlier same-day create-employee item ("New
  // Starter", id 'add-employee') and the "New Starter Payroll" item (NSPAYNAV) are both GONE -
  // creating an employee moved to the top-right "+ Add Employee" button (matching the COO
  // Dashboard), and the Section 3 worklist folded into "New Starters" (NSWORKLIST).
  // Confirmed scope: the WHOLE ONBOARDING section
  // stays gated on can_submit_new_starters ONLY - the same single flag that already gated this
  // section before this change. A payroll-role account without the delegate flag (e.g. a
  // Financial Controller who was never granted it) sees no ONBOARDING section at all, only
  // PAYROLL/OFFBOARDING/MY PROFILE - unchanged behaviour, re-verified below.
  const payrollNavSections = [
    // NSADDBTN: ONBOARDING now holds ONLY "New Starters" (the entity-wide worklist). The
    // create-employee item is GONE from the sidebar - it moved to a top-right "+ Add Employee"
    // button (opened via setShowAddEmployee), matching the COO Dashboard's own placement.
    ...(canSubmitNewStarters ? [{ label: 'ONBOARDING', items: [
      // NSWORKLIST_RELEASE: label follows the flag (see FinanceView's ONBOARDING above).
      { id: 'submissions',  label: nsWorklistReleased ? 'New Starters' : 'My Submissions', icon: <IconFiles /> },
    ] }] : []),
    { label: 'PAYROLL', items: [
      // PAYSPLIT: the payroll contact only ever sees the Payroll workflow here (server-filtered),
      // so the broader "Financial Workflows" label would oversell what the section holds.
      { id: 'workflows', label: 'Payroll Workflow',  icon: <IconFiles /> },
      { id: 'oversight', label: 'Payroll Oversight', icon: <IconChart /> },
      // S3TASK: the Country Lead's own task list. Scoped SERVER side to tasks assigned to this
      // account (GET /dashboard/my-tasks filters on assigned_to), so it can never show another
      // role's work. Placed in PAYROLL because every task landing here is payroll work.
      { id: 'tasks', label: 'Tasks', icon: <IconCheck /> },
    ]},
    { label: 'OFFBOARDING', items: [
      // FINPAYROLL: Section 3 of the Leaver Form belongs to the entity's payroll contact.
      { id: 'leavers', label: 'Leaver Payroll', icon: <IconUser /> },
    ]},
    // VALIDATION: its own section, gated on validationItem so the header never shows for a
    // non-manager/non-approver (same whole-section gating as ONBOARDING).
    ...(validationItem ? [{ label: 'VALIDATION', items: [validationItem] }] : []),
    { label: 'MY PROFILE', items: [
      { id: 'myprofile', label: 'My Profile', icon: <IconUser />, action: openMyProfile },
    ]},
  ];
  const finNavStyle = {
    // REDESIGN Stage 2: the shared sidebar chrome from theme.js, identical to the COO
    // Dashboard. Solid navy active fill; gold is reserved for the active item's icon.
    ...shellNav(isDarkMode),
  };
  // PAYNAVGROUP: shared section-label style, matching the COO Dashboard's navSectionLabel
  // (uppercase, muted, tracked). Defined here so both role paths reference one recipe.
  const navSectionLabelStyle = {
    fontSize: '10px', color: isDarkMode ? TEXT_MUTED : '#667085', textTransform: 'uppercase',
    letterSpacing: '0.08em', margin: '14px 16px 4px', fontWeight: 600,
  };
  const isPayrollShell = role === 'payroll';

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#f8f9fa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: NAVY_SHELL, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' }}>Σ</span>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>Enko Portal</span>
            {/* COUNTRYLEAD (confirmed COO decision): the payroll shell header reads "Country Lead",
                not "Payroll". These accounts belong to the people who run the entity in their
                country (a deputy CEO in one case, an operations director in another), so "Payroll"
                described only part of what they do. DISPLAY LABEL ONLY - the role VALUE stays
                'payroll' everywhere (database, permissions, routes, component names), and the
                finance branch is untouched. Deliberately NOT gated on canSubmitNewStarters: that
                flag arrives from an async GET /auth/me that fails closed to false, so gating the
                label on it would flicker "Payroll" then "Country Lead" on every load and would
                mislabel the account whenever that read fails. */}
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}> · {role === 'payroll' ? 'Country Lead' : 'Financial Controller'}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* TOPBARNAME: the person's name, falling back to their email when the account has
              no linked employee record. */}
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginRight: '4px' }}>{topBarIdentity || email}</span>
          <DarkModeToggle />
          {/* NSADDBTN: the "+ Add Employee" top-right button, gated on the SAME can_submit_new_starters
              flag that gated the removed sidebar item, matching the COO Dashboard's own top-right
              placement (goldBtn: solid navy). Opens the SAME AddEmployee delegate flow as a modal. */}
          {canSubmitNewStarters && (
            <button
              onClick={() => setShowAddEmployee(true)}
              style={{ backgroundColor: NAVY_FORM, color: '#fff', border: '1px solid transparent', borderRadius: '8px', padding: '8px 14px', fontSize: '13.5px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 570 }}
            >
              + Add Employee
            </button>
          )}
          {/* UKDUALROLE: deliberately NO shell-switch control here. The COO's own dual role uses
              one (a link from the COO Dashboard into the Technology shell) because those are two
              genuinely different workplaces. This account's case is the opposite: she works in
              the finance shell all day, and her secondary payroll role only needs to make
              Section 3 REACHABLE for her own entity's employees WITHIN it - which the existing
              New Starters worklist, profile and form already do, entirely inside /finance (see
              FINROUTE). Adding a switch would send her somewhere she has no reason to go. */}
          <NavBtn onClick={onLogout}>Sign out</NavBtn>
        </div>
      </div>

      {/* NSADDBTN: AddEmployee mounted as a MODAL (not inline) - same delegate wiring as before
          (createdBy set, NO currentUserRole so SMCR classification fields stay hidden, entity-scoped
          Location dropdown with SENEGALLOC multi-location support). onCreated refreshes the New
          Starters worklist so a just-created employee appears there immediately. */}
      {showAddEmployee && canSubmitNewStarters && (
        <AddEmployee
          createdBy={email}
          onClose={() => setShowAddEmployee(false)}
          onCreated={() => { setShowAddEmployee(false); if (activeSection === 'submissions') fetchPayrollNewStarters(); }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* ── SIDEBAR ── (RESPONSIVE Phase 3: the shared <Sidebar>; behaviour byte-identical to the
            previous inline render - same {label,items} sections, same finNavStyle tokens, same
            navSectionLabelStyle, same inline gold badge, same onClick, same responsive width. The
            isPayrollShell gate still only SELECTS which section array. FINNAVGROUP scope unchanged.) */}
        <Sidebar
          width={isNarrow ? '180px' : '200px'}
          containerStyle={{ backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', borderRight: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`, minHeight: 'calc(100vh - 53px)', paddingTop: '8px' }}
          sections={isPayrollShell ? payrollNavSections : financeNavSections}
          activeSection={activeSection}
          onSelect={(item) => { if (item.action) item.action(); else setActiveSection(item.id); }}
          itemStyle={finNavStyle.item}
          itemActiveStyle={finNavStyle.itemActive}
          sectionLabelStyle={navSectionLabelStyle}
          renderBadge={(item) => item.badge ? (
            <span style={{ backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 600, flexShrink: 0 }}>{item.badge}</span>
          ) : null}
        />

        {/* ── MAIN CONTENT ── */}
        <div style={{ flex: 1, padding: '20px 24px', minWidth: 0 }}>
          {activeSection === 'workflows' && <WorkflowList currentUserRole={role} />}

          {/* S3TASK: the Country Lead's own tasks. The list comes from GET /dashboard/my-tasks,
              which resolves the CALLER's employee record and filters Task.assigned_to to it - so
              this shows only what this account must act on, never another role's tasks and never
              an employee outside their entity (such a task is assigned to a different contact). */}
          {/* OPSTASKSALL: the COO's Operational Tasks card, on this account's OWN tasks (GET
              /dashboard/my-tasks, filtered server side on the caller), for payroll and finance alike,
              in English or French. The buttons are the ones this list had: Open, the workflow step's
              Mark complete and the enrolment tasks' Mark complete; rows are not links, as before. */}
          {activeSection === 'tasks' && (
            myTasksLoading ? (
              <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>Loading...</p>
            ) : myTasksError ? (
              /* LOADSTATES: a failed fetch never reads as an empty list. */
              <LoadError
                dark={isDarkMode}
                message="Couldn't load your tasks."
                onRetry={fetchMyTasks}
              />
            ) : (
              <OperationalTasksCard
                tasks={myTasks} s={mkS(isDarkMode)} isDarkMode={isDarkMode} t={fvT}
                tab={myTasksTab} onTabChange={setMyTasksTab}
                error={myTaskCompleteError === true ? fvT('enrolmentTask.completeError', 'Could not complete this task.') : (myTaskCompleteError || null)}
                titleOf={(t) => (isWorkflowStepTask(t) ? shownTaskTitle(t) : t.title)}
                renderActions={(t) => (
                  <>
                  {/* TASKOPENFIX: this used to always switch to the Submissions tab,
                      regardless of which employee the task concerned - a Section 3 task
                      for one entity member opened a list showing everyone, not that
                      person. The response already carries employee_id per task
                      (dashboard.py's get_my_tasks), and clProfileOpenEmployee/
                      payrollEmployeePath already exist for opening a specific employee
                      from this same shell (NSLIST's View button uses the identical call) -
                      so this reuses that path instead of inventing a new one. A SEPARATE
                      setActiveSection('submissions') call is NOT needed alongside it and
                      would in fact race it: activeSection (above) already resolves to
                      'submissions' the moment clProfileEmployeeId is set, and
                      setActiveSection('submissions') navigates to the bare list path,
                      which would overwrite the more specific per-employee path
                      clProfileOpenEmployee just wrote.

                      TWO THINGS ARE CHECKED, NOT ASSUMED, before that path is taken:
                      t.employee_id (a task whose employee cannot be resolved - missing/
                      null on the response - gets a disabled, greyed button instead of a
                      click that opens nothing) and nsWorklistReleased (the OTHER submissions
                      branch, still live while this flag is off, renders NewStarterForm from
                      payrollNewStarter's OWN fields - first_name/employee_type/
                      primary_entity - which employee_id alone cannot supply; opening it
                      with only an id would show a form with blank name/type/entity rather
                      than the honest "cannot open" state this task requires). A stale-but-
                      present id (a real 404 or fetch failure once EmployeeProfile mounts)
                      is already handled by that component's own "Employee not found" /
                      LoadError states - unaffected by either guard here. */}
                  {/* WFTASKS: a workflow step task opens that workflow step. */}
                  {isWorkflowStepTask(t) ? (
                    <button
                      type="button"
                      data-testid="mytask-open-workflow-step"
                      style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                      onClick={() => shellNavigate(workflowStepOpenPath(t))}
                    >
                      {fvT('workflowStepTask.open')}
                    </button>
                  ) : (() => {
                    const openable = Boolean(t.employee_id) && nsWorklistReleased;
                    return (
                      <button
                        disabled={!openable}
                        title={openable ? undefined : (!t.employee_id
                          ? 'This task has no employee to open'
                          : 'Employee opening is not yet available in this view')}
                        style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${openable ? GOLD : '#ccc'}`, color: openable ? GOLD : '#aaa', background: 'none', cursor: openable ? 'pointer' : 'not-allowed', fontWeight: 600, whiteSpace: 'nowrap' }}
                        onClick={() => {
                          if (!openable) return;
                          clProfileOpenEmployee({ employee_id: t.employee_id });
                          // Only when there is no URL to derive activeSection from:
                          // clProfileOpenEmployee itself already navigates the URL when
                          // urlRoutes is set (see its own definition), and activeSection
                          // in that mode resolves to 'submissions' the instant
                          // clProfileEmployeeId is set - a second, explicit switch here
                          // would instead navigate to the bare list path and overwrite the
                          // more specific per-employee path just written.
                          if (!urlRoutes) setActiveSection('submissions');
                        }}
                      >
                        Open
                      </button>
                    );
                  })()}
                  {isWorkflowStepTask(t) && (
                    <button
                      type="button"
                      data-testid="mytask-complete-workflow-step"
                      disabled={completingMyTaskId === t.task_id}
                      onClick={() => completeMyWorkflowStepTask(t)}
                      style={{ marginLeft: '8px', fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: completingMyTaskId === t.task_id ? 'default' : 'pointer' }}
                    >
                      {completingMyTaskId === t.task_id
                        ? fvT('workflowStepTask.saving')
                        : fvT('workflowStepTask.markComplete')}
                    </button>
                  )}
                  {ENROLMENT_TASK_TYPES.includes(t.task_type) && (
                    <button
                      type="button"
                      disabled={completingMyTaskId === t.task_id}
                      onClick={() => completeMyTask(t.task_id)}
                      style={{ marginLeft: '8px', fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: completingMyTaskId === t.task_id ? 'default' : 'pointer' }}
                    >
                      {completingMyTaskId === t.task_id
                        ? fvT('enrolmentTask.saving', 'Saving...')
                        : fvT('enrolmentTask.markComplete', 'Mark complete')}
                    </button>
                  )}
                  </>
                )}
              />
            )
          )}

          {/* PAYOVERSIGHT: rolling 60-day view. Read-only - it reads the two source tables
              directly and never touches a workflow instance. */}
          {activeSection === 'oversight' && (() => {
            const cardBg = isDarkMode ? '#111d36' : '#fff';
            const cardBorder = `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`;
            const heading = { margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' };
            const sub = { margin: '0 0 16px 0', fontSize: '12px', color: '#8a8a8a' };
            const cardTitle = { margin: '0 0 10px 0', fontSize: '14px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' };
            const card = { background: cardBg, border: cardBorder, borderRadius: '8px', padding: '16px 18px', marginBottom: '12px' };
            const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f4f4f4'}`, fontSize: '13px' };
            const emptyStyle = { fontSize: '13px', color: '#aaa', fontStyle: 'italic', margin: 0 };
            const fmt = (d) => (d ? toDisplayDate(d).toLocaleDateString() : '');

            // PAYOVERSIGHT2: the log stores raw COLUMN names ("rib_iban"), which leaked straight
            // onto the screen. BANKCHANGE can only ever record these three fields
            // (new_starter.py's changed-field list), so the map is complete, not a best guess.
            //
            // rib_iban is deliberately "Account number or IBAN": EmployeeView labels that SAME
            // column "Account number" for a UK employee and "IBAN" for everyone else, and this
            // row has no entity context to choose between them. Naming both is honest; picking
            // one would be wrong half the time.
            const BANK_FIELD_LABELS = {
              rib_iban: 'Account number or IBAN',
              bank_iban: 'IBAN',
              bank_account_number: 'Account number',
            };
            const bankFieldLabel = (raw) => (raw || '')
              .split(',')
              .map(f => f.trim())
              .filter(Boolean)
              .map(f => BANK_FIELD_LABELS[f] || f)   // unknown key falls back to the raw name
              .join(', ');

            return (
              <div>
                <h2 style={heading}>Payroll Oversight</h2>
                {/* PAYOVERSIGHT2: the window is the current calendar month, so the subtitle
                    names the month explicitly. Without it a near-empty view on the 1st looks
                    broken rather than simply early in the period. */}
                <p style={sub}>
                  Bank detail changes and new joiners this month
                  {oversight?.period_start
                    ? ` (since ${toDisplayDate(oversight.period_start).toLocaleDateString()})`
                    : ''}
                  {oversight && !oversight.entity_scoped ? ', all entities' : ', for your entity'}.
                </p>

                {oversightLoading && <p style={emptyStyle}>Loading...</p>}
                {oversightError && (
                  <LoadError
                    dark={isDarkMode}
                    message="Couldn't load the payroll oversight data."
                    onRetry={fetchOversight}
                  />
                )}

                {!oversightLoading && !oversightError && oversight && (
                  <>
                    {/* Bank changes FIRST: the fraud-sensitive one is the reason this view exists. */}
                    <div style={card}>
                      <p style={cardTitle}>
                        Bank detail changes ({oversight.bank_changes.length})
                      </p>
                      {oversight.bank_changes.length === 0 ? (
                        <p style={emptyStyle}>No bank detail changes in this period</p>
                      ) : oversight.bank_changes.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => openMiniProfile(b.employee_id)}
                          style={{ ...rowStyle, width: '100%', background: 'none', border: 'none', borderBottom: rowStyle.borderBottom, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                        >
                          <span style={{ color: isDarkMode ? '#e8e4d8' : '#1a1a2e', fontWeight: 500 }}>
                            {b.name}
                            <span style={{ color: '#8a8a8a', fontWeight: 400 }}>
                              {b.changed_fields ? ` - ${bankFieldLabel(b.changed_fields)}` : ''}
                            </span>
                          </span>
                          <span style={{ color: '#8a8a8a', whiteSpace: 'nowrap' }}>{fmt(b.changed_at)}</span>
                        </button>
                      ))}
                    </div>

                    <div style={card}>
                      <p style={cardTitle}>New joiners ({oversight.joiners.length})</p>
                      {oversight.joiners.length === 0 ? (
                        <p style={emptyStyle}>No new joiners in this period</p>
                      ) : oversight.joiners.map(j => (
                        /* PAYMINI: rows open a NARROW read-only mini profile in a modal, NOT the
                           full employee profile. EmployeeProfile is mounted only in the COO
                           Dashboard shell, and both GET /employees/{id} and
                           GET /dashboard/employee/{id} exclude finance and payroll - those
                           viewers get 403. Rather than widening either (FINPAYROLL deliberately
                           gives finance no employee-list access), the modal reads its own narrow
                           endpoint, entity-scoped by the same _resolve_payroll_contact rule. */
                        <button
                          key={j.id}
                          type="button"
                          onClick={() => openMiniProfile(j.id)}
                          style={{ ...rowStyle, width: '100%', background: 'none', border: 'none', borderBottom: rowStyle.borderBottom, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                        >
                          <span style={{ color: isDarkMode ? '#e8e4d8' : '#1a1a2e', fontWeight: 500 }}>
                            {j.name}
                            <span style={{ color: '#8a8a8a', fontWeight: 400 }}>
                              {j.job_title ? ` - ${j.job_title}` : ''}
                            </span>
                          </span>
                          <span style={{ color: '#8a8a8a', whiteSpace: 'nowrap' }}>{fmt(j.created_at)}</span>
                        </button>
                      ))}
                    </div>

                    {/* Leavers: a COUNT that links across. The Leaver Payroll worklist owns the
                        rows - duplicating them here would create two lists to keep in step. */}
                    <div style={card}>
                      <p style={cardTitle}>Leavers awaiting your Section 3</p>
                      {oversight.leavers_pending_section3 === 0 ? (
                        <p style={emptyStyle}>No leaver payroll sections pending</p>
                      ) : (
                        <button
                          onClick={() => setActiveSection('leavers')}
                          style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600 }}
                        >
                          {oversight.leavers_pending_section3} awaiting Section 3 - open Leaver Payroll
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* PAYMINI: the read-only mini profile. Follows the same overlay pattern as
                    ConfirmModal/COMPMODAL - fixed rgba(0,0,0,0.5) backdrop, centred card at
                    radius 12, role="dialog" aria-modal, backdrop click closes - so it looks and
                    behaves like every other modal in the product. Deliberately shows bank STATUS
                    and change history only, never the account values themselves. */}
                {miniProfile && (
                  <div
                    onClick={closeMiniProfile}
                    style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      role="dialog" aria-modal="true" aria-label="Employee payroll summary"
                      style={{
                        backgroundColor: isDarkMode ? '#131c2e' : '#fff',
                        color: isDarkMode ? '#eaecf0' : '#1a1a2e',
                        borderRadius: '12px', padding: '24px', width: '460px',
                        maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 96px)',
                        overflowY: 'auto',
                        boxShadow: '0 12px 16px -4px rgba(16,24,40,.18), 0 4px 6px -2px rgba(16,24,40,.08)',
                        border: isDarkMode ? '1px solid #25324c' : 'none',
                      }}
                    >
                      {miniLoading && <p style={emptyStyle}>Loading...</p>}
                      {miniError && (
                        <>
                          <h2 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>
                            Not available
                          </h2>
                          <p style={{ margin: '0 0 20px 0', fontSize: '13px', lineHeight: 1.5, color: isDarkMode ? '#b6bdc9' : '#444' }}>
                            This employee's payroll summary could not be loaded. They may be
                            outside your payroll entity.
                          </p>
                        </>
                      )}
                      {!miniLoading && !miniError && miniProfile.name && (
                        <>
                          <h2 style={{ margin: '0 0 2px 0', fontSize: '16px', fontWeight: 600 }}>
                            {miniProfile.name}
                          </h2>
                          <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#8a8a8a' }}>
                            {miniProfile.job_title || 'Job title not set'}
                          </p>

                          <div style={{ fontSize: '13px', lineHeight: 1.9, marginBottom: '16px' }}>
                            <div>Entity: {miniProfile.primary_entity || 'Not set'}</div>
                            <div>Location: {miniProfile.location || 'Not set'}</div>
                            <div>Email: {miniProfile.company_email || 'Not set'}</div>
                            <div>Start date: {fmt(miniProfile.start_date) || 'Not set'}</div>
                          </div>

                          <p style={{ ...cardTitle, marginBottom: '6px' }}>Bank status</p>
                          <div style={{ fontSize: '13px', lineHeight: 1.9, marginBottom: '10px' }}>
                            <div>
                              Bank details on file:{' '}
                              {miniProfile.bank_details_on_file ? 'Yes' : 'No'}
                            </div>
                            <div>Recorded changes: {miniProfile.bank_change_count ?? 0}</div>
                          </div>

                          {/* PAYBANK: click-to-reveal. Hidden by default and re-hidden on close,
                              so an account number is never on screen unless someone deliberately
                              asked for it in that moment - which also keeps it out of casual
                              shoulder view and out of any screen share of the oversight list. */}
                          {miniProfile.bank_details_on_file && (
                            <div style={{ marginBottom: '16px' }}>
                              {!bankRevealed ? (
                                <button
                                  type="button"
                                  onClick={revealBankDetails}
                                  disabled={bankLoading}
                                  style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: bankLoading ? 'default' : 'pointer', fontWeight: 600 }}
                                >
                                  {bankLoading ? 'Loading...' : 'Show bank details'}
                                </button>
                              ) : (
                                <div style={{ fontSize: '13px', lineHeight: 1.9 }}>
                                  <div>Bank name: {miniProfile.bank_details?.bank_name || 'Not set'}</div>
                                  <div>Account holder: {miniProfile.bank_details?.account_holder || 'Not set'}</div>
                                  {miniProfile.bank_details?.sort_code && (
                                    <div>Sort code: {miniProfile.bank_details.sort_code}</div>
                                  )}
                                  <div>
                                    {miniProfile.bank_details?.account_number_label || 'Account number'}:{' '}
                                    {miniProfile.bank_details?.account_number || 'Not set'}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setBankRevealed(false)}
                                    style={{ marginTop: '6px', fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #8a8a8a', color: '#8a8a8a', background: 'none', cursor: 'pointer' }}
                                  >
                                    Hide
                                  </button>
                                </div>
                              )}
                              {bankError && (
                                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#c0392b' }}>
                                  Bank details could not be loaded.
                                </p>
                              )}
                            </div>
                          )}

                          <p style={{ ...cardTitle, marginBottom: '6px' }}>Change history</p>
                          {(!miniProfile.bank_changes || miniProfile.bank_changes.length === 0) ? (
                            <p style={emptyStyle}>No bank detail changes recorded</p>
                          ) : miniProfile.bank_changes.map(c => (
                            <div key={c.id} style={{ ...rowStyle, fontSize: '12px' }}>
                              <span>{bankFieldLabel(c.changed_fields) || 'Not recorded'}</span>
                              <span style={{ color: '#8a8a8a', whiteSpace: 'nowrap' }}>{fmt(c.changed_at)}</span>
                            </div>
                          ))}
                        </>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button
                          type="button"
                          onClick={closeMiniProfile}
                          style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {activeSection === 'validation' && <ValidationView />}
          {/* FINPAYROLL: Section 3 worklist + the real LeaverForm pinned to Section 3. The
              backend enforces the section split (finance is 403 on Sections 1/2), so onNavigate
              is a deliberate no-op here - the Financial Controller stays on their section.
              S3SPLIT: allowedSections={[3]} means Manager and IT are no longer rendered or
              navigable at all (previously the progress bar let a payroll user click into them),
              and payrollScopeOnly hides the Admin/Loans/Guarantees groups inside Section 3. */}
          {activeSection === 'leavers' && (payrollLeaver ? (
            <LeaverForm
              employeeId={payrollLeaver.employee_id}
              employeeName={`${payrollLeaver.first_name} ${payrollLeaver.last_name}`}
              requiresVisa={payrollLeaver.requires_visa}
              location={payrollLeaver.location}
              section={3}
              completedBy={email}
              allowedSections={[3]}
              payrollScopeOnly
              onBack={() => { setPayrollLeaver(null); fetchPayrollLeavers(); }}
              onNavigate={() => {}}
              onComplete={() => { setPayrollLeaver(null); fetchPayrollLeavers(); }}
            />
          ) : (
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>Leaver Payroll</h2>
              <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#8a8a8a' }}>
                Leavers whose HR &amp; Payroll section (final pay, P45, pension, benefits) is still open.
              </p>
              {/* LOADSTATES: this screen had NEITHER a loading nor an error state, so the empty
                  line below was shown while the fetch was still running AND when it failed. All
                  three are now distinct, in the same order PAYOVERSIGHT renders them. */}
              {leaversLoading ? (
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>Loading...</p>
              ) : leaversError ? (
                <LoadError
                  dark={isDarkMode}
                  message="Couldn't load the leaver payroll list."
                  onRetry={fetchPayrollLeavers}
                />
              ) : payrollLeavers.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>No leaver payroll sections pending</p>
              ) : payrollLeavers.map(lv => (
                <div key={lv.employee_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 16px', marginBottom: '8px', borderRadius: '8px', backgroundColor: isDarkMode ? '#111d36' : '#fff', border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}` }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 2px 0', fontSize: '13px', fontWeight: 500, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>{lv.first_name} {lv.last_name}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
                      {lv.company_email || ''}{lv.last_working_date ? ` · Last working day: ${toDisplayDate(lv.last_working_date).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <button
                    style={{ flexShrink: 0, fontSize: '12px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setPayrollLeaver(lv)}
                  >
                    Fill Section 3 (Payroll)
                  </button>
                </div>
              ))}
            </div>
          ))}

          {/* NSADDBTN: the inline 'add-employee' section render is GONE - AddEmployee now opens as a
              top-right-button modal (above), matching the COO Dashboard. */}

          {/* NSWORKLIST_RELEASE: the ONBOARDING 'submissions' section has TWO renders, chosen by the
              release flag (nsWorklistReleased, from GET /auth/me):

              FLAG ON (approved) -> NSWORKLIST: the upgraded ENTITY-WIDE "New Starters" worklist
              (replaces both the old NSPAYNAV Section 3 worklist and the NSFINVIEW per-creator "My
              Submissions" table). Shows EVERY non-archived new starter in this account's entity
              (server-scoped via the ?all=true endpoint), with S1..S4 status columns matching the
              COO's own worklist, and a "View" per row. PAYPROFILEVIEW: View opens the FULL
              EmployeeProfile (role="payroll"), which the backend admits narrowly + entity-scoped
              (payroll_delegate_may_view_profile) and which trims itself to Payroll & Benefits -
              Documents, Fill IT Setup, Compliance/Regulatory Classification, Background Check, and
              every visa/Right-to-Work/sponsor section are hidden for role==='payroll'; "Fill Payroll
              & Benefits" stays actionable. Back returns to the worklist and refreshes it.

              FLAG OFF (default, below) -> the ORIGINAL "My Submissions" table: read-only per-creator
              progress table (own creations only, no View), exactly as before NSWORKLIST. */}
          {activeSection === 'submissions' && canSubmitNewStarters && nsWorklistReleased && (clProfileOpen ? (
            /* S3ONLYFIN: the FINANCE role never opens the full employee profile from this
               worklist. It opens the SECTION-3-ONLY NewStarterForm instead - the very same mount
               the flag-OFF My Submissions branch below already uses (MYSUBSFILL), with
               allowedSections={[3]} so the form itself renders no navigation to Sections 1, 2
               or 4. Nothing new is invented here, and the Country Lead path below is untouched.

               The backend is unchanged and remains the real control: Section 3 access is already
               entity-scoped by _resolve_payroll_contact, so this decides only what is OFFERED. */
            s3OnlyWorklist ? (
              <NewStarterForm
                employeeId={clProfileEmployeeId || payrollNewStarter?.employee_id}
                employeeName={clProfileName}
                employeeType={clProfileRow?.employee_type}
                employeeEntity={clProfileRow?.primary_entity}
                section={3}
                completedBy={email}
                currentUserRole={role}
                allowedSections={[3]}
                onBack={clProfileBack}
                onNavigate={() => {}}
                onComplete={clProfileBack}
              />
            ) : (
            <>
              {/* CLBACK (confirmed decision by Marie-Lynne): opening an employee from the New
                  Starters list left NO way back to it - the profile is reached by a STATE change
                  (setPayrollNewStarter), not a route, so it does not change the URL and the browser
                  back button leaves the shell entirely rather than returning to the list.
                  onBack was already wired here but EmployeeProfile never renders a control for it,
                  so nothing surfaced it.

                  This follows the COO Dashboard's own pattern rather than inventing one: a
                  breadcrumb ABOVE the profile, rendered by the SHELL (not inside EmployeeProfile),
                  whose first crumb returns to the list and refreshes it. Same onBack the archive
                  flow already calls, so there is one definition of "go back" here.

                  WHICH list it returns to is not ambiguous: this branch is the nsWorklistReleased
                  ON path, and the flag-OFF My Submissions branch below has its OWN separate
                  render. The two are mutually exclusive on the same flag, so the crumb can only
                  ever lead back to the list the user actually came from. */}
              {/* CLCRUMB: hidden while the New Starter Form is open, because EmployeeProfile
                  renders its OWN breadcrumb for that level - "New Starters > {name} > New Starter
                  Form" - and the two were stacking. The form's crumb is the one kept: it is the
                  only one that names the level actually on screen, and its first two crumbs
                  reproduce this one exactly, so nothing is lost by hiding this while it shows.
                  At PROFILE level the form's crumb does not render, so this one is still the only
                  breadcrumb there and remains the way back to the list.
                  The mechanism is NOT new: onFormOpenChange already existed on EmployeeProfile and
                  the COO Dashboard already drives its own outer crumb from it the same way. This
                  shell simply never passed the prop, so its crumb had no way to know. */}
              {!clProfileFormOpen && (
                <div style={{ marginBottom: '16px' }}>
                  <span
                    style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer' }}
                    onMouseOver={e => { e.currentTarget.style.color = GOLD; }}
                    onMouseOut={e => { e.currentTarget.style.color = '#9ba8c0'; }}
                    onClick={clProfileBack}
                  >
                    New Starters
                  </span>
                  <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                  <span style={{ fontSize: '13px', color: isDarkMode ? TEXT_DARK_MODE : '#1a2744', fontWeight: 500 }}>
                    {clProfileName}
                  </span>
                </div>
              )}
              <EmployeeProfile
                employeeId={clProfileEmployeeId || payrollNewStarter?.employee_id}
                currentUser={email}
                role={role}
                onBack={clProfileBack}
                onFormOpenChange={setClProfileFormOpen}
                onNavigateToNewStarters={clProfileBack}
              />
            </>
            )
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>New Starters</h2>
                {/* S3FILTER: wrapped rather than passed bare. fetchPayrollNewStarters now takes a
                    pendingOnly argument, and a bare handler would hand it the CLICK EVENT, which
                    is truthy - silently forcing pending-only on every Refresh. */}
                <button onClick={() => fetchPayrollNewStarters()} style={{ fontSize: '12px', color: GOLD, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>Refresh</button>
              </div>
              {/* S3FILTER: the subtitle follows the filter, so the page never claims to be showing
                  everyone while a filter is narrowing it. */}
              <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#8a8a8a' }}>
                {nsS3PendingOnly
                  ? 'New starters in your entity whose Payroll and Benefits section is still pending.'
                  : 'All new starters in your entity. Open one to complete its Payroll & Benefits section.'}
              </p>
              {/* NSLIST: filters the rows ALREADY fetched for this entity. It never queries the
                  server, so it cannot surface anyone outside the entity scope. */}
              {/* S3FILTER: the filter sits directly BESIDE the search box, on one row, because the
                  two narrow the same list and are read together - a checkbox elsewhere on the page
                  would leave the reader unsure which controls the table. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <input
                  type="text"
                  value={nsSearch}
                  onChange={e => setNsSearch(e.target.value)}
                  placeholder="Search by name"
                  style={{
                    width: '100%', maxWidth: '280px', padding: '7px 10px',
                    fontSize: '13px', borderRadius: '6px',
                    border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : '#ddd'}`,
                    background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#fff',
                    color: isDarkMode ? TEXT_DARK_MODE : '#1a1a2e',
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', cursor: 'pointer', color: isDarkMode ? TEXT_DARK_MODE : '#1a1a2e', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={nsS3PendingOnly}
                    onChange={toggleNsS3PendingOnly}
                    style={{ cursor: 'pointer' }}
                  />
                  Payroll &amp; Benefits still pending
                </label>
              </div>
              {submissionsLoading ? (
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>Loading...</p>
              ) : submissionsError ? (
                /* LOADSTATES: a failure used to render "No new starters in your entity." - a
                   positive claim about the entity, made on no data at all. Retry calls
                   fetchPayrollNewStarters, the loader this ON-flag branch actually uses. */
                <LoadError
                  dark={isDarkMode}
                  message="Couldn't load the new starters list."
                  onRetry={() => fetchPayrollNewStarters()}
                />
              ) : payrollNewStarters.length === 0 ? (
                /* S3FILTER: with the filter ON an empty response means "nothing PENDING", which is
                   good news and a different fact from an empty entity. Saying "No new starters in
                   your entity" there would be a false claim about the entity, the same class of
                   error LOADSTATES fixed for a failed fetch. */
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>
                  {nsS3PendingOnly
                    ? 'No new starters with Payroll and Benefits still pending. Untick the filter to see everyone in your entity.'
                    : 'No new starters in your entity.'}
                </p>
              ) : nsFiltered.length === 0 ? (
                /* NSLIST: a search that matches nothing gets its OWN message naming the term,
                   distinct from "no new starters in your entity" above - the two mean different
                   things, and a blank table would leave the user unsure which applied.
                   S3FILTER: when BOTH are active the message names BOTH, so the reader can tell
                   which one to relax - a message naming only the search would leave them retyping
                   a name that does exist, just not among the pending rows. */
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>
                  {nsS3PendingOnly
                    ? `No new starters with Payroll and Benefits still pending match "${nsSearch.trim()}".`
                    : `No new starters match "${nsSearch.trim()}".`}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {/* NSLIST: the COUNTRY column is present only for a multi-location entity,
                          so a single-country account sees exactly the columns it saw before. */}
                      {(s3OnlyWorklist
                        // S3ONLYFIN: finance sees the employee and their Section 3 progress, and
                        // nothing else. S1/S2/S4, Country and Date created all belong to the
                        // Country Lead view below.
                        ? [
                          { label: 'Employee', key: 'name' },
                          { label: 'S3 Payroll', key: 's3' },
                          { label: '', key: null },
                        ]
                        : [
                          { label: 'Employee', key: 'name' },
                          ...(showCountryColumn ? [{ label: 'Country', key: 'country' }] : []),
                          { label: 'S1 Personal', key: 's1' },
                          { label: 'S2 IT', key: 's2' },
                          { label: 'S3 Payroll', key: 's3' },
                          // S4AB: Section 4 is two steps, 4A (compliance) and 4B (HR).
                          { label: `S${docText('section4ab.short4a', currentLang())}`, key: 's4a' },
                          { label: `S${docText('section4ab.short4b', currentLang())}`, key: 's4b' },
                          { label: 'Date created', key: 'created' },
                          { label: '', key: null },
                        ]
                      ).map((h, i) => (
                        <th
                          key={i}
                          onClick={h.key ? () => nsToggleSort(h.key) : undefined}
                          style={{ textAlign: 'left', fontSize: '11px', fontWeight: 600, color: isDarkMode ? TEXT_MUTED : '#aaa', padding: '6px 10px', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}`, textTransform: 'uppercase', letterSpacing: '0.05em', cursor: h.key ? 'pointer' : 'default', userSelect: 'none' }}
                        >
                          {h.label}
                          {h.key && nsSort.key === h.key && (
                            <span style={{ marginLeft: '4px', color: GOLD }}>{nsSort.dir === 'asc' ? '\u2191' : '\u2193'}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nsFiltered.map(ns => {
                      const dot = (done) => (
                        <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '10px', padding: '2px 8px', backgroundColor: done ? '#eaf3ee' : '#fdf8ee', color: done ? '#2D4A3E' : '#7a4d00' }}>
                          {done ? '✓' : '-'}
                        </span>
                      );
                      return (
                        <tr key={ns.employee_id} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f4f4f4'}` }}>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL }}>
                            {ns.first_name} {ns.last_name}
                            {/* ACCESSWAIT: the SAME shared component the COO list uses, so the two
                                cannot drift. The row here is a PendingPayrollNewStarter rather
                                than an Employee, so its flat fields are mapped onto the employee
                                and form shapes the helper expects - no second rule is written. */}
                            <AccessWait
                              employee={{ created_at: ns.created_at, is_migrated: ns.is_migrated, migration_confirmed: ns.migration_confirmed }}
                              form={{ section2_completed: ns.section2_completed, welcome_email_sent_at: ns.welcome_email_sent_at }}
                            />
                            <div style={{ fontSize: '11px', color: isDarkMode ? TEXT_MUTED : '#aaa', marginTop: '2px' }}>{ns.company_email}</div>
                          </td>
                          {/* S3ONLYFIN: exactly the cells the header declares, in the same order.
                              The S3 cell is common to both views, so it sits outside the branch. */}
                          {!s3OnlyWorklist && showCountryColumn && (
                            <td style={{ padding: '10px', fontSize: '12px', color: isDarkMode ? TEXT_MUTED : '#555' }}>
                              {nsCountry(ns)}
                            </td>
                          )}
                          {!s3OnlyWorklist && <td style={{ padding: '10px' }}>{dot(ns.section1_completed)}</td>}
                          {!s3OnlyWorklist && <td style={{ padding: '10px' }}>{dot(ns.section2_completed)}</td>}
                          <td style={{ padding: '10px' }}>{dot(ns.section3_completed)}</td>
                          {!s3OnlyWorklist && <td style={{ padding: '10px' }}>{dot(s4aDone(ns))}</td>}
                          {!s3OnlyWorklist && <td style={{ padding: '10px' }}>{dot(s4bDone(ns))}</td>}
                          {!s3OnlyWorklist && (
                            <td style={{ padding: '10px', fontSize: '12px', color: isDarkMode ? TEXT_MUTED : '#aaa' }}>
                              {ns.created_at ? toDisplayDate(ns.created_at).toLocaleDateString() : '-'}
                            </td>
                          )}
                          <td style={{ padding: '10px' }}>
                            {/* S3ONLYFIN: for finance the action IS Section 3, so a row whose
                                Section 3 is already complete offers NO control rather than one
                                leading to a screen with nothing left to do. The Country Lead View
                                is unchanged and still shown on every row. */}
                            {s3OnlyWorklist ? (
                              !ns.section3_completed && (
                                <button
                                  style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600 }}
                                  onClick={() => clProfileOpenEmployee(ns)}
                                >
                                  Fill Payroll and Benefits
                                </button>
                              )
                            ) : (
                              <button
                                style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => clProfileOpenEmployee(ns)}
                              >
                                View
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          {/* NSWORKLIST_RELEASE (flag OFF, DEFAULT): the ORIGINAL "My Submissions" table - the same
              read-only tracking table EmployeeView.js renders, on the same two data sources
              (employees created_by this account + each one's new-starter form for the progress
              count). No entity-wide list, no clickable View, no mini/full profile. This is the
              live behaviour until Craig approves the worklist (NEW_STARTERS_WORKLIST_RELEASED). */}
          {activeSection === 'submissions' && canSubmitNewStarters && !nsWorklistReleased && (payrollNewStarter ? (
            /* MYSUBSFILL (confirmed COO decision): "Fill Payroll & Benefits" on a My Submissions row
               opens the EXISTING Section-3-only NewStarterForm (allowedSections=[3]) for that
               employee - the SAME mechanism the pre-NSWORKLIST payroll worklist used, NOT the
               flag-gated PAYPROFILEVIEW EmployeeProfile. The backend admits a payroll+delegate to
               read/submit Section 3 for its own-entity employees independent of
               NEW_STARTER_PAYROLL_SCOPE_RELEASED (MYSUBSFILL in new_starter.py). onBack/onComplete
               return to the table and refresh it. */
            <NewStarterForm
              employeeId={payrollNewStarter.employee_id}
              employeeName={`${payrollNewStarter.first_name} ${payrollNewStarter.last_name}`}
              employeeType={payrollNewStarter.employee_type}
              employeeEntity={payrollNewStarter.primary_entity}
              section={3}
              completedBy={email}
              currentUserRole={role}
              allowedSections={[3]}
              onBack={() => { setPayrollNewStarter(null); loadSubmissions(); }}
              onNavigate={() => {}}
              onComplete={() => { setPayrollNewStarter(null); loadSubmissions(); }}
            />
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>My Submissions</h2>
                <button onClick={loadSubmissions} style={{ fontSize: '12px', color: GOLD, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>Refresh</button>
              </div>
              {submissionsLoading ? (
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>Loading...</p>
              ) : submissionsError ? (
                /* LOADSTATES: retry calls loadSubmissions, the loader THIS flag-OFF branch uses -
                   not fetchPayrollNewStarters, which fetches a different endpoint and would leave
                   this table exactly as broken while appearing to have retried. */
                <LoadError
                  dark={isDarkMode}
                  message="Couldn't load your submissions."
                  onRetry={loadSubmissions}
                />
              ) : submissions.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>No employees submitted yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Employee', 'Entity', 'Date created', 'Onboarding progress', 'Status', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', fontSize: '11px', fontWeight: 600, color: isDarkMode ? TEXT_MUTED : '#aaa', padding: '6px 10px', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(emp => {
                      const ns = nsFormsMap[emp.id];
                      // S4AB: five onboarding steps, Section 4 counting as 4A and 4B.
                      const count = ns ? onboardingDoneCount(ns) : 0;
                      const done = count === ONBOARDING_STEP_COUNT;
                      return (
                        <tr key={emp.id} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f4f4f4'}` }}>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL }}>
                            {emp.first_name} {emp.last_name}
                            <div style={{ fontSize: '11px', color: isDarkMode ? TEXT_MUTED : '#aaa', marginTop: '2px' }}>{emp.company_email}</div>
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555' }}>{emp.primary_entity || '-'}</td>
                          <td style={{ padding: '10px', fontSize: '12px', color: isDarkMode ? TEXT_MUTED : '#aaa' }}>
                            {emp.created_at ? toDisplayDate(emp.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555' }}>{count} / {ONBOARDING_STEP_COUNT} sections complete</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '10px', padding: '2px 8px', backgroundColor: done ? '#eaf3ee' : '#fdf8ee', color: done ? '#2D4A3E' : '#7a4d00' }}>
                              {done ? 'Complete' : 'In progress'}
                            </span>
                          </td>
                          <td style={{ padding: '10px' }}>
                            {/* MYSUBSFILL: per-row action opening Section 3 (Payroll & Benefits) for this
                                employee. Sets payrollNewStarter with the fields NewStarterForm needs.
                                S3FILLGUARD: the button is shown ONLY while Section 3 is still open.
                                Follows the guard EmployeeProfile.js already uses for the leaver form
                                (`{!leaverForm?.section3_completed && <button .../>}`) plus its own
                                `✓ Done` indicator, rather than inventing a new affordance.
                                The test is deliberately `=== true`, not truthiness: the per-row form
                                fetch swallows failures with an empty catch, so `ns` can be undefined
                                and `section3_completed` unknown. Unknown must render the BUTTON, never
                                the completed indicator, which would claim work is done that may not be.
                                Note this is the SECTION 3 flag alone; the Status pill and the progress
                                count beside it deliberately read all four sections and are unchanged. */}
                            {ns?.section3_completed === true ? (
                              <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '10px', padding: '2px 8px', backgroundColor: '#eaf3ee', color: '#2D4A3E', whiteSpace: 'nowrap' }}>
                                ✓ Payroll complete
                              </span>
                            ) : (
                              <button
                                style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${GOLD}`, color: GOLD, background: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                                onClick={() => setPayrollNewStarter({
                                  employee_id: emp.id,
                                  first_name: emp.first_name,
                                  last_name: emp.last_name,
                                  employee_type: emp.employee_type,
                                  primary_entity: emp.primary_entity,
                                })}
                              >
                                Fill Payroll &amp; Benefits
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Compliance Officer sidebar icons ─────────────────────────────────────────
// UXNAVICONS: the 6 forked Cv* icon components were removed and replaced with the shared icons.js
// set (imported at the top of this file). They were byte-identical glyphs at 15px/stroke 1.8;
// icons.js's Svg wrapper renders them at the approved 16px/stroke 1.75 and adds aria-hidden, so
// the Compliance and Finance/Payroll nav now match the COO Dashboard's icons exactly.

// ── Compliance Officer Dashboard ──────────────────────────────────────────────
export function ComplianceView({ email, onLogout }) {
  const { isDarkMode } = useDarkMode();
  // RESPONSIVE Phase 1: sidebar narrows below ~900px instead of crushing content (see FinanceView).
  const isNarrow = useIsNarrow();
  const cvNav = useNavigate();
  const cvLoc = useLocation();
  // COMPSECTION: this view had no translator of its own. The compliance onboarding task is stored
  // with one English title and translated for display, so the label follows the language of the
  // officer reading it. Named `cvT` rather than `t` deliberately: the Tasks table below names its
  // ROW argument `t`, and a translator called `t` would be shadowed inside that callback.
  const { t: cvT } = useTranslation();
  const CV_URL_TO_SECTION = { '/compliance': 'overview', '/compliance/profile': 'myprofile', '/compliance/tasks': 'tasks', '/compliance/team': 'team-compliance', '/compliance/report': 'compliance-report', '/compliance/policies': 'policy-repository', '/compliance/retention': 'retention', '/compliance/workflows': 'workflows', '/compliance/validation': 'validation', '/compliance/attestations': 'attestations' };
  const CV_SECTION_TO_URL = { 'overview': '/compliance', 'myprofile': '/compliance/profile', 'tasks': '/compliance/tasks', 'team-compliance': '/compliance/team', 'compliance-report': '/compliance/report', 'policy-repository': '/compliance/policies', 'retention': '/compliance/retention', 'workflows': '/compliance/workflows', 'validation': '/compliance/validation', 'attestations': '/compliance/attestations' };
  // COMPCARDLAYOUT: the open employee card is derived from the URL, not from local state.
  //
  // WHY THIS CHANGED. It used to be `useState(null)` consumed by an early `return` ABOVE this
  // component's own layout, which is precisely why the card escaped the shell: returning before
  // the topbar/sidebar JSX meant none of it rendered, so the card painted as a standalone page.
  // Local state also could not survive a refresh and gave the card no address to link to.
  //
  // This is the SAME mechanism the COO shell already uses for its own card
  // (`useMatch('/dashboard/employees/:employeeId')` in Dashboard.js, with the card rendered as a
  // branch INSIDE the content area). Reusing it rather than inventing a second pattern means a
  // refresh, a direct URL and the browser Back button all behave on the compliance side exactly
  // as they already do on the COO side.
  const cvCardMatch = useMatch('/compliance/employees/:employeeId');
  // ATTESTVIEW: the open attestation is route-derived too, for the same reasons the
  // employee card is - it survives a refresh, it can be linked to, and the browser Back
  // button agrees with the breadcrumb.
  const cvAttestMatch = useMatch('/compliance/attestations/:attestationId');
  const cardEmployeeId = cvCardMatch?.params?.employeeId || null;
  const openAttestationId = cvAttestMatch?.params?.attestationId || null;
  // setCardEmployee keeps its old call signature (it is handed an employee OBJECT by the list row)
  // so every call site reads the same, but it now navigates instead of setting state. Passing
  // null returns to the Employees list.
  const setCardEmployee = (emp) => cvNav(emp ? `/compliance/employees/${emp.id}` : '/compliance');
  // COMPCARDLAYOUT: the card route lives UNDER Oversight, so the sidebar keeps Oversight
  // highlighted while a card is open - the same resolution the COO shell applies for its
  // own card route (`empProfileMatch ? 'hr-overview' : ...`).
  const activeSection = cvCardMatch ? 'overview'
    : cvAttestMatch ? 'attestations'
    : (CV_URL_TO_SECTION[cvLoc.pathname] || 'overview');
  const setActiveSection = (section) => cvNav(CV_SECTION_TO_URL[section] || '/compliance');
  const [employees,          setEmployees]          = useState([]);
  const [newStarterForms,    setNewStarterForms]    = useState({});
  const [leaverForms,        setLeaverForms]        = useState({});
  const [loading,            setLoading]            = useState(true);
  // CVLOADFAILSAFE: distinct from `loading`. A failed load left the employee list, the compliance
  // report summary, and both onboarding/leaver worklists all at their empty defaults - a single
  // failure across the bundled Promise.all silently zeroed the whole compliance dashboard, with
  // "No active employees" / "No overdue tasks" reading as if nothing needed attention.
  const [loadFailed,         setLoadFailed]         = useState(false);
  const [selectedEmployee,   setSelectedEmployee]   = useState(null);
  // COMPCARD: the Employees list filters, mirroring the COO list's own set (status,
  // Visa Sponsored, entity, team, name search). Local to this view: the COO list keeps its
  // own state in Dashboard.js, so neither can affect the other.
  // ATTESTVIEW: the employee name for the attestation breadcrumb's leaf. Set when a row is
  // clicked; null on a direct URL open, where the breadcrumb shows a placeholder until the
  // detail screen has loaded its own data.
  const [cvOpenAttestationName, setCvOpenAttestationName] = useState(null);
  // TASKCLICK: where each compliance task type is actually DONE.
  //
  // INVESTIGATED PER TYPE rather than assumed, because the task types that really land in this
  // table are not all what the declared _COMPLIANCE_TASK_TYPES list suggests:
  //
  //   upload_background_check  -> the employee's COMPLIANCE CARD, which already has the
  //                               background-check upload/view/archive panel, entity-scoped and
  //                               type-scoped to exactly this document. This is the one upload
  //                               task compliance both owns and can complete from its own screen.
  //   review_background_check  -> DECLARED compliance-owned, but the code assigns it to the COO
  //                               (employees.py, assigned_to=_coo.employee_id). It therefore does
  //                               not appear in this table in practice, and no destination is
  //                               invented for it.
  //   right_to_work_recheck    -> likewise DECLARED here but assigned to the COO
  //                               (documents.py, from the 28-day visa-expiry trigger).
  //   upload_cv / upload_contract / upload_passport / upload_visa_stamp /
  //   upload_approval_letter   -> MY PROFILE (OWNTASKDEST). These are the officer's OWN
  //                               onboarding tasks: _add_upload_tasks sets assigned_to=
  //                               employee_id, so the subject and the assignee are the same
  //                               person. They are self-service uploads on her own record.
  //
  //                               SUPERSEDES TASKCLICK's original decision to leave these rows
  //                               non-interactive. That was the right call while no destination
  //                               existed, but My Profile IS the destination: it mounts
  //                               EmployeeView on her own record, which is where every other
  //                               employee completes exactly these five tasks. Leaving the row
  //                               dead made her look up her own profile by hand to do something
  //                               the row could take her to.
  //
  //                               THE EMPLOYEE VIEW'S OWN BEHAVIOUR, matched deliberately:
  //                               EmployeeView.handleTaskClick maps an upload task to a document
  //                               type (UPLOAD_TASK_TO_DOC_TYPE) and opens the upload modal on
  //                               the employee's own profile. Four of these five are in that map
  //                               (cv, contract, passport, visa_stamp); upload_approval_letter is
  //                               NOT, and there it falls through to simply opening the profile.
  //                               So navigating to My Profile reaches the same screen the
  //                               employee shell reaches, for all five, and the modal behaviour
  //                               that already exists there is inherited rather than duplicated.
  //   holiday_approval         -> Validation, unchanged (the one destination that already worked).
  //
  // A row with no destination keeps its current non-interactive display. That is deliberate: a
  // cursor and a click that led nowhere useful would be worse than no click at all.
  // OWNTASKDEST: the five task types an account owes as an EMPLOYEE rather than as the
  // compliance officer. Declared once, so the destination table above and the code below cannot
  // disagree about which types these are.
  const CV_OWN_ONBOARDING_TASK_TYPES = [
    'upload_cv', 'upload_contract', 'upload_passport', 'upload_visa_stamp',
    'upload_approval_letter',
  ];
  const cvTaskDestination = (task) => {
    if (!task) return null;
    if (task.task_type === 'holiday_approval') return () => setActiveSection('validation');
    if (task.task_type === 'upload_background_check' && task.employee_id) {
      return () => cvNav(`/compliance/employees/${task.employee_id}`);
    }
    // OWNTASKDEST: her own onboarding tasks open MY PROFILE, where she completes them, exactly as
    // any other employee completes the same five from their own profile. openMyProfile is used
    // rather than setActiveSection, so the id resolution the sidebar item already performs is
    // reused and this cannot drift from it. It is referenced inside a returned closure, which
    // runs on click and therefore after every const in this component is initialised.
    if (CV_OWN_ONBOARDING_TASK_TYPES.includes(task.task_type)) {
      return () => openMyProfile();
    }
    // DECLRENAME: an attestation row in THIS table opens the sidebar Attestations view, which is now
    // the only place this screen's attestations live for compliance staff (the duplicate tab on the
    // Declarations screen is gone). dashboard.py emits these rows with a synthetic
    // "attestation_<type>" task_type, so the prefix is the match, not a fixed list - a new
    // attestation type then needs no change here.
    //
    // HER OWN ATTESTATIONS ARE NOT THIS ROW AND DO NOT COME HERE. Those are completed from My
    // Profile, where ATTESTSELF's checkbox-confirm card still renders for her exactly as for every
    // other employee, and the two paths stay separate: this table is the compliance worklist about
    // OTHER people, My Profile is her own record. Nothing about the self path is touched.
    if (typeof task.task_type === 'string' && task.task_type.startsWith('attestation_')) {
      return () => setActiveSection('attestations');
    }
    return null;
  };
  const [cvEmpSearch,        setCvEmpSearch]        = useState('');
  const [cvEmpStatus,        setCvEmpStatus]        = useState('active');
  const [cvVisaFilter,       setCvVisaFilter]       = useState(false);
  const [cvEntityFilter,     setCvEntityFilter]     = useState('');
  const [cvTeamFilter,       setCvTeamFilter]       = useState('');
  const [selectedLeaver,     setSelectedLeaver]     = useState(null);
  const [myProfileEmployeeId, setMyProfileEmployeeId] = useState(null);
  const [myProfileError,      setMyProfileError]      = useState(null);
  const [myProfileBackHov,    setMyProfileBackHov]    = useState(false);
  const [summary,            setSummary]            = useState(null);
  const [outstandingTasks,   setOutstandingTasks]   = useState([]);
  // OPSLAYOUT: the Overdue/Upcoming tab, defaulting to OVERDUE exactly as the COO view's own
  // opsTasksTab does. Component state, not a URL parameter: it is a preference within an
  // already-routed section, which is how the COO view treats the same choice.
  const [cvTasksTab, setCvTasksTab] = useState('overdue');
  const [attestationsByType, setAttestationsByType] = useState([]);
  // FPFIX: only the setters are kept. Validation is always in this shell's menu now, so the two
  // values are no longer read; /employees/me still sets them as before.
  const [,                   setIsManager]          = useState(false);
  const [,                   setIsApprover]         = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    Promise.all([
      axios.get(`${API}/employees/`),
      axios.get(`${API}/compliance/report/summary`),
      axios.get(`${API}/compliance/report/outstanding-tasks`),
      axios.get(`${API}/compliance/report/attestations-by-type`),
    ])
      .then(async ([empRes, sumRes, tasksRes, attsRes]) => {
        const allEmps = empRes.data;
        setEmployees(allEmps);
        setSummary(sumRes.data);
        setOutstandingTasks(tasksRes.data);
        setAttestationsByType(attsRes.data);
        const nsMap = {};
        const lvMap = {};
        await Promise.all(allEmps.map(emp => Promise.all([
          axios.get(`${API}/new-starter/${emp.id}`).then(r => { nsMap[emp.id] = r.data; }).catch(() => {}),
          axios.get(`${API}/leaver/${emp.id}`).then(r => { lvMap[emp.id] = r.data; }).catch(() => {}),
        ])));
        setNewStarterForms(nsMap);
        setLeaverForms(lvMap);
        setLoading(false);
      })
      .catch(() => { setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // "Validation" nav item gating - is_manager is a derived capability (GET /employees/me), not a
  // role, so it needs its own small fetch independent of the bulk employees list above. is_approver
  // (same endpoint) covers the COO fallback-approver case: an employee with no manager_id has the
  // COO as their resolved approver, so the COO has a genuine pending approval here even though
  // is_manager alone is False for them.
  useEffect(() => {
    axios.get(`${API}/employees/me`)
      .then(res => {
        const nowIsManager = !!res.data.is_manager;
        const nowIsApprover = !!res.data.is_approver;
        setIsManager(nowIsManager);
        setIsApprover(nowIsApprover);
        // UX audit item 10 (§2.3): pending-approval count badge - /employees/me returns only
        // booleans, never a count, so the count comes from the existing pending-approvals
        // endpoint, fetched only for users who actually qualify.
        // LEAVECANCEL: the badge counts BOTH kinds of decision the Validation page now shows -
        // pending holiday approvals AND pending leave cancellation requests. Counting only the
        // former would leave a manager whose sole outstanding item is a cancellation looking at an
        // unbadged Validation item, which reads as "nothing to do". allSettled so one failing
        // endpoint degrades that half to zero rather than losing the whole count.
        if (nowIsManager || nowIsApprover) {
          Promise.allSettled([
            axios.get(`${API}/leave/pending-approvals`),
            axios.get(`${API}/leave/pending-cancellations`),
          ]).then(rs => setPendingApprovalCount(
            rs.reduce((n, r) => n + (r.status === 'fulfilled' ? (r.value.data || []).length : 0), 0),
          ));
        }
        // Honors an emailed deep link's ?next=validation (see postLoginNext.js) once manager
        // status is actually known - a non-manager/non-approver's pending "next" is simply dropped.
        // FPFIX: PEEK first. The old order consumed the signal for a manager or approver before the
        // attestations / F&P branch could read it, so those links were dropped for them; and the My
        // Profile section was opened without its employee id, so it stayed on "Loading...".
        const _next = peekPostLoginNext();
        // ATTESTDEEPLINK: an attestation email's ?next=attestations targets the personal My Profile
        // view (EmployeeView), which loads and can submit the viewer's own attestations - so open
        // My Profile and let EmployeeView consume the signal and land on its Attestations tab. PEEK
        // (not consume) so EmployeeView still sees it.
        if (['attestations', 'fp'].includes(_next)) {
          if (res.data && res.data.id) setMyProfileEmployeeId(res.data.id);
          setActiveSection('myprofile');
        }
        // FPFIX: compliance always has Validation (its F&P steps), so the link is always honoured.
        else if (_next === 'validation') { consumePostLoginNext(); setActiveSection('validation'); }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const openMyProfile = () => {
    setActiveSection('myprofile');
    if (myProfileEmployeeId) return;
    setMyProfileError(null);
    // Resolve the logged-in compliance user's own record via the self endpoint,
    // not the team-wide employees list, so this doesn't depend on list-level permissions.
    axios.get(`${API}/employees/me`)
      .then(res => { if (res.data) setMyProfileEmployeeId(res.data.id); else setMyProfileError('Unable to load your profile. Please try again.'); })
      .catch(() => setMyProfileError('Unable to load your profile. Please try again.'));
  };
  // COMPCARDLAYOUT: the early return that used to live here has been REMOVED. It rendered the
  // card above this component's own topbar and sidebar, which is what made the card a standalone
  // page. The card is now a branch inside the MAIN CONTENT area further down, so the shell stays
  // on screen. The Section 4 form below keeps its own early return deliberately: a full-page form
  // is the existing behaviour for every role and this task does not change it.
  if (selectedEmployee) return (
    <>
      <FormTopBar subtitle="Compliance" onLogout={onLogout} role="compliance" onLogoClick={() => setSelectedEmployee(null)} />
      <NewStarterForm
        employeeId={selectedEmployee.id}
        employeeName={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
        employeeEntity={selectedEmployee.primary_entity}
        section={4}
        completedBy={email}
        currentUserRole="compliance"
        onBack={() => setSelectedEmployee(null)}
        onComplete={() => { setSelectedEmployee(null); load(); }}
      />
    </>
  );
  if (selectedLeaver) return (
    <>
      <FormTopBar subtitle="Compliance" onLogout={onLogout} role="compliance" onLogoClick={() => setSelectedLeaver(null)} />
      <LeaverForm
        employeeId={selectedLeaver.id}
        employeeName={`${selectedLeaver.first_name} ${selectedLeaver.last_name}`}
        requiresVisa={selectedLeaver.requires_visa}
        section={4}
        completedBy={email}
        /* SASCOPE: compliance no longer fills the leaver IT section (the server refuses it). */
        allowedSections={[1, 3]}
        onBack={() => setSelectedLeaver(null)}
        onComplete={() => { setSelectedLeaver(null); load(); }}
      />
    </>
  );

  // My Profile: full-page replacement (own topbar + sidebar via EmployeeView) - mirrors
  // CooProfileView in Dashboard.js. The compliance navbar/sidebar are never rendered here.
  if (activeSection === 'myprofile') {
    const backBar = (
      <div style={{ padding: '10px 20px', background: isDarkMode ? '#0a1020' : '#f8f6f3', borderBottom: '0.5px solid rgba(26,39,68,0.1)' }}>
        <button
          onClick={() => setActiveSection('overview')}
          onMouseEnter={() => setMyProfileBackHov(true)}
          onMouseLeave={() => setMyProfileBackHov(false)}
          style={{
            fontSize: '12px', color: myProfileBackHov ? GOLD : NAVY_SHELL,
            border: `0.5px solid ${myProfileBackHov ? GOLD : 'rgba(26,39,68,0.25)'}`,
            borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
            background: 'none', transition: 'color 0.15s, border-color 0.15s',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        >
          ← Back to oversight
        </button>
      </div>
    );

    if (!myProfileEmployeeId) {
      return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
          {backBar}
          {myProfileError ? (
            <div style={{ padding: '24px', color: '#aaa', fontSize: '14px' }}>{myProfileError}</div>
          ) : (
            <p style={{ padding: '24px', color: '#aaa', fontStyle: 'italic', fontSize: '13px' }}>Loading...</p>
          )}
        </div>
      );
    }

    return (
      <>
        {backBar}
        <EmployeeView
          employeeId={myProfileEmployeeId}
          currentUser={email}
          userRole="compliance"
          onBack={() => setActiveSection('overview')}
          onLogoClick={(resetToOverview) => resetToOverview()}
        />
      </>
    );
  }

  const C = { overdue: '#A63A3A', pending: GOLD, done: '#2D4A3E', navy: NAVY_SHELL };
  const activeEmps  = employees.filter(e => e.is_active);
  const s4k         = 'section4_completed';
  // S4CLASSSPLIT: Compliance's genuine Section 4 job differs by employee kind.
  //  - MIGRATED employees: the background check is owned by the COO's upload (auto-validated by
  //    MIGRAUTOVAL, which sets section4_completed on its own). Compliance still owes the SMCR
  //    classification, so "done" for the worklist is classification_completed_at being set - NOT
  //    section4_completed, which would make them vanish the moment the COO uploaded, before
  //    classification. A migrated employee stays pending until classification is recorded, then
  //    disappears.
  //  - Genuine NEW STARTERS: unchanged - Compliance owns both classification and background check,
  //    and section4_completed (set only by a full Section 4 submit) is the right signal.
  // S4AB: for a new starter, compliance's part of Section 4 is 4A (HR's upload is 4B).
  const s4Done = e => (e.migration_confirmed
    ? !!newStarterForms[e.id]?.classification_completed_at
    : s4aDone(newStarterForms[e.id]));
  const nsPending   = activeEmps.filter(e => !s4Done(e));
  const nsDone      = activeEmps.filter(e =>  s4Done(e));
  // COMPCARD: the filtered Employees list. Same semantics as the COO list's own derivation:
  // status first, then visa, then entity, then team, then the name search. "active" means
  // is_active !== false and "former" its complement, which is exactly how the COO list splits
  // them, so an identical filter choice yields an identical set on both sides.
  const cvEntityOptions = Array.from(
    new Set(employees.map(e => e.primary_entity).filter(Boolean))).sort();
  const cvTeamOptions = Array.from(
    new Set(employees.map(e => e.department).filter(Boolean))).sort();
  const cvDisplayedEmployees = (() => {
    let list = employees;
    if (cvEmpStatus === 'active') list = list.filter(e => e.is_active !== false);
    else if (cvEmpStatus === 'former') list = list.filter(e => e.is_active === false);
    if (cvVisaFilter) list = list.filter(e => e.requires_visa);
    if (cvEntityFilter) list = list.filter(e => e.primary_entity === cvEntityFilter);
    if (cvTeamFilter) list = list.filter(e => e.department === cvTeamFilter);
    const q = cvEmpSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(e =>
        `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase().includes(q)
        || (e.company_email || '').toLowerCase().includes(q));
    }
    return list;
  })();

  const leaverPending = employees.filter(e => leaverForms[e.id]?.section1_completed && !leaverForms[e.id]?.[s4k]);
  const leaverDone    = employees.filter(e => leaverForms[e.id]?.section1_completed &&  leaverForms[e.id]?.[s4k]);

  // COMPSTATUS: the badge is now SERVER-COMPUTED and this function only reads it.
  //
  // WHAT WAS WRONG. It used to be derived here from s4Done plus njcStatusMap, with `|| 'Pending'`
  // for everyone else. njcStatusMap was built from ONE attestation type, so every employee outside
  // that type hit the fallback: 305 of 308 active employees read "Pending" while the compliance
  // Tasks list showed 7. The two screens shared no definition - this one read form-completion flags,
  // the Tasks list read task assignment.
  //
  // NOW: GET /employees/ returns compliance_status, computed in compliance.compliance_status_map
  // from the SAME _role_scoped_pending_tasks call the Tasks list uses, with the entity scope the
  // declaration lists already apply. Pending plus Overdue in this list therefore equals the distinct
  // employees in the Tasks list, by construction rather than by coincidence.
  //
  // njcStatusMap IS GONE, and with it the fallback. attestationsByType is untouched and still feeds
  // pendingByType below, which is a different question (which attestations are outstanding, by type)
  // and keeps its own derivation.
  //
  // THE LOCAL FALLBACK BELOW IS NOT THE OLD DEFAULT. It only covers a row that arrived without the
  // field at all - an older cached response, or a role whose branch does not populate it - and it
  // falls back to the kind-aware s4Done rather than to a blanket "Pending", so a stale payload
  // degrades to the previous Complete/not-Complete split instead of mislabelling everyone.
  const empBadgeStatus = emp => {
    if (emp.compliance_status) return emp.compliance_status;
    return s4Done(emp) ? 'Complete' : 'Not started';
  };

  // COMPSTATUS: display text for a badge. The canonical English value stays the one the style and
  // every comparison use; only the rendered text is localised. An unrecognised value is returned
  // unchanged, so a label this map has not been taught about still reads sensibly rather than
  // rendering a raw key.
  const CV_STATUS_KEY = {
    'Overdue': 'overdue', 'Pending': 'pending', 'Complete': 'complete',
    'Awaiting COO': 'awaitingCoo', 'Not started': 'notStarted',
  };
  const cvStatusLabel = status => {
    const key = CV_STATUS_KEY[status];
    return key ? cvT(`compliance.oversightStatus.${key}`, status) : status;
  };

  const badgeStyle = status => {
    const base = { borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' };
    if (status === 'Complete') return { ...base, backgroundColor: '#eaf3ee', color: C.done };
    if (status === 'Overdue')  return { ...base, backgroundColor: '#fdf5f5', color: C.overdue };
    // COMPSTATUS: the two new labels are NEUTRAL GREY, deliberately not green and not amber.
    // Neither is an achievement and neither is compliance's own work: "Awaiting COO" says somebody
    // else owns the next step, "Not started" says nobody has begun. Green would read as done and
    // amber as outstanding-for-compliance, and both would reintroduce in colour the confusion this
    // build removes from the label. Amber below stays reserved for Pending, which IS compliance's
    // own outstanding work.
    if (status === 'Awaiting COO' || status === 'Not started') {
      return { ...base, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : '#f1f1ef',
               color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#5c5c57' };
    }
    return                            { ...base, backgroundColor: '#fdf8ee', color: '#7a4d00' };
  };

  // NOGREENLIST (confirmed decision): the EMPLOYEES list badge, with NO green anywhere.
  //
  // WHY A SECOND FUNCTION RATHER THAN EDITING badgeStyle. badgeStyle is SHARED with two other
  // sections of this same screen - "Leavers: Compliance", which renders a literal
  // badgeStyle('Complete'), and "Pending attestations by type", which passes its own per-employee
  // status. The brief is scoped to the Employees list, so changing the shared function would have
  // silently restyled both of those. This wraps it instead: the base geometry, the Overdue red and
  // the Pending amber are inherited unchanged, and only the Complete case is overridden.
  //
  // THE FIVE LABELS STAY DISTINGUISHABLE, which is the constraint that rules out simply making
  // everything grey:
  //   Overdue       red on pale red      (inherited, unchanged)
  //   Pending       amber on pale cream  (inherited, unchanged - compliance's own outstanding work)
  //   Complete      NAVY on pale navy    (was green on pale green)
  //   Awaiting COO  grey on neutral grey (inherited from COMPSTATUS, unchanged)
  //   Not started   grey on neutral grey (inherited, same as Awaiting COO - both mean
  //                 "not compliance's move", and the TEXT is what separates them)
  //
  // Navy for Complete is the portal's own primary colour, already used for the avatars and the COO
  // Operational Tasks active pill (#1a2744), so it reads as a finished state without claiming the
  // green that this list must not use. The status is given by the badge TEXT; colour only groups.
  const listBadgeStyle = status => {
    const inherited = badgeStyle(status);
    if (status !== 'Complete') return inherited;
    return {
      ...inherited,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.10)' : '#eef1f6',
      color: isDarkMode ? '#c7d2e4' : C.navy,
    };
  };

  const pendingByType = attestationsByType
    .map(g => ({ ...g, employees: g.employees.filter(e => e.status !== 'Complete') }))
    .filter(g => g.employees.length > 0);

  const overdueTasks = outstandingTasks.filter(t => t.is_overdue);

  const pendingDaysMap = {};
  outstandingTasks.filter(t => !t.is_overdue).forEach(t => { pendingDaysMap[t.name] = t.days; });

  const cv = {
    card:      { backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', borderRadius: '10px', padding: '20px', boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)', border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}` },
    cardTitle: { fontSize: '14px', fontWeight: 600, color: isDarkMode ? TEXT_DARK_MODE : C.navy, margin: '0 0 14px 0' },
    empRow:    { display: 'flex', alignItems: 'center', padding: '10px 12px', marginBottom: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}`, borderRadius: '8px', backgroundColor: isDarkMode ? NAVY_SHELL : 'transparent' },
    avatar:    (bg) => ({ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, flexShrink: 0, marginRight: '12px' }),
  };

  const myRecord      = employees.find(e => e.company_email.toLowerCase() === email.toLowerCase());
  const displayName   = myRecord ? `${myRecord.first_name} ${myRecord.last_name}` : email;
  const avatarInitial = myRecord ? myRecord.first_name.charAt(0) : email.charAt(0).toUpperCase();

  const cvNavSections = [
    { label: 'MAIN', items: [
      { id: 'overview',          label: 'Oversight',          icon: <IconDashboard /> },
      // TASKBADGEOVERDUE (confirmed decision): the badge counts OVERDUE tasks only, not the whole
      // list. It read outstandingTasks.length, so it showed 7 where 2 were overdue and 5 were merely
      // upcoming - a number that said "how much is on your list", when what a badge is for is saying
      // what is URGENT.
      //
      // DERIVED FROM overdueTasks, which is the SAME `is_overdue` filter the Overdue TAB renders
      // from, so the badge and that tab count cannot drift apart. Deliberately not re-derived from
      // days or due_date here: _is_overdue on the server is the one rule (a null due_date counts as
      // overdue), and copying it into the client is how the two would eventually disagree.
      //
      // `|| null` is the EXISTING hide-when-zero convention of this nav: the shared renderBadge
      // draws nothing for a null badge, so no overdue work shows no badge rather than a "0".
      { id: 'tasks',             label: 'Tasks',             icon: <IconCheck />,   badge: overdueTasks.length || null },
      { id: 'myprofile',         label: 'My Profile',        icon: <IconUser />,    action: openMyProfile },
      // "Validation" - derived capability (is_manager, from GET /employees/me), not a role -
      // shown to any account that is someone's line manager. Plain item at the bottom of MAIN,
      // not a separate visual group, per the confirmed placement.
      // FPFIX: always present for compliance, manager or not: its F&P outcome and follow-up steps
      // are on the Validation page. The badge still counts only its own pending leave decisions.
      { id: 'validation', label: 'Validation', icon: <IconCheck />, badge: pendingApprovalCount || null },
    ]},
    { label: 'COMPLIANCE', items: [
      // DECLRENAME: "Team Compliance" renamed "Declarations" / "Declarations" in French. NAVIGATION
      // LABEL AND SCREEN TITLE ONLY: the item id, its route (/compliance/team), the component it
      // opens and its read scope are all unchanged, so no link breaks and nothing needs redirecting.
      // This sidebar belongs to ComplianceView, which Route gates allowedRole="compliance", so the
      // rename cannot reach the COO, admin, HR or employee navigation.
      { id: 'team-compliance',   label: cvT('compliance.declarationsTitle'),   icon: <IconShield /> },
      // ATTESTVIEW: the Attestations view (Submitted / Pending), compliance only.
      { id: 'attestations',      label: 'Attestations',      icon: <IconCheck /> },
      { id: 'compliance-report', label: 'Compliance Report', icon: <IconChart /> },
      { id: 'policy-repository', label: 'Policy Repository', icon: <IconFiles /> },
      { id: 'retention',         label: 'Retention Review',  icon: <IconShield /> },
      // WFNAVLABEL (confirmed COO decision): item label renamed "Compliance workflows" (was the
      // bare "Workflows") - navigation label only, route/component/id/read-scope unchanged.
      { id: 'workflows',         label: 'Compliance workflows', icon: <IconFiles /> },
    ]},
  ];

  const cvNavStyle = {
    // REDESIGN Stage 2: the shared sidebar chrome from theme.js, identical to the COO
    // Dashboard. Solid navy active fill; gold is reserved for the active item's icon.
    ...shellNav(isDarkMode),
  };

  return (
    <div style={shellSurface(isDarkMode).page}>

      {/* ── TOPBAR ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: C.navy, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => cvNav(ROLE_DEFAULTS['compliance'] || '/compliance')} title="Go to dashboard">
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' }}>Σ</span>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>Enko Portal</span>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}> · Compliance</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DarkModeToggle />
          <NavBtn onClick={onLogout}>Sign out</NavBtn>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* ── SIDEBAR ── (RESPONSIVE Phase 3: shared <Sidebar>; behaviour byte-identical - same
            profile block, divider, cvNavSections, cvNavStyle tokens, inline section label + gold
            badge, same onClick, same responsive width.) */}
        <Sidebar
          width={isNarrow ? '180px' : '200px'}
          containerStyle={{ backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', borderRight: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`, minHeight: 'calc(100vh - 53px)', paddingTop: '8px' }}
          profile={(
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#F5F0E8', border: '1.5px solid #E8DFC8', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', flexShrink: 0 }}>
                {avatarInitial}
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{displayName}</p>
                <p style={{ fontSize: '11px', color: GOLD, margin: '2px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>Compliance Officer</p>
              </div>
            </div>
          )}
          dividerAfterProfile={<div style={{ height: '0.5px', backgroundColor: isDarkMode ? 'rgba(201,168,76,0.15)' : '#E8E4DC', margin: '8px 12px' }} />}
          sections={cvNavSections}
          activeSection={activeSection}
          onSelect={(item) => { if (item.action) item.action(); else setActiveSection(item.id); }}
          itemStyle={cvNavStyle.item}
          itemActiveStyle={cvNavStyle.itemActive}
          sectionLabelStyle={{ fontSize: '10px', color: isDarkMode ? TEXT_MUTED : '#667085', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '12px 16px 4px', fontWeight: 600 }}
          renderBadge={(item) => item.badge ? (
            <span style={{ backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 600, flexShrink: 0 }}>{item.badge}</span>
          ) : null}
        />

        {/* ── MAIN CONTENT ── */}
        <div style={{ flex: 1, padding: '20px 24px', minWidth: 0 }}>

          {/* COMPCARDLAYOUT: THE EMPLOYEE CARD, rendered INSIDE the content area.
              This is the structural fix. The card used to be returned above the topbar and
              sidebar, so it painted as a standalone page with only a Back button. Rendering it
              here - to the right of the sidebar, under the topbar, with the breadcrumb above it -
              is the same arrangement the COO shell uses for its own card.
              The breadcrumb follows the COO pattern exactly (clickable "Employees", the same
              single-angle separator, the employee name in navy): clicking Employees navigates
              back to the Oversight list rather than clearing a piece of state, so the browser
              Back button agrees with it. The filters are NOT reset on the way back - they live in
              this component's own state, which is untouched by the navigation, so returning to
              the list shows the same filtered set the user left. */}
          {/* ATTESTVIEW: one attestation, opened by clicking a name in the Attestations view.
              Rendered INSIDE the content area with a breadcrumb, following the employee card
              pattern exactly: the shell keeps its topbar and sidebar, and the breadcrumb owns
              going back by NAVIGATING, so the browser Back button agrees with it. */}
          {openAttestationId ? (
            <>
              <div style={{ marginBottom: '16px' }}>
                <span
                  style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer' }}
                  onMouseOver={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseOut={e => { e.currentTarget.style.color = '#9ba8c0'; }}
                  onClick={() => cvNav('/compliance/attestations')}
                >
                  Attestations
                </span>
                <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>&rsaquo;</span>
                <span style={{ fontSize: '13px', color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, fontWeight: 500 }}>
                  {cvOpenAttestationName || 'Attestation'}
                </span>
              </div>
              <ComplianceAttestationDetail attestationId={openAttestationId} />
            </>
          ) : cardEmployeeId ? (
            <>
              <div style={{ marginBottom: '16px' }}>
                <span
                  style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer' }}
                  onMouseOver={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseOut={e => { e.currentTarget.style.color = '#9ba8c0'; }}
                  onClick={() => cvNav('/compliance')}
                >
                  Employees
                </span>
                <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                <span style={{ fontSize: '13px', color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, fontWeight: 500 }}>
                  {(() => {
                    const emp = employees.find(x => x.id === cardEmployeeId);
                    return emp ? `${emp.first_name} ${emp.last_name}` : '…';
                  })()}
                </span>
              </div>
              <ComplianceEmployeeCard
                employeeId={cardEmployeeId}
                employee={employees.find(x => x.id === cardEmployeeId) || null}
                /* COMPSOD: the signed-in officer's OWN employee id, so the card can hide the
                   background-check upload and archive controls on their own record. The server
                   refuses those two actions regardless (compliance_may_act_on_document's
                   `mutating` flag); this only avoids offering a control that would 403. */
                viewerEmployeeId={myRecord ? myRecord.id : null}
                onFillCompliance={(emp) => setSelectedEmployee(emp)}
              />
            </>
          ) : (
          <>

          {/* OVERVIEW */}
          {activeSection === 'overview' && (
            <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: isDarkMode ? '#0f1a2e' : '#edecea' }}>
              {loading ? (
                <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: '14px', paddingBottom: '24px' }}>Loading...</p>
              ) : loadFailed ? (
                // CVLOADFAILSAFE: replaces the KPI cards and both employee/task lists below, so a
                // failed load can never render zeroed KPI values or "No active employees"/
                // "No overdue tasks" as if the compliance dashboard were genuinely quiet.
                <div style={{ paddingBottom: '24px' }}>
                  <LoadError message="Could not load the compliance overview." onRetry={load} />
                </div>
              ) : (
                <>
                  {/* ROW 1 - 4 KPI cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)'), gap: '14px', marginBottom: '20px' }}>
                    {[
                      { label: 'Total employees',      value: activeEmps.length,  accent: C.navy,    sub: 'active employees' },
                      { label: 'Attestations pending', value: summary?.pending,   accent: C.pending, sub: 'compliance tasks pending' },
                      { label: 'Attestations overdue', value: summary?.overdue,   accent: C.overdue, sub: 'compliance tasks overdue' },
                      { label: 'Completed this month', value: summary?.completed, accent: C.done,    sub: 'compliance forms submitted' },
                    ].map(k => (
                      <div key={k.label} style={{
                        backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', borderRadius: '10px', padding: '18px 20px',
                        boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                        borderTop: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`, borderRight: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`,
                        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`, borderLeft: `3px solid ${k.accent}`,
                      }}>
                        <p style={{ fontSize: '11px', color: isDarkMode ? TEXT_MUTED : '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0', fontWeight: 600 }}>{k.label}</p>
                        <p style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 4px 0', color: k.accent }}>{k.value ?? '-'}</p>
                        <p style={{ fontSize: '11px', color: isDarkMode ? TEXT_MUTED : '#aaa', margin: 0 }}>{k.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* ROW 2 - EMPLOYEES (COMPCARD).
                      The compliance role's own Employees list: every ACTIVE employee across every
                      entity by default, with the SAME filter set the COO list offers (status
                      active/all/former, Visa Sponsored, entity, team, name search). The filters
                      are applied client side over the already-loaded array, exactly as the COO
                      list does it, so both sides derive from one fetch and cannot disagree about
                      what a filter means.
                      ROWFILLGONE: the rows carry NO Fill Compliance button and no per-row Section 4
                      status. SUPERSEDES the earlier per-row rule (a button when Section 4 was not
                      submitted, who completed it when it was): the action and its status now live
                      only on the employee compliance card, reached by clicking a name. The status
                      badge still renders on every row.
                      THIS BLOCK IS INSIDE ComplianceView ONLY. The COO's own Oversight list lives
                      in Dashboard.js and is untouched by any of this. */}
                  <div style={{ ...cv.card, marginBottom: '20px' }}>
                    <h3 style={{ ...cv.cardTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Employees
                      {cvDisplayedEmployees.length > 0 && <span style={{ backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 600 }}>{cvDisplayedEmployees.length}</span>}
                    </h3>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="text" placeholder="Search by name..." aria-label="Search by name"
                        value={cvEmpSearch} onChange={e => setCvEmpSearch(e.target.value)}
                        style={{ padding: '7px 10px', fontSize: '12px', borderRadius: '6px',
                                 border: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
                                 backgroundColor: isDarkMode ? NAVY_SHELL : '#fff',
                                 color: isDarkMode ? TEXT_DARK_MODE : '#333', minWidth: '180px' }} />
                      <div style={{ display: 'flex' }}>
                        {['active', 'all', 'former'].map(st => (
                          <button key={st} onClick={() => setCvEmpStatus(st)}
                            style={{ padding: '7px 12px', fontSize: '12px', cursor: 'pointer',
                                     border: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
                                     backgroundColor: cvEmpStatus === st ? C.navy : (isDarkMode ? NAVY_SHELL : '#fff'),
                                     color: cvEmpStatus === st ? '#fff' : (isDarkMode ? TEXT_DARK_MODE : '#666') }}>
                            {st.charAt(0).toUpperCase() + st.slice(1)}
                          </button>
                        ))}
                        <button onClick={() => setCvVisaFilter(v => !v)}
                          style={{ padding: '7px 12px', fontSize: '12px', cursor: 'pointer',
                                   border: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
                                   backgroundColor: cvVisaFilter ? C.navy : (isDarkMode ? NAVY_SHELL : '#fff'),
                                   color: cvVisaFilter ? '#fff' : (isDarkMode ? TEXT_DARK_MODE : '#666') }}>
                          Visa Sponsored
                        </button>
                      </div>
                      <select aria-label="Filter by country / entity" value={cvEntityFilter}
                        onChange={e => setCvEntityFilter(e.target.value)}
                        style={{ padding: '7px 10px', fontSize: '12px', borderRadius: '6px',
                                 border: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
                                 backgroundColor: isDarkMode ? NAVY_SHELL : '#fff',
                                 color: isDarkMode ? TEXT_DARK_MODE : '#666', cursor: 'pointer' }}>
                        <option value="">All entities</option>
                        {cvEntityOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                      <select aria-label="Filter by team" value={cvTeamFilter}
                        onChange={e => setCvTeamFilter(e.target.value)}
                        style={{ padding: '7px 10px', fontSize: '12px', borderRadius: '6px',
                                 border: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
                                 backgroundColor: isDarkMode ? NAVY_SHELL : '#fff',
                                 color: isDarkMode ? TEXT_DARK_MODE : '#666', cursor: 'pointer' }}>
                        <option value="">All teams</option>
                        {cvTeamOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>

                    {cvDisplayedEmployees.length === 0
                      ? <p style={{ color: '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 }}>No employees match these filters</p>
                      : cvDisplayedEmployees.map(emp => {
                          const status = empBadgeStatus(emp);
                          // NOGREENLIST: EVERY avatar in this list is the same navy, whatever the
                          // status. It used to turn green (#27ae60) when Section 4 was complete,
                          // which ROWFILLGONE had kept as the last trace of a row status it had
                          // otherwise removed. The badge text already states the status, so the
                          // colour was a second, weaker telling of the same thing - and a green
                          // circle read as an achievement next to a badge that might say nothing of
                          // the kind. The s4Submitted read is gone with it: nothing else used it.
                          //
                          // SECTION 4 IS UNTOUCHED. This removes a colour, not a fact: no Section 4
                          // value, no status computation and no permission changes.
                          return (
                            <div key={emp.id} style={{ ...cv.empRow, opacity: emp.is_active === false ? 0.6 : 1 }}>
                              <div style={cv.avatar(C.navy)}>{emp.first_name.charAt(0)}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* COMPCARD: the name opens the compliance employee card. A real
                                    <button>, so it is keyboard reachable like every other control. */}
                                <button type="button" onClick={() => setCardEmployee(emp)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                           fontWeight: 600, fontSize: '13px', color: C.navy,
                                           textDecoration: 'underline', font: 'inherit' }}>
                                  {emp.first_name} {emp.last_name}
                                </button>
                                <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>{emp.job_title || '-'}</span>
                                {emp.requires_visa && (
                                  <span style={{ fontSize: '11px', color: '#7a4d00', backgroundColor: '#fdf8ee',
                                                 borderRadius: '10px', padding: '1px 7px', marginLeft: '8px',
                                                 fontWeight: 600 }}>Visa</span>
                                )}
                                {emp.is_active === false && (
                                  <span style={{ fontSize: '11px', color: C.overdue, backgroundColor: '#fdf5f5',
                                                 borderRadius: '10px', padding: '1px 7px', marginLeft: '8px',
                                                 fontWeight: 600 }}>Former</span>
                                )}
                              </div>
                              {/* COMPSTATUS: the STORED/SERVER label drives the style, and the
                                  TRANSLATED label is what is shown, so a French-reading officer sees
                                  French while the styling and every comparison keep working off the
                                  one canonical English value. */}
                              {/* NOGREENLIST: the list-specific badge, so Complete is navy here while
                                  the two OTHER sections that share badgeStyle keep their own
                                  styling untouched. */}
                              <span style={{ ...listBadgeStyle(status), marginRight: '12px' }}>{cvStatusLabel(status)}</span>
                              {/* ROWFILLGONE: NO Fill Compliance button and no per-row
                                  Section 4 status here. Fill Compliance is reachable ONLY
                                  from the employee compliance card, opened by clicking the
                                  name above, in its new starter onboarding section. The card
                                  shows either the button or who completed Section 4 and when,
                                  so the action and its status live in ONE place instead of
                                  two. The status badge to the left is unchanged. */}
                            </div>
                          );
                        })
                    }

                    {(leaverPending.length > 0 || leaverDone.length > 0) && (
                      <div style={{ borderTop: '0.5px solid #EEE', marginTop: '14px', paddingTop: '14px' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: C.overdue, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          Leavers: Compliance
                          {leaverPending.length > 0 && <span style={{ backgroundColor: C.overdue, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 600 }}>{leaverPending.length}</span>}
                        </p>
                        {leaverPending.map(emp => (
                          <div key={emp.id} style={cv.empRow}>
                            <div style={cv.avatar(C.overdue)}>{emp.first_name.charAt(0)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy }}>{emp.first_name} {emp.last_name}</span>
                            </div>
                            <button style={{ backgroundColor: C.overdue, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}
                              onClick={() => setSelectedLeaver(emp)}>
                              Fill Leaver Compliance
                            </button>
                          </div>
                        ))}
                        {leaverDone.map(emp => (
                          <div key={emp.id} style={{ ...cv.empRow, opacity: 0.6 }}>
                            <div style={cv.avatar('#27ae60')}>{emp.first_name.charAt(0)}</div>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy }}>{emp.first_name} {emp.last_name}</span>
                            </div>
                            <span style={badgeStyle('Complete')}>Complete</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ROW 3 - 2 columns */}
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '16px', alignItems: 'stretch' }}>

                    {/* LEFT - Pending attestations by type */}
                    <div style={cv.card}>
                      <h3 style={cv.cardTitle}>Pending attestations by type</h3>
                      {pendingByType.length === 0 ? (
                        <p style={{ color: '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 }}>All attestations up to date</p>
                      ) : pendingByType.map(group => (
                        <div key={group.type_name} style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: C.navy }}>{group.type_name}</span>
                            <span style={{ backgroundColor: '#fdf8ee', color: '#7a4d00', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 600 }}>{group.employees.length}</span>
                          </div>
                          {group.employees.map(emp => {
                            const isOverdue = emp.status === 'Overdue';
                            const days = pendingDaysMap[emp.name];
                            return (
                              <div key={emp.id} style={{
                                display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '4px',
                                borderRadius: '6px', borderLeft: `3px solid ${isOverdue ? C.overdue : C.pending}`,
                                backgroundColor: isOverdue ? '#fdf5f5' : '#f9f8f6',
                              }}>
                                <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: C.navy }}>{emp.name}</span>
                                {days != null
                                  ? <span style={{ fontSize: '12px', fontWeight: 600, color: isOverdue ? C.overdue : C.pending, flexShrink: 0 }}>{days}d</span>
                                  : <span style={{ ...badgeStyle(emp.status), marginLeft: '8px' }}>{emp.status}</span>
                                }
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {/* RIGHT - Overdue compliance tasks */}
                    <div style={cv.card}>
                      <h3 style={cv.cardTitle}>Overdue compliance tasks</h3>
                      {overdueTasks.length === 0 ? (
                        <p style={{ color: '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 }}>No overdue tasks</p>
                      ) : overdueTasks.map((task, i) => (
                        <div key={task.task_id || i} style={{
                          display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px',
                          borderRadius: '6px', borderLeft: `3px solid ${C.overdue}`, backgroundColor: '#fdf5f5',
                        }}>
                          <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy, flexShrink: 0, marginRight: '8px' }}>{task.name}</span>
                          {/* COMPSECTION: translated at render; any other task title passes through unchanged. */}
                          <span style={{ flex: 1, fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{complianceTaskTitle(task.description, cvT)}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, marginLeft: '8px', flexShrink: 0, color: C.overdue }}>{task.days}d</span>
                        </div>
                      ))}
                    </div>

                  </div>
                </>
              )}
            </div>
          )}

          {/* TEAM COMPLIANCE */}
          {activeSection === 'team-compliance' && (
            <ComplianceModule
              currentUser={email}
              isAdmin={true}
              isStaffView={true}
              userRole="compliance"
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* COMPLIANCE REPORT - CARDFILTER: onNavigate dropped, the summary cards now filter
              the report's own detail list in place (see ComplianceReport.js), so this mount's
              incompatible section-only callback (whose 'compliance'/'analytics' args were not CV
              sections and ejected the user to /compliance) is no longer reachable or needed. */}
          {activeSection === 'compliance-report' && <ComplianceReport />}

          {/* POLICY REPOSITORY */}
          {activeSection === 'policy-repository' && (
            <PolicyRepository
              currentUser={email}
              userRole="compliance"
              canManage={true}
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* RETENTION REVIEW */}
          {activeSection === 'retention' && (
            <RetentionReview
              currentUser={email}
              userRole="compliance"
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* WORKFLOWS */}
          {activeSection === 'workflows' && (
            <WorkflowList onBack={() => setActiveSection('overview')} currentUserRole="compliance" />
          )}

          {/* VALIDATION */}
          {activeSection === 'validation' && <ValidationView />}

          {/* ATTESTVIEW: the Attestations view. Clicking a name NAVIGATES to the detail route,
              so a refresh and a direct URL both land on the same screen. The clicked row's name
              is remembered only to render the breadcrumb leaf without a second fetch; the detail
              screen loads its own data from the server regardless. */}
          {activeSection === 'attestations' && (
            <ComplianceAttestations
              onOpenAttestation={(row) => {
                setCvOpenAttestationName(row?.employee_name || null);
                cvNav(`/compliance/attestations/${row.id}`);
              }}
            />
          )}

          {/* TASKS */}
          {/* OPSTASKSALL: the COO's Operational Tasks card, on the same list as before (GET
              /compliance/report/outstanding-tasks: every compliance account's tasks for the CCO, the
              covered entities' for a scoped account, SASCOPE), with the same destinations, in English
              or French. */}
          {activeSection === 'tasks' && (
            loading ? (
              <div style={cv.card}>
                <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: '13px' }}>Loading...</p>
              </div>
            ) : loadFailed ? (
              // CVLOADFAILSAFE: a failure must not read as an empty list.
              <div style={cv.card}>
                <LoadError message="Could not load compliance tasks." onRetry={load} />
              </div>
            ) : (
              <OperationalTasksCard
                tasks={outstandingTasks} s={mkS(isDarkMode)} isDarkMode={isDarkMode} t={cvT}
                tab={cvTasksTab} onTabChange={setCvTasksTab}
                titleOf={(t) => (isWorkflowStepTask(t)
                  /* WFTASKS: a workflow step task's title comes from the server in both languages. */
                  ? shownTaskTitle(t, 'description')
                  : complianceTaskTitle(t.description, cvT))}
                /* SAMENAMEENTITY: the name with its entity beneath, as before. */
                employeeOf={(t) => (
                  <>
                    <div>{t.name}</div>
                    {t.entity && (
                      <div style={{ fontSize: '11px',
                                    color: isDarkMode ? '#9ba8c0' : '#777',
                                    marginTop: '1px' }}>
                        {t.entity}
                      </div>
                    )}
                  </>
                )}
                /* TASKCLICK / OWNTASKDEST / WFTASKS: the destinations already decided. */
                onRowClick={(t) => (isWorkflowStepTask(t)
                  /* WFTASKS: a workflow step task (NPPR, the FCA quarterly reports) opens that step. */
                  ? () => cvNav(workflowStepOpenPath(t))
                  : cvTaskDestination(t))}
              />
            )
          )}

          </>
          )}

        </div>
      </div>
    </div>
  );
}

function EmployeeRouteView({ employeeId, currentUser, onLogout, role = 'employee' }) {
  return (
    <EmployeeView
      employeeId={employeeId}
      currentUser={currentUser}
      onLogout={onLogout}
      userRole={role}
    />
  );
}

function EmployeeByEmail({ email, onLogout, onBack = null, role = 'employee' }) {
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeEntity, setEmployeeEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Load the logged-in user's own record via the self endpoint (GET /employees/{id}
    // is now restricted to coo/admin/hr).
    axios.get(`${API}/employees/me`)
      .then(res => {
        const match = res.data;
        if (match) { setEmployeeId(match.id); setEmployeeEntity(match.primary_entity); }
        else setNotFound(true);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [email]); // eslint-disable-line

  if (loading) return <div style={loadingStyle}>Loading your profile...</div>;
  if (notFound) return (
    <div style={loadingStyle}>
      <p>No profile found for <strong>{email}</strong>.</p>
      <button onClick={onLogout} style={logoutBtnStyle}>Back to login</button>
    </div>
  );

  return (
    <EmployeeView
      employeeId={employeeId}
      currentUser={email}
      onLogout={onLogout}
      onBack={onBack}
      userRole={role}
      employeeEntity={employeeEntity}
    />
  );
}

const staffStyles = {
  container: { fontFamily: FONT_STACK, maxWidth: '900px', margin: '0 auto', padding: S[6], backgroundColor: N_50, minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  title: { ...TYPE.display, color: N_900, margin: '0' },
  subtitle: { ...TYPE.body, color: N_500, margin: `${S[1]} 0 0 0` },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  email: { ...TYPE.meta, color: N_500 },
  signOutBtn: { backgroundColor: N_0, border: `1px solid ${N_300}`, borderRadius: RADIUS.md, padding: '8px 14px', fontSize: '13.5px', fontWeight: 570, cursor: 'pointer', color: N_600 },
  section: { backgroundColor: N_0, borderRadius: RADIUS.xl, padding: S[5], marginBottom: S[4], boxShadow: ELEV.e1, border: `1px solid ${N_200}` },
  sectionTitle: { ...TYPE.h3, color: N_900, margin: `0 0 ${S[3]} 0`, display: 'flex', alignItems: 'center', gap: S[2] },
  badge: { backgroundColor: WARN_50, color: WARN_600, borderRadius: RADIUS.pill, padding: '2px 8px', fontSize: '11.5px', fontWeight: 590 },
  card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', border: `1px solid ${N_200}`, borderRadius: RADIUS.lg, marginBottom: S[2] },
  cardLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: NAVY_700, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 640 },
  name: { ...TYPE.body, fontWeight: 540, color: N_900, margin: '0 0 2px 0' },
  meta: { ...TYPE.meta, color: N_500, margin: '0' },
  fillBtn: { backgroundColor: NAVY_700, color: '#fff', border: '1px solid transparent', borderRadius: RADIUS.md, padding: '8px 14px', fontSize: '13.5px', fontWeight: 570, cursor: 'pointer' },
  // DONEGREY: the per-row "Done" label in the Completed badge's neutral grey (#374151 on #E5E7EB).
  doneBadge: { fontSize: '13.5px', color: '#374151', backgroundColor: '#E5E7EB', border: '1px solid #D1D5DB', borderRadius: RADIUS.pill, padding: '2px 10px', fontWeight: 600 },
  empty: { ...TYPE.meta, color: N_500 }
};

const loadingStyle = { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '15px', color: N_500, fontFamily: FONT_STACK, gap: S[4] };
const logoutBtnStyle = { backgroundColor: N_0, border: `1px solid ${N_300}`, borderRadius: RADIUS.md, padding: '8px 14px', fontSize: '13.5px', fontWeight: 570, cursor: 'pointer', color: N_600 };

function App() {
  return (
    <BrowserRouter>
      <DarkModeProvider>
        <AppMain />
      </DarkModeProvider>
    </BrowserRouter>
  );
}

export default App;
